import {
	useState,
	useEffect,
	useLayoutEffect,
	useCallback,
	useMemo,
	useRef,
	useId,
} from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
	EXERCISE_TYPES,
	SHOOTING_FOCUS_OPTIONS,
	START_CONDITION_FIREARM,
	START_CONDITION_SHOOTER,
	formatStrengthRepResult,
	isDistanceStrengthReps,
	isTimedStrengthReps,
	getSupersetRoundCount,
	formatShootingTargets,
	hasShootingResultContent,
	resolveStageImage,
	startConditionLabel,
	getDeclaredComplexReference,
	isStrengthComplex,
	normalizeFartlekSegmentType,
} from "../lib/programBuilderUtils";
import { exerciseTypeIcon } from "./programBuilder/exerciseIcons";
import { ShootingScoreForm } from "./shooting/ShootingScoreForm";
import {
	StageDesignerDialog,
	StagePreview,
} from "./shooting/StageDesignerDialog";
import { StagePrintable } from "./shooting/StagePrintSheet";
import { ImageZoomDialog } from "./shooting/ImageZoomDialog";
import {
	plannedSeriesCount,
	pruneEmptySeries,
	roundsPerSeries,
	totalRoundsPlanned,
} from "./shooting/shootingPlan";
import { useLocation, useNavigate } from "react-router-dom";
import {
	fetchAPI,
	getToday,
	cn,
	formatTime,
	formatRestDisplay,
	parseSmartRestSeconds,
	normalizeSmartRatioInput,
} from "../lib/utils";
import {
	getExerciseHistoryLookupCandidates,
	doesHistoryResultMatchExercise,
	doesProfileHistoryConfigMatchExercise,
	extractExerciseHistoryItems,
} from "../lib/exerciseHistoryUtils";
import {
	getInputPropsForUnit,
	parseValueByUnit,
	normalizeTimeOrRepsInput,
	formatValueByUnit,
	getYouTubeEmbedUrl,
	getYouTubeThumbnail as getYouTubeThumb,
} from "../lib/coachUtils";
import {
	hasContent,
	formatAmrapRoundsRepsLabel,
	formatAmrapScoreWithRoundsLabel,
	extractAmrapTotalRepsFromSchemaValues,
} from "../lib/conditioningResultUtils";
import { getEmomRoundLabel } from "../lib/emomRoundLabels";
import {
	formatEmomHeadline,
	getEmomStructureParts,
} from "../lib/emomSummary";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { LeaderboardDialog } from "./LeaderboardDialog";
import { ExerciseHistoryDialog } from "./ExerciseHistoryDialog";
import {
	ChevronDown,
	CircleHelp,
	Dumbbell,
	Play,
	Image,
	Check,
	Clock,
	Footprints,
	Waves,
	Bike,
	Zap,
	Calendar,
	ExternalLink,
	Share2,
	FileText,
	Loader2,
	Send,
	Repeat,
	Timer,
	ListOrdered,
	Pencil,
	Trash2,
	Trophy,
	SlidersHorizontal,
	ArrowUp,
	ArrowDown,
	Mail,
	History,
} from "lucide-react";
import { toast } from "sonner";

/* ─── CONSTANTS ─── */
// Colori per tipo: le ICONE arrivano da `exerciseTypeIcon`
// (components/programBuilder/exerciseIcons.js). Questa mappa era una **copia
// locale** che non conosceva i tipi nuovi, ed è il motivo per cui un drill di
// tiro compariva con il manubrio nella dashboard dell'atleta.
const EXERCISE_ICON_COLORS = Object.fromEntries(
	EXERCISE_TYPES.map((entry) => [entry.value, entry.color]),
);

const RUNNING_LABELS = {
	it: {
		fondo_lento: "Fondo Lento",
		lungo: "Lungo",
		fondo_medio: "Fondo Medio",
		tempo_run: "Tempo Run",
		ripetute: "Ripetute",
		fartlek: "Fartlek",
		progressivo: "Progressivo",
		salite: "Salite",
		allunghi: "Allunghi",
		recupero: "Recupero",
		custom: "Custom",
	},
	en: {
		fondo_lento: "Easy Run",
		lungo: "Long Run",
		fondo_medio: "Moderate Run",
		tempo_run: "Tempo Run",
		ripetute: "Intervals",
		fartlek: "Fartlek",
		progressivo: "Progression Run",
		salite: "Hill Repeats",
		allunghi: "Strides",
		recupero: "Recovery",
		custom: "Custom",
	},
};

const CONDITIONING_LABELS = {
	it: {
		amrap: "AMRAP",
		for_time: "For Time",
		emom: "EMOM",
		hiit: "HIIT",
		free: "WOD Custom",
	},
	en: {
		amrap: "AMRAP",
		for_time: "For Time",
		emom: "EMOM",
		hiit: "HIIT",
		free: "Custom WOD",
	},
};

const SWIM_LABELS = {
	it: {
		endurance: "Resistenza",
		intervals: "Intervalli",
		technique: "Tecnica",
		sprint: "Sprint",
		threshold: "Soglia",
		mixed: "Misto",
		recovery: "Recupero",
		drill: "Drill",
		custom: "Custom",
	},
	en: {
		endurance: "Endurance",
		intervals: "Intervals",
		technique: "Technique",
		sprint: "Sprint",
		threshold: "Threshold",
		mixed: "Mixed",
		recovery: "Recovery",
		drill: "Drill",
		custom: "Custom",
	},
};

const BIKE_LABELS = {
	it: {
		endurance: "Resistenza",
		intervals: "Intervalli",
		tempo: "Tempo",
		threshold: "Soglia",
		vo2max: "VO2max",
		hill_repeats: "Salite",
		sprint: "Sprint",
		recovery: "Recupero",
		custom: "Custom",
	},
	en: {
		endurance: "Endurance",
		intervals: "Intervals",
		tempo: "Tempo",
		threshold: "Threshold",
		vo2max: "VO2max",
		hill_repeats: "Hill Repeats",
		sprint: "Sprint",
		recovery: "Recovery",
		custom: "Custom",
	},
};

export function formatLeaderboardMetric(
	entry,
	t,
	leaderboardData,
	language,
	conditioningExercises,
	conditioningType,
) {
	const parseIntegerTotalReps = (value) => {
		if (value === null || value === undefined) return null;
		const raw = String(value).trim();
		if (!raw) return null;
		const compact = raw.replace(/,/g, ".");
		const fullNumber = compact.match(/^\d+(?:\.0+)?$/);
		const embeddedInteger = compact.match(/\b(\d+)\b/);
		const parsed = Number(fullNumber ? compact : embeddedInteger?.[1]);
		return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
	};
	const resolvedConditioningType = String(
		leaderboardData?.conditioning_type || conditioningType || "",
	)
		.trim()
		.toLowerCase();
	const resolvedScoreType = String(leaderboardData?.score_type || "")
		.trim()
		.toLowerCase();
	const activeFieldId = leaderboardData?.active_schema_field_id;

	// Schema-driven leaderboard: format the active field's value using its unit.
	if (activeFieldId) {
		const schemaValues = entry?.schema_values || {};
		const raw = schemaValues[activeFieldId];
		if (raw !== undefined && raw !== null && raw !== "") {
			const field = (leaderboardData?.result_schema || []).find(
				(f) => f.field_id === activeFieldId,
			);
			const unit = String(field?.unit || "reps").toLowerCase();
			return formatValueByUnit(raw, unit);
		}
		return "-";
	}

	// AMRAP must win over schema rendering to keep a consistent
	// "reps + rounds" metric in workout leaderboard.
	const amrapLikeFromCustomTotalReps =
		resolvedScoreType === "time_or_reps" &&
		(resolvedConditioningType === "free" ||
			resolvedConditioningType === "custom");
	if (resolvedConditioningType === "amrap" || amrapLikeFromCustomTotalReps) {
		const repsCandidates = [
			parseIntegerTotalReps(entry?.score),
			parseIntegerTotalReps(entry?.reps),
			parseIntegerTotalReps(entry?.reps_text),
		].filter((value) => value !== null);
		const totalReps =
			repsCandidates.length > 0 ? Math.max(...repsCandidates) : null;
		const amrapLabel = formatAmrapScoreWithRoundsLabel(
			totalReps,
			conditioningExercises || leaderboardData?.conditioning_exercises || [],
			language,
		);
		if (amrapLabel) return amrapLabel;
		if (Number.isFinite(Number(totalReps))) return `${totalReps} reps`;
		if (entry?.reps_text) return entry.reps_text;
	}
	const supersetDetails = Array.isArray(entry?.superset_details)
		? entry.superset_details
		: [];
	if (supersetDetails.length > 0) {
		const activeSupersetIndex = Number(entry?.__superset_active_index);
		const hasActiveSupersetIndex =
			Number.isInteger(activeSupersetIndex) &&
			activeSupersetIndex >= 0 &&
			activeSupersetIndex < supersetDetails.length;

		const chunks = supersetDetails
			.map((detail, idx) => {
				const weight = Number(detail?.weight_kg);
				const reps = Number(detail?.reps);
				const hasWeight = Number.isFinite(weight) && weight > 0;
				const hasReps = Number.isFinite(reps) && reps > 0;
				if (!hasWeight && !hasReps) return null;
				const label = String.fromCharCode(65 + idx);
				if (hasWeight && hasReps) return `${label} ${weight} kg x ${reps}`;
				if (hasWeight) return `${label} ${weight} kg`;
				return `${label} ${reps} ${t("repsShort")}`;
			})
			.filter(Boolean);
		if (chunks.length > 0) {
			if (hasActiveSupersetIndex) {
				return chunks[activeSupersetIndex] || chunks[0];
			}
			return chunks.join(" · ");
		}
	}
	if (entry?.time_seconds) {
		return formatTime(entry.time_seconds);
	}
	if (entry?.weight_kg && entry?.reps) {
		return `${entry.weight_kg} kg x ${entry.reps}`;
	}
	if (entry?.weight_kg) {
		return `${entry.weight_kg} kg`;
	}
	if (entry?.score !== null && entry?.score !== undefined) {
		return Number.isInteger(entry.score)
			? `${entry.score}`
			: entry.score.toFixed(1);
	}
	if (entry?.reps) {
		return `${entry.reps} ${t("repsShort")}`;
	}
	if (entry?.reps_text) {
		return entry.reps_text;
	}
	return "-";
}

const ERG_LABELS = {
	it: {
		rowerg_endurance: "RowErg Endurance",
		rowerg_conditioning: "RowErg Conditioning",
		bikeerg_endurance: "BikeErg Endurance",
		bikeerg_conditioning: "BikeErg Conditioning",
		skierg_endurance: "SkiErg Endurance",
		skierg_conditioning: "SkiErg Conditioning",
		assault_bike_endurance: "Assault Bike Endurance",
		assault_bike_conditioning: "Assault Bike Conditioning",
		mixed_erg_endurance: "Mixed Erg Endurance",
		mixed_erg_conditioning: "Mixed Erg Conditioning",
		custom: "Custom",
	},
	en: {
		rowerg_endurance: "RowErg Endurance",
		rowerg_conditioning: "RowErg Conditioning",
		bikeerg_endurance: "BikeErg Endurance",
		bikeerg_conditioning: "BikeErg Conditioning",
		skierg_endurance: "SkiErg Endurance",
		skierg_conditioning: "SkiErg Conditioning",
		assault_bike_endurance: "Assault Bike Endurance",
		assault_bike_conditioning: "Assault Bike Conditioning",
		mixed_erg_endurance: "Mixed Erg Endurance",
		mixed_erg_conditioning: "Mixed Erg Conditioning",
		custom: "Custom",
	},
};

const LOAD_REFERENCE_BODYWEIGHT_ID = "__bodyweight__";

/* ─── HELPERS ─── */
function getExerciseIcon(type) {
	const Icon = exerciseTypeIcon(type);
	return (
		<Icon
			className={cn("w-4 h-4", EXERCISE_ICON_COLORS[type] || "text-volt")}
		/>
	);
}

function toSafeUrlString(value) {
	return typeof value === "string" ? value.trim() : "";
}

function hasMediaUrl(value) {
	return toSafeUrlString(value).length > 0;
}

function getImageUrl(url) {
	const safeUrl = toSafeUrlString(url);
	if (!safeUrl) return null;
	if (safeUrl.startsWith("http")) return safeUrl;
	const base = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
	if (safeUrl.startsWith("/api/")) return `${base}${safeUrl}`;
	if (safeUrl.startsWith("/uploads/")) return `${base}/api${safeUrl}`;
	return safeUrl;
}

function getDateLocale(language = "it") {
	return language === "it" ? "it-IT" : "en-GB";
}

function capitalizeLabel(value) {
	if (!value) return "";
	return value.charAt(0).toUpperCase() + value.slice(1);
}

function getLocalizedOptionLabel(
	labelMap,
	value,
	language = "it",
	fallback = "",
) {
	if (!value) return fallback;
	return (
		labelMap?.[language]?.[value] || labelMap?.en?.[value] || fallback || value
	);
}

function formatDayLabel(dateStr, language = "it") {
	if (!dateStr) return "";
	const d = new Date(dateStr + "T12:00:00");
	if (isNaN(d.getTime())) return "";
	const locale = getDateLocale(language);
	const weekday = d
		.toLocaleDateString(locale, { weekday: "short" })
		.replace(".", "")
		.trim();
	const prettyWeekday = weekday ? capitalizeLabel(weekday) : "";
	return `${prettyWeekday} ${d.getDate()}`;
}

// Coach-generated default week labels are stored as "Settimana N"; localize
// only those defaults so the athlete UI follows the selected language while
// leaving custom coach-authored labels untouched.
function localizeWeekLabel(label, t, fallbackIndex = 0) {
	const raw = String(label || "").trim();
	const match = raw.match(/^(?:settimana|week)\s+(\d+)$/i);
	if (match) return `${t("week")} ${match[1]}`;
	if (raw) return raw;
	return `${t("week")} ${fallbackIndex + 1}`;
}

function extractPercentageNumbers(value) {
	if (value === null || value === undefined) return [];
	const raw = String(value).trim();
	if (!raw) return [];
	const compactRaw = raw.replace(/\s+/g, "");
	const hasRangeSeparator = /[\/-]/.test(compactRaw);

	// Accept expressions like "up to 30%" while preserving raw label elsewhere.
	const percentMatches = [...raw.matchAll(/(\d+(?:[\.,]\d+)?)\s*%/g)].map((m) =>
		Number(String(m[1]).replace(",", ".")),
	);
	if (
		percentMatches.length >= 2 &&
		percentMatches.every((n) => Number.isFinite(n) && n > 0)
	) {
		return percentMatches;
	}
	if (
		percentMatches.length === 1 &&
		!hasRangeSeparator &&
		percentMatches.every((n) => Number.isFinite(n) && n > 0)
	) {
		return percentMatches;
	}

	// Legacy support for plain numeric forms like "70" or "70-75".
	if (/[a-zA-Z]/.test(raw)) return [];
	const cleaned = raw.replace(/\s+/g, "").replace(/%+/g, "").replace(/,/g, ".");
	const parts = cleaned.split(/[\/-]/).map((p) => Number(p));
	if (parts.some((n) => Number.isNaN(n) || n <= 0)) return [];
	return parts;
}

function formatLoadDisplay(value) {
	if (value === null || value === undefined) return "";
	const raw = String(value).trim();
	if (!raw) return "";
	if (/[a-zA-Z]/.test(raw)) return raw;
	return raw.includes("%") ? raw : `${raw}%`;
}

function formatRunningDistanceLabel(distanceValue) {
	if (distanceValue === null || distanceValue === undefined) return "";
	const raw = String(distanceValue).trim();
	if (!raw) return "";

	const normalized = raw.toLowerCase().replace(",", ".");
	const kmMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*km$/);
	if (kmMatch) {
		const value = Number(kmMatch[1]);
		return Number.isFinite(value) ? `${value}km` : raw;
	}

	const metersMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*m$/);
	if (metersMatch) {
		const value = Number(metersMatch[1]);
		return Number.isFinite(value) ? `${value}m` : raw;
	}

	const parsed = Number(normalized);
	if (!Number.isFinite(parsed)) return raw;

	if (parsed >= 1000) {
		const kmValue = parsed / 1000;
		const roundedKm = Number.isInteger(kmValue)
			? String(kmValue)
			: kmValue.toFixed(1).replace(/\.0$/, "");
		return `${roundedKm}km`;
	}

	return `${parsed}m`;
}

function parseRunningPaceToSeconds(paceValue) {
	if (paceValue === null || paceValue === undefined) return NaN;
	const raw = String(paceValue).trim().toLowerCase().replace(",", ".");
	if (!raw) return NaN;

	const paceMatch = raw.match(/^(\d+):(\d{1,2})(?:\s*\/\s*km)?$/i);
	if (paceMatch) {
		const minutes = Number(paceMatch[1]);
		const seconds = Number(paceMatch[2]);
		if (
			Number.isFinite(minutes) &&
			Number.isFinite(seconds) &&
			seconds >= 0 &&
			seconds < 60
		) {
			return minutes * 60 + seconds;
		}
	}

	const parsed = Number(raw.replace(/\s*\/\s*km$/, ""));
	return Number.isFinite(parsed) && parsed > 0 ? parsed : NaN;
}

function formatRunningPaceFromSeconds(totalSeconds) {
	const roundedSeconds = Math.round(Number(totalSeconds));
	if (!Number.isFinite(roundedSeconds) || roundedSeconds <= 0) return "";

	const minutes = Math.floor(roundedSeconds / 60);
	const seconds = roundedSeconds % 60;
	return `${minutes}:${String(seconds).padStart(2, "0")}/km`;
}

function parseRunningPaceModifier(modifierValue) {
	const raw = String(modifierValue || "").trim();
	if (!raw) return null;

	const percentageMatch = raw.match(/^(\d+(?:[.,]\d+)?)\s*%$/);
	if (percentageMatch) {
		const value = Number(percentageMatch[1].replace(",", "."));
		return Number.isFinite(value) ? { type: "percentage", value } : null;
	}

	const deltaMatch = raw.match(/^([+-]?\d+(?:[.,]\d+)?)\s*s$/i);
	if (deltaMatch) {
		const value = Number(deltaMatch[1].replace(",", "."));
		return Number.isFinite(value) ? { type: "delta", value } : null;
	}

	const numericValue = Number(raw.replace(",", "."));
	return Number.isFinite(numericValue)
		? { type: "percentage", value: numericValue }
		: null;
}

function resolveRunningReferencedPace(benchmarkPace, modifierValue) {
	const benchmarkSeconds = parseRunningPaceToSeconds(benchmarkPace);
	if (!Number.isFinite(benchmarkSeconds) || benchmarkSeconds <= 0) return "";

	const modifier = parseRunningPaceModifier(modifierValue);
	if (!modifier) return "";

	if (modifier.type === "delta") {
		const resolvedSeconds = benchmarkSeconds + modifier.value;
		return resolvedSeconds > 0
			? formatRunningPaceFromSeconds(resolvedSeconds)
			: "";
	}

	const factor = modifier.value / 100;
	if (!Number.isFinite(factor) || factor <= 0) return "";
	return formatRunningPaceFromSeconds(benchmarkSeconds / factor);
}

function getRunningTargetPaceDisplay(exercise) {
	const manualPace = String(exercise?.target_pace || "").trim();
	if (manualPace) return manualPace;

	const resolvedPace = String(exercise?.target_pace_resolved || "").trim();
	if (resolvedPace) return resolvedPace;

	const benchmarkPace = String(
		exercise?.target_pace_reference_pace || "",
	).trim();
	const modifier = String(
		exercise?.target_pace_reference_percentage || "",
	).trim();

	const resolvedFromReference = resolveRunningReferencedPace(
		benchmarkPace,
		modifier,
	);
	if (resolvedFromReference) return resolvedFromReference;

	// If reference benchmark exists but modifier is empty/invalid,
	// show benchmark pace instead of hiding pace entirely.
	return benchmarkPace;
}

function formatRunningPaceReferenceText(exercise, t) {
	const referenceName = String(
		exercise?.target_pace_reference_benchmark_name || "",
	).trim();
	const referencePace = String(
		exercise?.target_pace_reference_pace || "",
	).trim();
	const referenceModifier = String(
		exercise?.target_pace_reference_percentage || "",
	).trim();
	const resolvedPace =
		String(exercise?.target_pace_resolved || "").trim() ||
		resolveRunningReferencedPace(referencePace, referenceModifier);

	const parts = [];
	if (referenceName) parts.push(referenceName);
	if (referencePace) parts.push(referencePace);
	if (referenceModifier) parts.push(referenceModifier);
	if (resolvedPace) parts.push(`→ ${resolvedPace}`);

	return parts.length > 0 ? `${t("calculatedFrom")}: ${parts.join(" · ")}` : "";
}

function buildNormalizedNameKey(value) {
	const raw = String(value || "")
		.trim()
		.replace(/\s+/g, " ")
		.toLowerCase();
	return raw ? `_name_${raw}` : null;
}

const EXERCISE_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

function readCachedExercises(cacheKey) {
	if (typeof window === "undefined" || !cacheKey) return null;
	try {
		const raw = window.localStorage.getItem(cacheKey);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (
			!parsed ||
			!Array.isArray(parsed.data) ||
			typeof parsed.ts !== "number" ||
			Date.now() - parsed.ts > EXERCISE_CACHE_TTL_MS
		) {
			return null;
		}
		return parsed.data;
	} catch {
		return null;
	}
}

function writeCachedExercises(cacheKey, data) {
	if (typeof window === "undefined" || !cacheKey || !Array.isArray(data))
		return;
	try {
		window.localStorage.setItem(
			cacheKey,
			JSON.stringify({ ts: Date.now(), data }),
		);
	} catch {
		// Ignore quota / serialization errors — cache is best-effort.
	}
}

/**
 * Does `source_exercise_id` point at a DIFFERENT movement than this exercise?
 *
 * `source_exercise_id` survives every week duplication, so a row cloned from
 * "Bench Press" and then edited into "Deadlift" in the builder keeps pointing at
 * the bench library entry. Following it blindly logs the result under the wrong
 * library id and makes that lift's history show the other movement's numbers.
 *
 * Only reports a stale link when the library entry is actually known: an id the
 * current user cannot resolve stays trusted, to avoid false positives.
 */
function isStaleSourceExerciseId(libraryMap, exercise) {
	const sourceId = String(exercise?.source_exercise_id || "").trim();
	const libraryEntry = sourceId ? libraryMap?.[sourceId] : null;
	if (!libraryEntry) return false;

	const exerciseNameKey = buildNormalizedNameKey(
		exercise?.exercise_name || exercise?.name,
	);
	const libraryNameKey = buildNormalizedNameKey(libraryEntry.name);
	if (!exerciseNameKey || !libraryNameKey) return false;

	return exerciseNameKey !== libraryNameKey;
}

function resolveExerciseLibraryEntry(libraryMap, exercise) {
	if (!libraryMap || typeof libraryMap !== "object" || !exercise) return null;

	const primaryId = String(exercise?.exercise_id || "").trim();
	if (primaryId && libraryMap[primaryId]) return libraryMap[primaryId];

	const sourceId = String(exercise?.source_exercise_id || "").trim();
	if (
		sourceId &&
		libraryMap[sourceId] &&
		!isStaleSourceExerciseId(libraryMap, exercise)
	) {
		return libraryMap[sourceId];
	}

	const nameKey = buildNormalizedNameKey(
		exercise?.exercise_name || exercise?.name,
	);
	if (nameKey && libraryMap[nameKey]) return libraryMap[nameKey];

	return null;
}

function resolveStableExerciseId(exercise, libraryMap) {
	if (!exercise) return null;
	const primaryId = String(exercise?.exercise_id || "").trim();
	if (primaryId && libraryMap?.[primaryId]) return primaryId;

	const sourceId = String(exercise?.source_exercise_id || "").trim();
	const usableSourceId = isStaleSourceExerciseId(libraryMap, exercise)
		? ""
		: sourceId;
	if (usableSourceId && libraryMap?.[usableSourceId]) return usableSourceId;

	return primaryId || usableSourceId || null;
}

function resolveLoadSuggestion(
	loadSuggestions = {},
	referenceExerciseId = null,
	referenceName = null,
) {
	if (
		referenceExerciseId &&
		loadSuggestions[referenceExerciseId]?.reference_1rm
	) {
		return loadSuggestions[referenceExerciseId];
	}

	const nameKey = buildNormalizedNameKey(referenceName);
	if (nameKey && loadSuggestions[nameKey]?.reference_1rm) {
		return loadSuggestions[nameKey];
	}

	const normalizedName = String(referenceName || "")
		.trim()
		.toLowerCase();
	if (!normalizedName) return null;

	return (
		Object.values(loadSuggestions).find((suggestion) => {
			const suggestionName = String(
				suggestion?.reference_name || suggestion?.exercise_name || "",
			)
				.trim()
				.toLowerCase();
			return suggestionName === normalizedName && suggestion?.reference_1rm;
		}) || null
	);
}

function getSuggestedLoadHint(
	loadSuggestions = {},
	referenceExerciseId = null,
	percentageStr = null,
	referenceName = null,
) {
	try {
		if (!percentageStr) return null;
		const suggestion = resolveLoadSuggestion(
			loadSuggestions,
			referenceExerciseId,
			referenceName,
		);
		const reference1rm = Number(suggestion?.reference_1rm);
		if (!Number.isFinite(reference1rm) || reference1rm <= 0) return null;

		const pctValues = extractPercentageNumbers(percentageStr);
		if (pctValues.length === 0) return null;

		let kg = null;
		let kg_min = null;
		let kg_max = null;

		if (pctValues.length === 1) {
			const pct = pctValues[0];
			if (Number.isNaN(pct) || pct <= 0) return null;
			kg = Math.round(((reference1rm * pct) / 100) * 2) / 2;
		} else if (pctValues.length === 2) {
			const minPct = pctValues[0];
			const maxPct = pctValues[1];
			if (
				Number.isNaN(minPct) ||
				Number.isNaN(maxPct) ||
				minPct <= 0 ||
				maxPct <= 0
			) {
				return null;
			}
			kg_min = Math.round(((reference1rm * minPct) / 100) * 2) / 2;
			kg_max = Math.round(((reference1rm * maxPct) / 100) * 2) / 2;
		} else {
			return null;
		}

		return {
			kg,
			kg_min,
			kg_max,
			reference_name: referenceName || suggestion?.reference_name || null,
			source: suggestion?.source || null,
			source_label: suggestion?.source_label || null,
			source_detail: suggestion?.source_detail || null,
			reference_1rm: reference1rm,
		};
	} catch {
		return null;
	}
}

function formatLoadSourceText(loadHint, t) {
	if (!loadHint?.source_label) return "";
	const cleanLabel = String(loadHint.source_label)
		.replace(/\s*\(Epley\)$/i, "")
		.trim();
	const cleanDetail = loadHint.source_detail
		? String(loadHint.source_detail)
				.replace(/,\s*Epley/gi, "")
				.replace(/\(Epley\)/gi, "")
				.replace(/\s{2,}/g, " ")
				.trim()
		: "";
	return cleanDetail ? `${cleanLabel} · ${cleanDetail}` : cleanLabel;
}

function getLoadReferenceId(exercise, row) {
	if (row?.load_reference_exercise_id === LOAD_REFERENCE_BODYWEIGHT_ID) {
		return LOAD_REFERENCE_BODYWEIGHT_ID;
	}
	if (exercise?.load_reference_exercise_id === LOAD_REFERENCE_BODYWEIGHT_ID) {
		return LOAD_REFERENCE_BODYWEIGHT_ID;
	}
	const complexFallbackId = asArray(exercise?.complex_exercises).find(
		(item) => item?.exercise_id,
	)?.exercise_id;
	// A complex has no max of its own: without a declared reference the
	// complex itself used to be the reference, and as soon as the athlete
	// logged the session their own result became its "tested 1RM". Programs
	// written before the builder asked for it fall back to the first movement.
	if (isStrengthComplex(exercise)) {
		const declared = getDeclaredComplexReference(exercise, row);
		return declared ? declared.id : complexFallbackId || null;
	}
	return (
		row?.load_reference_exercise_id ||
		exercise?.load_reference_exercise_id ||
		exercise?.exercise_id ||
		complexFallbackId ||
		null
	);
}

function getLoadReferenceName(exercise, row) {
	const complexFallbackName = asArray(exercise?.complex_exercises).find(
		(item) => item?.exercise_name,
	)?.exercise_name;
	if (isStrengthComplex(exercise)) {
		const declared = getDeclaredComplexReference(exercise, row);
		return declared ? declared.name : complexFallbackName || null;
	}
	return (
		row?.load_reference_exercise_name ||
		exercise?.load_reference_exercise_name ||
		exercise?.exercise_name ||
		complexFallbackName ||
		null
	);
}

function asArray(value) {
	return Array.isArray(value) ? value : [];
}

// A prescription like "5-4-3-2" over 4 sets is a pyramid: one rep target per set.
// Split it only when the number of values matches the number of sets, so plain rep
// ranges ("8-12") keep being shown as-is.
function splitPyramidReps(repsValue, setsCount) {
	const totalSets = Number(setsCount);
	if (!Number.isFinite(totalSets) || totalSets < 2) return null;
	const parts = String(repsValue ?? "")
		.trim()
		.split(/[-\/,]/)
		.map((part) => part.trim())
		.filter(Boolean);
	if (parts.length !== totalSets) return null;
	if (!parts.every((part) => /^\d+$/.test(part))) return null;
	// Two values over two sets are ambiguous with a rep range: only descending
	// values ("5-3") are read as a pyramid.
	if (parts.length === 2 && Number(parts[0]) <= Number(parts[1])) return null;
	return parts;
}

function LinkifiedText({ text, className = "" }) {
	const content = String(text || "");
	const parts = content.split(/(https?:\/\/[^\s]+|www\.[^\s]+)/gi);

	return (
		<span className={className}>
			{parts.map((part, idx) => {
				if (!part) return null;
				const isHttpUrl = /^https?:\/\//i.test(part);
				const isWwwUrl = /^www\./i.test(part);
				if (!isHttpUrl && !isWwwUrl) return <span key={idx}>{part}</span>;
				const href = isHttpUrl ? part : `https://${part}`;
				return (
					<a
						key={idx}
						href={href}
						target="_blank"
						rel="noopener noreferrer"
						className="text-volt underline underline-offset-2 break-all"
					>
						{part}
					</a>
				);
			})}
		</span>
	);
}

function getEmomItemLoadDisplay(
	item,
	loadSuggestions = {},
	fallbackExerciseId = null,
) {
	if (!item?.load) return null;
	const loadStr = String(item.load).trim();
	const loadHint = getSuggestedLoadHint(
		loadSuggestions,
		item?.load_reference_exercise_id || item?.exercise_id || fallbackExerciseId,
		loadStr,
		item?.load_reference_exercise_name || item?.exercise_name || null,
	);
	if (loadHint) {
		const pctLabel = formatLoadDisplay(loadStr);
		return `${pctLabel} → ${
			loadHint.kg !== null
				? `${loadHint.kg}kg`
				: `${loadHint.kg_min}-${loadHint.kg_max}kg`
		}`;
	}

	const pctValues = extractPercentageNumbers(loadStr);
	if (pctValues.length > 0) {
		return formatLoadDisplay(loadStr);
	}

	return loadStr;
}

function getForTimeRoundsDisplay(roundsValue) {
	const normalized =
		roundsValue === null || roundsValue === undefined
			? ""
			: String(roundsValue).trim();

	if (!normalized) return "";
	if (/^\d+$/.test(normalized)) {
		const roundsNumber = Number(normalized);
		if (!Number.isFinite(roundsNumber) || roundsNumber <= 1) {
			return "";
		}
		return `${roundsNumber} ${roundsNumber === 1 ? "round" : "rounds"}`;
	}

	return normalized;
}

function getRoundLabelByValue(roundsValue, t) {
	const normalized =
		roundsValue === null || roundsValue === undefined
			? ""
			: String(roundsValue).trim();

	if (!normalized) return t("roundsLabel");

	if (normalized.includes("/")) {
		const values = normalized
			.split("/")
			.map((part) => Number(String(part || "").trim()))
			.filter((value) => Number.isFinite(value));

		if (values.length === 2 && values[0] === 1 && values[1] === 1) {
			return t("roundLabel");
		}

		return t("roundsLabel");
	}

	const numericValue = Number(normalized);
	if (Number.isFinite(numericValue) && numericValue === 1) {
		return t("roundLabel");
	}

	return t("roundsLabel");
}

function getDisplayEmomRounds(exercise) {
	const rounds = Array.isArray(exercise?.emom_rounds)
		? exercise.emom_rounds
		: Array.isArray(exercise?.emom_round_templates)
			? exercise.emom_round_templates
			: [];
	if (rounds.length > 0) {
		return rounds
			.filter((round) => round && typeof round === "object")
			.map((round) => {
				const normalizedItems = asArray(round?.items)
					.filter((item) => item && typeof item === "object")
					.map((item) => ({
						item_id: item?.item_id || null,
						exercise_id: item?.exercise_id || "",
						exercise_name:
							item?.exercise_name || item?.name || item?.movement || "",
						reps: item?.reps || "",
						load: item?.load || "",
						load_reference_exercise_id:
							item?.load_reference_exercise_id || null,
						load_reference_exercise_name:
							item?.load_reference_exercise_name || null,
						notes: item?.notes || "",
						is_custom: Boolean(item?.is_custom),
						custom_text: item?.custom_text || item?.content || item?.text || "",
					}));

				const roundContent = (
					round?.content ||
					round?.custom_text ||
					round?.text ||
					""
				).trim();
				const items =
					normalizedItems.length > 0
						? normalizedItems
						: roundContent
							? [
									{
										item_id: null,
										exercise_id: "",
										exercise_name: "",
										reps: "",
										load: "",
										notes: "",
										is_custom: true,
										custom_text: roundContent,
									},
								]
							: [];

				return {
					round_id: round?.round_id || null,
					assignment_type: round?.assignment_type || "cycle",
					specific_minutes: round?.specific_minutes || "",
					custom_label: round?.custom_label || round?.label || "",
					items,
				};
			});
	}

	return [];
}


function parseHiitWorkRestRatio(ratioValue) {
	const ratioText = String(ratioValue || "").trim();
	if (!ratioText) return null;
	const match = ratioText.match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
	if (!match) return null;

	const work = Number(match[1]);
	const rest = Number(match[2]);
	if (
		!Number.isFinite(work) ||
		!Number.isFinite(rest) ||
		work <= 0 ||
		rest <= 0
	) {
		return null;
	}

	return { work, rest };
}

function formatHiitIntervalShort(intervalSeconds) {
	const secondsValue = Number(intervalSeconds);
	if (!Number.isFinite(secondsValue) || secondsValue <= 0) {
		return "";
	}

	const minutes = Math.floor(secondsValue / 60);
	const remainingSeconds = secondsValue % 60;

	if (remainingSeconds === 0) {
		return `${minutes}'`;
	}

	if (minutes === 0) {
		return `${remainingSeconds}\"`;
	}

	return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function parseDurationStringToSeconds(rawValue) {
	const value = String(rawValue || "").trim();
	if (!value) return null;

	if (/^\d+$/.test(value)) {
		const minutes = Number(value);
		if (!Number.isFinite(minutes) || minutes <= 0) return null;
		return Math.round(minutes * 60);
	}

	const match = value.match(/^(\d+):(\d{1,2})$/);
	if (!match) return null;
	const minutes = Number(match[1]);
	const seconds = Number(match[2]);
	if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
	if (minutes < 0 || seconds < 0 || seconds > 59) return null;
	return Math.round(minutes * 60 + seconds);
}

function getHiitWorkSecondsPerRound(exercise) {
	const numericSeconds = Number(exercise?.hiit_work_seconds);
	if (Number.isFinite(numericSeconds) && numericSeconds > 0) {
		return Math.round(numericSeconds);
	}

	const parsedSecondsFromSecondsField = parseDurationStringToSeconds(
		exercise?.hiit_work_seconds,
	);
	if (
		Number.isFinite(parsedSecondsFromSecondsField) &&
		parsedSecondsFromSecondsField > 0
	) {
		return parsedSecondsFromSecondsField;
	}

	return parseDurationStringToSeconds(exercise?.hiit_work_interval);
}

function getHiitRestSecondsPerRound(exercise) {
	if (!exercise) return null;
	const restSecondsFromField = Number(exercise.hiit_rest_seconds);
	const parsedRestFromSecondsField = parseDurationStringToSeconds(
		exercise.hiit_rest_seconds,
	);
	const parsedRestInterval = parseDurationStringToSeconds(
		exercise.hiit_rest_interval,
	);
	const parsedSmartRest = parseSmartRestSeconds(
		exercise.hiit_rest_interval || exercise.hiit_rest_seconds,
	);
	const fixedRestSeconds =
		Number.isFinite(restSecondsFromField) && restSecondsFromField > 0
			? Math.round(restSecondsFromField)
			: Number.isFinite(parsedRestFromSecondsField) &&
				  parsedRestFromSecondsField > 0
				? parsedRestFromSecondsField
				: Number.isFinite(parsedRestInterval) && parsedRestInterval > 0
					? parsedRestInterval
					: parsedSmartRest;

	if (exercise.hiit_rest_mode === "fixed" || !exercise.hiit_rest_mode) {
		return fixedRestSeconds;
	}

	if (exercise.hiit_rest_mode === "ratio") {
		const workSeconds = getHiitWorkSecondsPerRound(exercise);
		const ratio = parseHiitWorkRestRatio(exercise.hiit_rest_ratio);
		if (!Number.isFinite(workSeconds) || workSeconds <= 0 || !ratio) {
			return null;
		}
		return Math.round((workSeconds * ratio.rest) / ratio.work);
	}

	return null;
}

function formatEmomItemSummary(item, language = "it") {
	if (item?.is_custom) return item?.custom_text || "Custom";
	const parts = [];
	if (item?.reps) parts.push(item.reps);
	if (item?.exercise_name) parts.push(item.exercise_name);
	if (item?.load) parts.push(`@${item.load}`);
	return (
		parts.join(" ").trim() || (language === "it" ? "Esercizio" : "Exercise")
	);
}

/* ─── EXERCISE SUMMARY (compact, inline) ─── */
function ExerciseSummary({ exercise, loadSuggestions = {} }) {
	const { t, language } = useLanguage();
	const type = exercise.exercise_type || "strength";
	const meth = exercise.methodology || "standard";
	const supersetExercises = asArray(exercise.superset_exercises);
	const emomRounds = getDisplayEmomRounds(exercise);
	const setRows = asArray(exercise.set_rows);
	const intervalRows = asArray(exercise.interval_rows);
	const swimSets = asArray(exercise.swim_sets);
	const bikeIntervals = asArray(exercise.bike_intervals);

	// Superset
	if (meth === "superset" && supersetExercises.length > 0) {
		return (
			<span className="text-xs text-muted-foreground">
				Superset {exercise.superset_sets || exercise.sets || ""}x ·{" "}
				{supersetExercises.length} {t("exerciseCountLabel")}
			</span>
		);
	}

	// EMOM (Strength)
	if (meth === "emom" && emomRounds.length > 0) {
		return (
			<div className="space-y-0.5">
				<span className="text-[10px] text-muted-foreground">
					{formatEmomHeadline(exercise, t)}
				</span>
				{emomRounds.map((round, roundIndex) => {
					const roundItems = asArray(round.items)
						.map((item) => formatEmomItemSummary(item, language))
						.filter(Boolean)
						.join(" · ");
					return (
						<span
							key={round.round_id || roundIndex}
							className="block text-[10px] text-muted-foreground"
						>
							<span className="font-medium text-yellow-400">
								{getEmomRoundLabel(round, roundIndex, language)}:
							</span>{" "}
							{roundItems ||
								(language === "it" ? "Nessun esercizio" : "No exercise")}
						</span>
					);
				})}
			</div>
		);
	}

	if (type === "strength") {
		const parts = [];
		let summaryLoadHint = null;
		if (setRows.length > 0) {
			const totalSets = setRows.reduce((sum, r) => {
				if (!r || typeof r !== "object") return sum;
				const setsValue = Number(r.sets);
				return (
					sum + (Number.isFinite(setsValue) && setsValue > 0 ? setsValue : 1)
				);
			}, 0);
			const repsSet = [...new Set(setRows.map((r) => r?.reps).filter(Boolean))];
			if (repsSet.length <= 1) {
				parts.push(`${totalSets}x${repsSet[0] || ""}`);
			} else {
				parts.push(`${totalSets} ${t("setsShort")}`);
			}
			const loads = [
				...new Set(setRows.map((r) => r?.load_percentage).filter(Boolean)),
			];
			const numericLoads = loads.flatMap(extractPercentageNumbers);
			if (loads.length === 1) {
				parts.push(`@${formatLoadDisplay(loads[0])}`);
			} else if (loads.length > 1) {
				if (numericLoads.length > 0) {
					parts.push(
						`@${Math.min(...numericLoads)}-${Math.max(...numericLoads)}%`,
					);
				} else {
					parts.push(`@${loads.map(formatLoadDisplay).join(", ")}`);
				}
			}
			const rowHints = setRows
				.map((row) => {
					if (!row || !row.load_percentage) return null;
					const referenceId = getLoadReferenceId(exercise, row);
					const suggestion = referenceId ? loadSuggestions[referenceId] : null;
					const pctValues = extractPercentageNumbers(row.load_percentage);
					if (!suggestion?.reference_1rm) return null;
					// Support both single and range percentages
					if (pctValues.length === 1) {
						return (
							Math.round(
								((suggestion.reference_1rm * pctValues[0]) / 100) * 2,
							) / 2
						);
					} else if (pctValues.length === 2) {
						const minKg =
							Math.round(
								((suggestion.reference_1rm * pctValues[0]) / 100) * 2,
							) / 2;
						const maxKg =
							Math.round(
								((suggestion.reference_1rm * pctValues[1]) / 100) * 2,
							) / 2;
						return { minKg, maxKg };
					}
					return null;
				})
				.filter((value) => value !== null);
			if (rowHints.length > 0) {
				const allMinValues = rowHints
					.map((h) => (typeof h === "object" ? h.minKg : h))
					.sort((a, b) => a - b);
				const allMaxValues = rowHints
					.map((h) => (typeof h === "object" ? h.maxKg : h))
					.sort((a, b) => a - b);
				const minKg = Math.min(...allMinValues);
				const maxKg = Math.max(...allMaxValues);
				summaryLoadHint =
					minKg === maxKg ? `${minKg}kg` : `${minKg}-${maxKg}kg`;
			}
		} else {
			if (exercise.sets) parts.push(`${exercise.sets}x${exercise.reps || ""}`);
			else if (exercise.reps) {
				parts.push(
					isTimedStrengthReps(exercise.reps) ||
						isDistanceStrengthReps(exercise.reps)
						? exercise.reps
						: `${exercise.reps} ${t("repsShort")}`,
				);
			}
			if (exercise.load_percentage)
				parts.push(`@${formatLoadDisplay(exercise.load_percentage)}`);
			if (exercise.load_kg) {
				parts.push(`${exercise.load_kg}kg`);
			}
			// Compute hint for non-set_rows case: support both single and range percentages
			const singleLoadHint = exercise.load_percentage
				? getSuggestedLoadHint(
						loadSuggestions,
						getLoadReferenceId(exercise, null),
						exercise.load_percentage,
						getLoadReferenceName(exercise, null),
					)
				: null;
			if (singleLoadHint) {
				summaryLoadHint =
					singleLoadHint.kg !== null
						? `→ ${singleLoadHint.kg}kg`
						: `→ ${singleLoadHint.kg_min}-${singleLoadHint.kg_max}kg`;
			}
		}
		if (exercise.rpe) parts.push(`RPE ${exercise.rpe}`);
		if (exercise.rir) parts.push(`RIR ${exercise.rir}`);
		return (
			<span className="text-xs text-muted-foreground">
				{parts.join(" · ")}
				{summaryLoadHint && (
					<>
						{parts.length > 0 && " · "}
						<span className="text-green-400 font-medium">
							{summaryLoadHint}
						</span>
					</>
				)}
			</span>
		);
	}

	if (type === "running") {
		const isCustomRunning =
			String(exercise.running_type || "")
				.trim()
				.toLowerCase() === "custom";
		const runningDistanceLabel = formatRunningDistanceLabel(
			exercise.distance_meters,
		);
		const label = getLocalizedOptionLabel(
			RUNNING_LABELS,
			exercise.running_type,
			language,
			exercise.running_type || "",
		);
		const parts = isCustomRunning ? [] : [label];
		if (intervalRows.length > 0) {
			parts.push(
				intervalRows
					.map((r) => `${r.repetitions || 1}x${r.distance || ""}`)
					.join(", "),
			);
		} else {
			if (runningDistanceLabel) parts.push(runningDistanceLabel);
			if (exercise.duration_minutes)
				parts.push(`${exercise.duration_minutes}'`);
			const runningPace = getRunningTargetPaceDisplay(exercise);
			if (runningPace) parts.push(runningPace);
		}
		return (
			<span className="text-xs text-muted-foreground">
				{parts.filter(Boolean).join(" · ")}
			</span>
		);
	}

	if (type === "swim") {
		const isCustomSwim =
			String(exercise.swim_type || "")
				.trim()
				.toLowerCase() === "custom";
		const label = getLocalizedOptionLabel(
			SWIM_LABELS,
			exercise.swim_type,
			language,
			t("swimLabel"),
		);
		const parts = isCustomSwim ? [] : [label];
		if (swimSets.length > 0) {
			parts.push(
				swimSets
					.map((s) => `${s.repetitions || 1}x${s.distance || ""}m`)
					.join(", "),
			);
		}
		return (
			<span className="text-xs text-muted-foreground">{parts.join(" · ")}</span>
		);
	}

	if (type === "bike" || type === "erg") {
		const isCustomBikeLike =
			String(exercise.bike_type || "")
				.trim()
				.toLowerCase() === "custom";
		const label = getLocalizedOptionLabel(
			type === "erg" ? ERG_LABELS : BIKE_LABELS,
			exercise.bike_type,
			language,
			type === "erg" ? "Erg" : t("bikeLabel"),
		);
		const parts = isCustomBikeLike ? [] : [label];
		if (bikeIntervals.length > 0) {
			parts.push(
				bikeIntervals
					.map((i) => `${i.repetitions || 1}x${i.duration || ""}`)
					.join(", "),
			);
		}
		return (
			<span className="text-xs text-muted-foreground">{parts.join(" · ")}</span>
		);
	}

	if (type === "conditioning") {
		const condType = String(exercise.conditioning_type || "")
			.trim()
			.toLowerCase();
		// EMOM: stessa identica riga del blocco forza, "EMOM · 10 round ·
		// ogni 1:00". Prima qui usciva "EMOM · 10 rounds · Ogni 1 minuto".
		if (condType === "emom") {
			const emomParts = [formatEmomHeadline(exercise, t)];
			if (exercise.time_cap_seconds) {
				emomParts.push(
					`${t("timeCapLabel")} ${Math.floor(exercise.time_cap_seconds / 60)}'`,
				);
			}
			return (
				<span className="text-xs text-muted-foreground">
					{emomParts.join(" · ")}
				</span>
			);
		}
		const forTimeRoundsDisplay = getForTimeRoundsDisplay(exercise.rounds);
		const label =
			getLocalizedOptionLabel(
				CONDITIONING_LABELS,
				exercise.conditioning_type,
				language,
				exercise.conditioning_type || "",
			) || "";
		// Avoid duplicating the type label if exercise_name already contains it
		const nameLower = (exercise.exercise_name || "").toLowerCase();
		const labelLower = label.toLowerCase();
		const parts = [];
		const hideTypeLabel = condType === "free" || condType === "custom";
		if (label && !nameLower.includes(labelLower) && !hideTypeLabel)
			parts.push(label);
		if (exercise.time_cap_seconds)
			parts.push(
				`${t("timeCapLabel")} ${Math.floor(exercise.time_cap_seconds / 60)}'`,
			);
		if (condType === "for_time" || condType === "time_or_reps") {
			if (forTimeRoundsDisplay) {
				parts.push(forTimeRoundsDisplay);
			}
		} else if (exercise.rounds) {
			parts.push(
				`${exercise.rounds} ${getRoundLabelByValue(exercise.rounds, t)}`,
			);
		}
		if (parts.length === 0) return null;
		return (
			<span className="text-xs text-muted-foreground">
				{parts.filter(Boolean).join(" · ")}
			</span>
		);
	}

	return null;
}

function getScoreUnitPreviewLabel(unit, t) {
	const normalized = String(unit || "reps")
		.trim()
		.toLowerCase();
	if (normalized === "minutes_seconds") return "MM:SS";
	if (normalized === "time_or_reps") return "MM:SS / Reps";
	if (normalized === "seconds") return t("timeLabel");
	if (normalized === "meters") return t("distanceMetersLabel");
	if (normalized === "kg") return "kg";
	if (normalized === "points") return t("scoreLabel");
	if (normalized === "minutes") return "min";
	return t("repsLabel");
}

/* ─── EXERCISE DETAIL (expanded view, full prescription) ─── */
function ExerciseDetail({
	exercise,
	exerciseLibrary,
	onOpenMedia,
	hideScorePreview = false,
	loadSuggestions = {},
}) {
	const { t, language } = useLanguage();
	// La piantina a schermo pieno del ramo tiro. Sta qui e non dentro quel
	// ramo perché gli hook non si chiamano sotto condizione: il componente
	// rende tipi di esercizio diversi, e l'ordine degli hook cambierebbe.
	const [stageOpen, setStageOpen] = useState(false);
	// L'immagine da ingrandire (piantina o allegato): il tiratore ci clicca
	// sopra «aspettandosi lo zoom per vederla bene».
	const [zoomImage, setZoomImage] = useState(null);
	if (!exercise) return null;
	const type = exercise.exercise_type || "strength";
	const meth = exercise.methodology || "standard";
	const safeLoadSuggestions =
		loadSuggestions && typeof loadSuggestions === "object"
			? loadSuggestions
			: {};
	const safeExerciseLibrary =
		exerciseLibrary && typeof exerciseLibrary === "object"
			? exerciseLibrary
			: {};

	// Helper to render single exercise's set_rows or basic info
	const renderStrengthBlock = (ex, label) => {
		if (!ex || typeof ex !== "object") return null;
		const setRows = asArray(ex.set_rows);
		const rowLoadSources = setRows
			.map((row) => {
				if (!row || typeof row !== "object" || !row.load_percentage)
					return null;
				const referenceId = getLoadReferenceId(ex, row);
				const referenceName = getLoadReferenceName(ex, row);
				const loadHint = getSuggestedLoadHint(
					safeLoadSuggestions,
					referenceId,
					row.load_percentage,
					referenceName,
				);
				if (!loadHint?.source_label) return null;
				return {
					reference_name: loadHint.reference_name,
					source: loadHint.source,
					source_label: loadHint.source_label,
					source_detail: loadHint.source_detail,
					reference_1rm: loadHint.reference_1rm,
				};
			})
			.filter(Boolean);
		const uniqueRowLoadSources = rowLoadSources.filter(
			(source, index, arr) =>
				index ===
				arr.findIndex(
					(other) =>
						other.reference_name === source.reference_name &&
						other.source === source.source &&
						other.source_label === source.source_label &&
						other.source_detail === source.source_detail &&
						other.reference_1rm === source.reference_1rm,
				),
		);
		const sharedLoadSource =
			setRows.length > 1 && uniqueRowLoadSources.length === 1
				? uniqueRowLoadSources[0]
				: null;
		// A percentage the app could not turn into kg used to show as a bare
		// "@50%": say which max is missing, once the suggestions have answered
		// for that reference (the key is there, with no value).
		const missingMaxReferenceName = (() => {
			for (const row of setRows) {
				if (!row || typeof row !== "object") continue;
				if (extractPercentageNumbers(row.load_percentage).length === 0) continue;
				const referenceId = getLoadReferenceId(ex, row);
				if (!referenceId || referenceId === LOAD_REFERENCE_BODYWEIGHT_ID) continue;
				if (!Object.prototype.hasOwnProperty.call(safeLoadSuggestions, referenceId))
					continue;
				const referenceName = getLoadReferenceName(ex, row);
				if (
					getSuggestedLoadHint(
						safeLoadSuggestions,
						referenceId,
						row.load_percentage,
						referenceName,
					)
				)
					continue;
				return referenceName || null;
			}
			return null;
		})();
		const exName = String(ex?.exercise_name || ex?.name || "")
			.trim()
			.toLowerCase();
		const lib =
			safeExerciseLibrary[ex.exercise_id] ||
			(exName ? safeExerciseLibrary[`_name_${exName}`] : null) ||
			null;
		const hasMedia = hasMediaUrl(lib?.video_url) || hasMediaUrl(lib?.image_url);
		const mediaExercise = {
			...ex,
			...lib,
			video_url: toSafeUrlString(lib?.video_url),
			image_url: toSafeUrlString(lib?.image_url),
		};
		return (
			<div className="space-y-1.5">
				{label && (
					<div className="flex items-center gap-2">
						<span className="text-xs font-medium">
							{ex.exercise_name || label}
						</span>
						{hasMedia && (
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									onOpenMedia?.(mediaExercise);
								}}
								className="flex items-center gap-1 text-[10px] text-volt hover:text-volt/80 active:opacity-70"
							>
								{hasMediaUrl(lib?.video_url) ? (
									<Play className="w-3 h-3" />
								) : (
									<Image className="w-3 h-3" />
								)}
								<span>{t("mediaLabel")}</span>
							</button>
						)}
					</div>
				)}
				{setRows.length > 0 ? (
					<>
						{setRows.map((row, i) => {
							if (!row || typeof row !== "object") return null;
							const hasReps = row.reps && row.reps !== "0";
							const timedReps = isTimedStrengthReps(row.reps);
							const distanceReps = isDistanceStrengthReps(row.reps);
							const hasSets = row.sets && row.sets > 0;
							const displaySets = row.sets_label || row.sets;
							if (!hasReps && !hasSets && !row.load_percentage && !row.load_kg)
								return null;
							const referenceId = getLoadReferenceId(ex, row);
							const referenceName = getLoadReferenceName(ex, row);
							const loadHint = row.load_percentage
								? getSuggestedLoadHint(
										safeLoadSuggestions,
										referenceId,
										row.load_percentage,
										referenceName,
									)
								: null;
							return (
								<div
									key={i}
									className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs bg-secondary/50 rounded-sm px-3 py-2"
								>
									{setRows.length > 1 && (
										<span className="text-muted-foreground shrink-0">
											{t("setLabel")} {i + 1}
										</span>
									)}
									{(hasSets || hasReps) && (
										<span className="font-medium">
											{hasSets && hasReps
												? `${displaySets} ${t("setsShort")} × ${row.reps}${timedReps || distanceReps ? "" : ` ${t("repsShort")}`}`
												: hasSets
													? `${displaySets} ${t("setsShort")}`
													: `${row.reps}${timedReps || distanceReps ? "" : ` ${t("repsShort")}`}`}
										</span>
									)}
									{row.load_percentage && (
										<span className="text-volt inline-flex items-center gap-1">
											@{formatLoadDisplay(row.load_percentage)}
											{loadHint && (
												<span className="text-emerald-400 font-semibold">
													→
													{loadHint.kg !== null
														? ` ${loadHint.kg}kg`
														: ` ${loadHint.kg_min}-${loadHint.kg_max}kg`}
												</span>
											)}
										</span>
									)}
									{row.load_kg && (
										<span className="text-volt">{row.load_kg}kg</span>
									)}
									{row.rpe && (
										<span className="text-muted-foreground">RPE {row.rpe}</span>
									)}
									{row.rir && (
										<span className="text-muted-foreground">RIR {row.rir}</span>
									)}
									{row.rest_seconds && (
										<span className="text-muted-foreground ml-auto shrink-0">
											{t("restLabel")} {formatRestDisplay(row.rest_seconds)}
										</span>
									)}
									{loadHint && loadHint.source_label && !sharedLoadSource && (
										<span className="text-muted-foreground/60 w-full text-[10px]">
											{loadHint.reference_name
												? `${loadHint.reference_name} · `
												: ""}
											{formatLoadSourceText(loadHint, t)}
										</span>
									)}
									{row.notes && (
										<span className="text-muted-foreground w-full text-[10px] italic">
											{row.notes}
										</span>
									)}
								</div>
							);
						})}
						{sharedLoadSource && (
							<span className="text-muted-foreground/60 text-[10px] px-1">
								{sharedLoadSource.reference_name
									? `${sharedLoadSource.reference_name} · `
									: ""}
								{formatLoadSourceText(sharedLoadSource, t)}
							</span>
						)}
						{missingMaxReferenceName && (
							<span className="text-amber-300/80 text-[10px] px-1">
								{t("loadMissingMaxInProfile")} · {missingMaxReferenceName}
							</span>
						)}
					</>
				) : (
					<div className="flex flex-wrap gap-2">
						{ex.sets > 0 && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">{t("sets")}</p>
								<p className="text-sm font-medium">{ex.sets}</p>
							</div>
						)}
						{ex.reps && ex.reps !== "0" && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">
									{isTimedStrengthReps(ex.reps)
										? "sec"
										: isDistanceStrengthReps(ex.reps)
											? t("distanceLabel")
											: t("repsShort")}
								</p>
								<p className="text-sm font-medium">{ex.reps}</p>
							</div>
						)}
						{ex.load_percentage &&
							(() => {
								const lh = getSuggestedLoadHint(
									safeLoadSuggestions,
									getLoadReferenceId(ex, null),
									ex.load_percentage,
									getLoadReferenceName(ex, null),
								);
								return (
									<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
										<p className="text-[10px] text-muted-foreground">
											{t("load")}
										</p>
										<p className="text-sm font-medium">
											{formatLoadDisplay(ex.load_percentage)}
										</p>
										{lh && (
											<>
												<p className="text-sm font-semibold text-emerald-400">
													→
													{lh.kg !== null
														? ` ${lh.kg}kg`
														: ` ${lh.kg_min}-${lh.kg_max}kg`}
												</p>
												<p className="text-[11px] text-muted-foreground/60 mt-0.5">
													{formatLoadSourceText(lh, t)}
												</p>
											</>
										)}
									</div>
								);
							})()}
						{ex.load_kg && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">
									{t("weight")}
								</p>
								<p className="text-sm font-medium">{ex.load_kg}kg</p>
							</div>
						)}
						{ex.rpe && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">RPE</p>
								<p className="text-sm font-medium">{ex.rpe}</p>
							</div>
						)}
						{ex.rir && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">RIR</p>
								<p className="text-sm font-medium">{ex.rir}</p>
							</div>
						)}
						{ex.rest_seconds && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">
									{t("restLabel")}
								</p>
								<p className="text-sm font-medium">
									{formatRestDisplay(ex.rest_seconds)}
								</p>
							</div>
						)}
					</div>
				)}
			</div>
		);
	};

	/* ── SUPERSET ── */
	const supersetExercises = asArray(exercise.superset_exercises);
	if (meth === "superset" && supersetExercises.length > 0) {
		return (
			<div className="mt-3 space-y-2">
				<div className="flex items-center gap-2">
					<Repeat className="w-3.5 h-3.5 text-volt" />
					<span className="text-xs font-medium text-volt">
						Superset — {exercise.superset_sets || exercise.sets || "?"}{" "}
						{getRoundLabelByValue(exercise.superset_sets || exercise.sets, t)}
					</span>
					{exercise.superset_rest_seconds && (
						<span className="text-xs text-muted-foreground ml-auto">
							<Clock className="w-3 h-3 inline mr-0.5" />
							{t("restLabel")}:{" "}
							{formatRestDisplay(exercise.superset_rest_seconds)}
						</span>
					)}
				</div>
				{/* Superset exercises (A, B, C, ...) */}
				{supersetExercises.map((se, i) => {
					const rows = asArray(se?.set_rows).filter(
						(row) => row && typeof row === "object",
					);
					const row = rows[0] || null;
					const seName = (se?.exercise_name || "").toLowerCase();
					const seLib =
						safeExerciseLibrary[se?.exercise_id] ||
						(seName ? safeExerciseLibrary[`_name_${seName}`] : null) ||
						null;
					const seHasMedia =
						hasMediaUrl(seLib?.video_url) || hasMediaUrl(seLib?.image_url);
					const seMediaExercise = {
						...se,
						...seLib,
						video_url: toSafeUrlString(seLib?.video_url),
						image_url: toSafeUrlString(seLib?.image_url),
					};
					const repsValue = row?.reps || se?.reps || null;
					const loadPct = row?.load_percentage || se?.load_percentage || null;
					const loadKg = row?.load_kg || se?.load_kg || null;
					const rpeValue = row?.rpe || se?.rpe || null;
					const rirValue = row?.rir || se?.rir || null;
					const rowNotes = row?.notes || se?.notes || "";
					const loadHint = loadPct
						? getSuggestedLoadHint(
								safeLoadSuggestions,
								getLoadReferenceId(se, row),
								loadPct,
								getLoadReferenceName(se, row),
							)
						: null;
					return (
						<div
							key={i}
							className="text-xs bg-secondary/35 rounded-sm px-2.5 py-1.5 space-y-0.5"
						>
							<div className="flex items-start gap-2">
								<p className="text-foreground leading-tight flex-1 min-w-0">
									<span className="text-volt font-semibold mr-1">
										{String.fromCharCode(65 + i)}:
									</span>
									<span className="font-medium">
										{se?.exercise_name || "-"}
									</span>
									{repsValue && (
										<span className="text-muted-foreground">
											{" "}
											· {repsValue}
										</span>
									)}
									{loadPct && (
										<span className="text-volt">
											{" "}
											· @{formatLoadDisplay(loadPct)}
											{loadHint && (
												<span className="text-emerald-400 font-semibold">
													{" "}
													→
													{loadHint.kg !== null
														? ` ${loadHint.kg}kg`
														: ` ${loadHint.kg_min}-${loadHint.kg_max}kg`}
												</span>
											)}
										</span>
									)}
									{loadKg && <span className="text-volt"> · {loadKg}kg</span>}
									{rpeValue && (
										<span className="text-muted-foreground">
											{" "}
											· RPE {rpeValue}
										</span>
									)}
									{rirValue && (
										<span className="text-muted-foreground">
											{" "}
											· RIR {rirValue}
										</span>
									)}
								</p>
								{seHasMedia && (
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											onOpenMedia?.(seMediaExercise);
										}}
										className="shrink-0 flex items-center gap-1 text-[10px] text-volt hover:text-volt/80 active:opacity-70"
									>
										{hasMediaUrl(seLib?.video_url) ? (
											<Play className="w-3 h-3" />
										) : (
											<Image className="w-3 h-3" />
										)}
										<span>{t("mediaLabel")}</span>
									</button>
								)}
							</div>
							{loadHint?.source_label && (
								<p className="text-[10px] text-muted-foreground/60 leading-tight">
									{loadHint.reference_name
										? `${loadHint.reference_name} · `
										: ""}
									{formatLoadSourceText(loadHint, t)}
								</p>
							)}
							{rowNotes && (
								<p className="text-[10px] text-muted-foreground italic leading-tight">
									{rowNotes}
								</p>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	/* ── EMOM (Strength) ── */
	const emomRounds = getDisplayEmomRounds(exercise);
	if (meth === "emom" && emomRounds.length > 0) {
		return (
			<div className="mt-3 space-y-1.5">
				{/* Compact header */}
				<div className="flex items-center gap-2">
					<Timer className="w-3.5 h-3.5 text-volt shrink-0" />
					<span className="text-xs font-medium text-volt">
						{formatEmomHeadline(exercise, t)}
					</span>
				</div>
				<div className="space-y-2">
					{emomRounds.map((round, roundIndex) => (
						<div
							key={round.round_id || roundIndex}
							className="space-y-1.5 bg-volt/10 border border-volt/20 rounded-sm px-3 py-2"
						>
							<div className="space-y-1">
								{asArray(round.items).map((item, itemIndex) => {
									const itemName = (item.exercise_name || "").toLowerCase();
									const itemLib = item.is_custom
										? null
										: safeExerciseLibrary[item.exercise_id] ||
											(itemName
												? safeExerciseLibrary[`_name_${itemName}`]
												: null) ||
											null;
									const itemHasMedia =
										hasMediaUrl(itemLib?.video_url) ||
										hasMediaUrl(itemLib?.image_url);
									const itemReferenceId =
										item.load_reference_exercise_id ||
										item.exercise_id ||
										itemLib?.exercise_id ||
										itemLib?._id ||
										null;
									const itemReferenceName =
										item.load_reference_exercise_name ||
										item.exercise_name ||
										null;
									const itemLoadHint = item.load
										? getSuggestedLoadHint(
												safeLoadSuggestions,
												itemReferenceId,
												item.load,
												itemReferenceName,
											)
										: null;
									return (
										<div
											key={`${round.round_id || roundIndex}-${item.item_id || itemIndex}`}
											className="flex items-start gap-2 text-xs bg-background/50 rounded-sm px-2.5 py-2"
										>
											{itemIndex === 0 && (
												<span className="shrink-0 text-[10px] uppercase tracking-wider text-volt font-medium min-w-[64px]">
													{getEmomRoundLabel(
														round,
														roundIndex,
														language,
													)}
												</span>
											)}
											{itemIndex > 0 && (
												<span className="shrink-0 min-w-[64px]" />
											)}
											<span className="flex-1 min-w-0 leading-tight">
												{item.is_custom ? (
													item.custom_text
												) : (
													<>
														{item.reps && (
															<span className="font-medium">{item.reps} </span>
														)}
														<span>{item.exercise_name}</span>
														{item.load && (
															<span className="text-volt ml-1 inline-flex items-center gap-1 flex-wrap">
																@
																{getEmomItemLoadDisplay(
																	item,
																	safeLoadSuggestions,
																	itemReferenceId,
																)}
															</span>
														)}
													</>
												)}
												{itemLoadHint && itemLoadHint.source_label && (
													<div className="text-muted-foreground/60 mt-1 text-[10px]">
														{itemLoadHint.reference_name
															? `${itemLoadHint.reference_name} · `
															: ""}
														{formatLoadSourceText(itemLoadHint, t)}
													</div>
												)}
												{item.notes && (
													<span className="text-muted-foreground ml-1 italic text-[10px]">
														({item.notes})
													</span>
												)}
											</span>
											{itemHasMedia && (
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														onOpenMedia?.({
															...item,
															...itemLib,
															video_url: toSafeUrlString(itemLib?.video_url),
															image_url: toSafeUrlString(itemLib?.image_url),
														});
													}}
													className="shrink-0 w-6 h-6 rounded-sm bg-volt/10 flex items-center justify-center active:opacity-70"
												>
													{hasMediaUrl(itemLib?.video_url) ? (
														<Play className="w-3 h-3 text-volt" />
													) : (
														<Image className="w-3 h-3 text-volt" />
													)}
												</button>
											)}
										</div>
									);
								})}
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	/* ── COMPLEX (Strength) ── */
	const complexExercises = asArray(exercise.complex_exercises);
	if (meth === "complex" && complexExercises.length > 0) {
		return (
			<div className="mt-3 space-y-2">
				{/* Complex header */}
				<div className="flex items-center gap-2">
					<Repeat className="w-3.5 h-3.5 text-volt" />
					<span className="text-xs font-medium text-volt">
						{t("complexMethodLabel")}
					</span>
				</div>

				{/* Loading scheme (set_rows apply to the whole complex sequence) */}
				{renderStrengthBlock(exercise, null)}

				{/* Sub-exercises list — one row per exercise with media access */}
				<div className="space-y-1">
					<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
						{language === "it" ? "Sequenza" : "Sequence"}
					</p>
					{complexExercises.map((ce, i) => {
						const ceName = (ce?.exercise_name || "").toLowerCase();
						const ceLib =
							safeExerciseLibrary[ce?.exercise_id] ||
							(ceName ? safeExerciseLibrary[`_name_${ceName}`] : null) ||
							null;
						const stepLabel = String.fromCharCode(65 + i);
						const ceHasMedia =
							hasMediaUrl(ceLib?.video_url) || hasMediaUrl(ceLib?.image_url);
						const ceMediaExercise = {
							...ce,
							...ceLib,
							video_url: toSafeUrlString(ceLib?.video_url),
							image_url: toSafeUrlString(ceLib?.image_url),
						};
						return (
							<div
								key={`${ce?.exercise_id || ce?.exercise_name || "complex"}_${i}`}
								className="flex items-center gap-2 text-xs bg-secondary/35 rounded-sm px-2.5 py-2"
							>
								<span className="shrink-0 text-volt font-semibold">
									{stepLabel}.
								</span>
								<span className="flex-1 min-w-0 font-medium truncate">
									{ce?.exercise_name || "-"}
								</span>
								{ceHasMedia && (
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											onOpenMedia?.(ceMediaExercise);
										}}
										className="shrink-0 flex items-center gap-1 text-[10px] text-volt hover:text-volt/80 active:opacity-70"
									>
										{hasMediaUrl(ceLib?.video_url) ? (
											<Play className="w-3 h-3" />
										) : (
											<Image className="w-3 h-3" />
										)}
										<span>{t("mediaLabel")}</span>
									</button>
								)}
							</div>
						);
					})}
				</div>

				{exercise.custom_workout_text && (
					<div className="text-xs bg-secondary/50 rounded-sm px-3 py-2 whitespace-pre-wrap">
						{exercise.custom_workout_text}
					</div>
				)}
			</div>
		);
	}

	/* ── STANDARD STRENGTH ── */
	if (type === "strength") {
		return (
			<div className="mt-3 space-y-2">
				{renderStrengthBlock(exercise, exercise.exercise_name)}
				{exercise.custom_workout_text && (
					<div className="text-xs bg-secondary/50 rounded-sm px-3 py-2 whitespace-pre-wrap">
						{exercise.custom_workout_text}
					</div>
				)}
			</div>
		);
	}

	/* ── DRY FIRE / LIVE FIRE ──
	   La prescrizione di tiro vive in un sotto-oggetto annidato: qui si legge,
	   non si ricompone. Reso in schede di coppie etichetta/valore perché
	   l'atleta la consulta al poligono, sul telefono, spesso in fretta. */
	if (type === "dry_fire" || type === "live_fire") {
		const shooting = exercise.shooting || {};
		const focusLabel = SHOOTING_FOCUS_OPTIONS.find(
			(f) => f.value === shooting.drill_focus,
		)?.label;
		const weaponLabel = {
			handgun: "Pistola",
			rifle: "Carabina",
			shotgun: "Fucile",
			pcc: "PCC",
		}[shooting.firearm_type];

		const facts = [
			focusLabel && ["Focus", focusLabel],
			weaponLabel && ["Arma", weaponLabel],
			type === "dry_fire" && exercise.sets && ["Ripetizioni", exercise.sets],
			shooting.par_time_seconds && [
				"Par time",
				`${shooting.par_time_seconds}s`,
			],
			plannedSeriesCount(shooting) > 0 && ["Serie", plannedSeriesCount(shooting)],
			// I colpi sono **per serie**; il totale è ciò che si porta al campo.
			roundsPerSeries(shooting) > 0 && [
				plannedSeriesCount(shooting) > 1 ? "Colpi / serie" : "Colpi",
				roundsPerSeries(shooting),
			],
			plannedSeriesCount(shooting) > 1 &&
				roundsPerSeries(shooting) > 0 && [
					"Colpi totali",
					totalRoundsPlanned(shooting),
				],
		].filter(Boolean);

		// I bersagli possono stare a distanze diverse — un drill di cadenze ne ha
		// tre a 5, 10 e 15 metri — quindi si elencano, non si riassumono in un
		// numero solo.
		const targetsLine = formatShootingTargets(shooting.targets);

		const isDry = type === "dry_fire";
		// Formato libero: niente schede di numeri, solo cosa fare. Mostrare una
		// griglia di campi vuoti su un lavoro descritto a parole è rumore.
		const isFreeFormat = shooting.prescription_format === "free";
		// La piantina può venire dal drill o dall'allestimento della giornata:
		// il drill vince. `exercise.session` è iniettata dall'aggregatore quando
		// l'esercizio appartiene a una sessione con uno stage condiviso.
		const stage = resolveStageImage(exercise, exercise.session);
		// "Disegnato" = c'è qualcosa in piantina. Un bersaglio descritto a
		// tabella non ha coordinate e non si disegna.
		const hasDrawnStage =
			(shooting.stage_layout || []).length > 0 ||
			(shooting.targets || []).some(
				(target) => target.x_m !== null && target.x_m !== undefined,
			);

		return (
			<div className="space-y-2">
				{/* Modalità sempre in chiaro: a secco in casa o al poligono con le
				    munizioni sono due preparazioni diverse. */}
				<div
					className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-[11px] font-semibold uppercase tracking-wider ${
						isDry ? "bg-sky-400/10 text-sky-400" : "bg-red-400/10 text-red-400"
					}`}
				>
					{isDry ? "Dry fire · a secco" : "Live fire · a fuoco"}
				</div>
				{isFreeFormat && shooting.duration_minutes && (
					<p className="text-sm">
						<span className="text-muted-foreground">Durata: </span>
						<span className="font-medium">{shooting.duration_minutes} min</span>
					</p>
				)}
				{!isFreeFormat && facts.length > 0 && (
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
						{facts.map(([label, value]) => (
							<div
								key={label}
								className="bg-secondary/60 rounded-sm px-2 py-1.5"
							>
								<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
									{label}
								</p>
								<p className="text-sm font-medium">{value}</p>
							</div>
						))}
					</div>
				)}
				{!isFreeFormat && targetsLine && (
					<p className="text-xs">
						<span className="text-muted-foreground">Bersagli: </span>
						{targetsLine}
					</p>
				)}
				{shooting.target_setup && (
					<p className="text-xs">
						<span className="text-muted-foreground">Setup: </span>
						{shooting.target_setup}
					</p>
				)}
				{/* Condizioni di partenza: due righe, perché il regolamento ne
				    descrive due cose diverse — l'arma (8.1) e il tiratore (8.2).
				    `start_position` è il campo unico di prima: si legge ancora
				    dove c'è. */}
				{(shooting.start_condition_firearm || shooting.start_position) && (
					<p className="text-xs">
						<span className="text-muted-foreground">Arma alla partenza: </span>
						{startConditionLabel(
							START_CONDITION_FIREARM,
							shooting.start_condition_firearm || shooting.start_position,
						)}
					</p>
				)}
				{shooting.start_condition_shooter && (
					<p className="text-xs">
						<span className="text-muted-foreground">Posizione: </span>
						{startConditionLabel(
							START_CONDITION_SHOOTER,
							shooting.start_condition_shooter,
						)}
					</p>
				)}
				{stage ? (
					<div className="space-y-1">
						<button
							type="button"
							className="w-full"
							onClick={() =>
								setZoomImage({
									src: getImageUrl(stage.url),
									title: "Piantina dello stage",
								})
							}
							title={t("pbShootZoom")}
						>
							<img
								src={getImageUrl(stage.url)}
								alt="Piantina dello stage"
								className="w-full max-h-56 object-contain rounded-sm bg-secondary/60"
							/>
						</button>
						<p className="text-[11px] text-muted-foreground">
							{stage.source === "session"
								? "Allestimento della giornata"
								: "Piantina del drill"}
							{stage.sector ? ` · ${stage.sector}` : ""}
							{stage.notes ? ` · ${stage.notes}` : ""}
						</p>
					</div>
				) : hasDrawnStage ? (
					/* Il coach ha **disegnato** lo stage invece di fotografarlo:
					   prima l'atleta non vedeva niente, perché si cercava solo
					   `stage_image_url`. Si apre a schermo pieno, in sola lettura
					   — è la stessa superficie del designer. */
					<StagePreview
						prescription={shooting}
						onOpen={() => setStageOpen(true)}
					/>
				) : null}
				{/* Gli altri file del drill: la foto del bersaglio, un dettaglio.
				    Ognuno si ingrandisce. */}
				{(shooting.attachments || []).length > 0 && (
					<div className="grid grid-cols-3 gap-1.5">
						{shooting.attachments.map((item, index) => (
							<button
								key={`${item.url}_${index}`}
								type="button"
								className="rounded-sm border border-border bg-secondary/60 overflow-hidden text-left"
								onClick={() =>
									setZoomImage({
										src: getImageUrl(item.url),
										title: item.label || t("pbShootAttachments"),
									})
								}
								title={t("pbShootZoom")}
							>
								<img
									src={getImageUrl(item.url)}
									alt={item.label || ""}
									className="w-full h-20 object-contain bg-white"
								/>
								{item.label && (
									<p className="text-[10px] text-muted-foreground px-1.5 py-1 truncate">
										{item.label}
									</p>
								)}
							</button>
						))}
					</div>
				)}
				{!stage && shooting.stage_sector && (
					<p className="text-xs">
						<span className="text-muted-foreground">Settore: </span>
						{shooting.stage_sector}
					</p>
				)}
				{(shooting.athlete_goal || shooting.success_criteria) && (
					<p className="text-xs text-volt">
						Obiettivo: {shooting.athlete_goal || shooting.success_criteria}
					</p>
				)}
				{/* Come va preso il punteggio. È il caso del brandeggio, dove si
				    segna solo lo split: senza questa riga l'atleta registra la
				    cosa sbagliata e il dato non serve a niente. */}
				{shooting.scoring_instructions && (
					<p className="text-xs rounded-sm bg-volt/10 border border-volt/25 px-2 py-1.5">
						<span className="text-muted-foreground">Da registrare: </span>
						{shooting.scoring_instructions}
					</p>
				)}
				{/* Le serie previste: l'atleta sa in che ordine sparare prima di
				    aprire il form punteggi. */}
				{(shooting.series || []).length > 0 && (
					<p className="text-xs">
						<span className="text-muted-foreground">Serie: </span>
						{shooting.series
							.map(
								(serie, i) =>
									serie.label ||
									(serie.distance_m ? `${serie.distance_m} m` : `#${i + 1}`),
							)
							.join(" · ")}
					</p>
				)}
				{exercise.notes && (
					<p className="text-xs text-muted-foreground whitespace-pre-wrap">
						{exercise.notes}
					</p>
				)}
				{/* Il foglio da portare al campo: chi spara ha occhiali, cuffie
				    e un timer, non il telefono in mano. */}
				{(stage ||
					hasDrawnStage ||
					(shooting.targets || []).length > 0 ||
					(shooting.attachments || []).length > 0 ||
					(!isDry && shooting.scoring_system_id)) && (
					<div className="pt-1">
						<StagePrintable
							name={exercise.exercise_name}
							prescription={shooting}
							t={t}
						/>
					</div>
				)}
				<StageDesignerDialog
					open={stageOpen}
					onOpenChange={setStageOpen}
					prescription={shooting}
					plannedRounds={shooting.rounds_planned}
					readOnly
					title={exercise.exercise_name || "Piantina dello stage"}
				/>
				<ImageZoomDialog
					open={!!zoomImage}
					onOpenChange={(open) => !open && setZoomImage(null)}
					src={zoomImage?.src}
					title={zoomImage?.title}
					alt={zoomImage?.title}
				/>
			</div>
		);
	}

	/* ── RUNNING ── */
	if (type === "running") {
		const intervalRows = asArray(exercise.interval_rows);
		const fartlekSegments = asArray(exercise.fartlek_segments);
		const runningType = String(exercise.running_type || "")
			.trim()
			.toLowerCase();
		const runningDistanceLabel = formatRunningDistanceLabel(
			exercise.distance_meters,
		);
		const shouldShowSegmentBlocks =
			runningType === "fartlek" || runningType === "progressivo";
		const showBaseRunningMetrics =
			intervalRows.length === 0 &&
			(fartlekSegments.length === 0 || runningType === "lungo");
		const runningPace = getRunningTargetPaceDisplay(exercise);
		const runningPaceReferenceText = formatRunningPaceReferenceText(
			exercise,
			t,
		);
		return (
			<div className="space-y-2 mt-3">
				{exercise.warmup_description && (
					<div className="text-xs bg-green-500/10 text-green-400 rounded-sm px-3 py-2">
						<span className="font-medium">{t("warmup")}:</span>{" "}
						{exercise.warmup_description}
					</div>
				)}
				{intervalRows.length > 0 && (
					<div className="space-y-1.5">
						{intervalRows.map((row, i) => (
							<div
								key={i}
								className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs bg-secondary/50 rounded-sm px-3 py-2"
							>
								<span className="font-medium">
									{row.repetitions || 1}x{row.distance || ""}
								</span>
								{(row.target_pace || row.target_pace_resolved) && (
									<span className="text-green-400">
										{row.target_pace || row.target_pace_resolved}
									</span>
								)}
								{row.recovery_time && (
									<span className="text-muted-foreground ml-auto">
										{t("restLabel")}: {row.recovery_time}{" "}
										{row.recovery_type === "jog" ? t("jogLabel") : ""}
									</span>
								)}
								{row.notes && (
									<span className="text-muted-foreground w-full text-[10px] italic">
										{row.notes}
									</span>
								)}
							</div>
						))}
					</div>
				)}
				{shouldShowSegmentBlocks && fartlekSegments.length > 0 && (
					<div className="space-y-1">
						{exercise.fartlek_repeat && exercise.fartlek_repeat > 1 && (
							<div className="text-xs text-green-400 mb-1">
								<Repeat className="w-3 h-3 inline mr-1" />
								{exercise.fartlek_repeat}x {t("blockLabel")}
							</div>
						)}
						{fartlekSegments.map((seg, i) => (
							<div
								key={i}
								className={cn(
									"text-xs rounded-sm px-3 py-2",
									seg.segment_type === "work" || seg.type === "work"
										? "bg-green-500/10 text-green-400"
										: "bg-blue-500/10 text-blue-400",
								)}
							>
								{seg.duration} —{" "}
								{seg.pace || seg.pace_resolved || seg.segment_type || seg.type}
								{seg.notes && (
									<span className="block text-[10px] opacity-70 mt-0.5">
										{seg.notes}
									</span>
								)}
							</div>
						))}
					</div>
				)}
				{showBaseRunningMetrics && (
					<div className="flex flex-wrap gap-2">
						{runningDistanceLabel && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">
									{t("distanceLabel")}
								</p>
								<p className="text-sm font-medium">{runningDistanceLabel}</p>
							</div>
						)}
						{exercise.duration_minutes && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">
									{t("duration")}
								</p>
								<p className="text-sm font-medium">
									{exercise.duration_minutes}'
								</p>
							</div>
						)}
						{runningPace && (
							<div className="bg-secondary/50 rounded-sm px-3 py-1.5 text-center">
								<p className="text-[10px] text-muted-foreground">
									{t("paceShort")}
								</p>
								<p className="text-sm font-medium">{runningPace}</p>
								{runningPaceReferenceText && (
									<p className="mt-1 text-[10px] text-muted-foreground leading-tight">
										{runningPaceReferenceText}
									</p>
								)}
							</div>
						)}
					</div>
				)}
				{exercise.cooldown_description && (
					<div className="text-xs bg-blue-500/10 text-blue-400 rounded-sm px-3 py-2">
						<span className="font-medium">{t("cooldown")}:</span>{" "}
						{exercise.cooldown_description}
					</div>
				)}
				{exercise.custom_workout_text && (
					<div className="text-xs bg-secondary/50 rounded-sm px-3 py-2 whitespace-pre-wrap">
						{exercise.custom_workout_text}
					</div>
				)}
			</div>
		);
	}

	/* ── SWIM ── */
	if (type === "swim") {
		const swimSets = asArray(exercise.swim_sets);
		return (
			<div className="space-y-2 mt-3">
				{exercise.warmup_description && (
					<div className="text-xs bg-cyan-500/10 text-cyan-400 rounded-sm px-3 py-2">
						<span className="font-medium">{t("warmup")}:</span>{" "}
						{exercise.warmup_description}
					</div>
				)}
				{exercise.swim_equipment && (
					<div className="text-xs text-muted-foreground">
						{t("equipment")}: {exercise.swim_equipment}
					</div>
				)}
				{swimSets.length > 0 && (
					<div className="space-y-1.5">
						{swimSets.map((set, i) => (
							<div
								key={i}
								className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs bg-secondary/50 rounded-sm px-3 py-2"
							>
								<span className="font-medium">
									{set.repetitions || 1}x{set.distance || ""}m
								</span>
								{set.stroke && (
									<span className="text-cyan-400">{set.stroke}</span>
								)}
								{set.intensity && (
									<span className="text-muted-foreground">{set.intensity}</span>
								)}
								{set.target_time && (
									<span className="text-muted-foreground">
										{set.target_time}
									</span>
								)}
								{set.equipment && (
									<span className="text-muted-foreground">{set.equipment}</span>
								)}
								{set.rest && (
									<span className="text-muted-foreground ml-auto">
										{t("restLabel")}: {set.rest}
									</span>
								)}
								{set.rest_seconds && !set.rest && (
									<span className="text-muted-foreground ml-auto">
										{t("restLabel")}: {formatRestDisplay(set.rest_seconds)}
									</span>
								)}
								{set.notes && (
									<span className="text-muted-foreground w-full text-[10px] italic">
										{set.notes}
									</span>
								)}
							</div>
						))}
					</div>
				)}
				{exercise.custom_workout_text && (
					<div className="text-xs bg-secondary/50 rounded-sm px-3 py-2 whitespace-pre-wrap">
						{exercise.custom_workout_text}
					</div>
				)}
				{exercise.cooldown_description && (
					<div className="text-xs bg-blue-500/10 text-blue-400 rounded-sm px-3 py-2">
						<span className="font-medium">{t("cooldown")}:</span>{" "}
						{exercise.cooldown_description}
					</div>
				)}
			</div>
		);
	}

	/* ── BIKE / ERG ── */
	if (type === "bike" || type === "erg") {
		const bikeIntervals = asArray(exercise.bike_intervals);
		return (
			<div className="space-y-2 mt-3">
				{exercise.bike_terrain && (
					<Badge
						variant="outline"
						className="text-[10px] rounded-sm pointer-events-none select-none"
					>
						{exercise.bike_terrain === "indoor"
							? t("indoorLabel")
							: exercise.bike_terrain === "outdoor"
								? t("outdoorLabel")
								: t("mixedLabel")}
					</Badge>
				)}
				{exercise.warmup_description && (
					<div className="text-xs bg-orange-500/10 text-orange-400 rounded-sm px-3 py-2">
						<span className="font-medium">{t("warmup")}:</span>{" "}
						{exercise.warmup_description}
					</div>
				)}
				{bikeIntervals.length > 0 && (
					<div className="space-y-1.5">
						{bikeIntervals.map((intv, i) => (
							<div
								key={i}
								className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs bg-secondary/50 rounded-sm px-3 py-2"
							>
								<span className="font-medium">
									{intv.repetitions || 1}x{intv.duration || ""}
								</span>
								{intv.intensity && (
									<span className="text-orange-400">{intv.intensity}</span>
								)}
								{intv.power && (
									<span className="text-muted-foreground">{intv.power}W</span>
								)}
								{intv.cadence && (
									<span className="text-muted-foreground">
										{intv.cadence}rpm
									</span>
								)}
								{intv.recovery && (
									<span className="text-muted-foreground ml-auto">
										{t("restLabel")}: {intv.recovery}
									</span>
								)}
								{intv.notes && (
									<span className="text-muted-foreground w-full text-[10px] italic">
										{intv.notes}
									</span>
								)}
							</div>
						))}
					</div>
				)}
				{exercise.bike_power_target && (
					<div className="text-xs text-muted-foreground">
						{t("targetPower")}: {exercise.bike_power_target}
					</div>
				)}
				{exercise.bike_cadence_target && (
					<div className="text-xs text-muted-foreground">
						{t("targetCadence")}: {exercise.bike_cadence_target}
					</div>
				)}
				{exercise.custom_workout_text && (
					<div className="text-xs bg-secondary/50 rounded-sm px-3 py-2 whitespace-pre-wrap">
						{exercise.custom_workout_text}
					</div>
				)}
				{exercise.cooldown_description && (
					<div className="text-xs bg-blue-500/10 text-blue-400 rounded-sm px-3 py-2">
						<span className="font-medium">{t("cooldown")}:</span>{" "}
						{exercise.cooldown_description}
					</div>
				)}
			</div>
		);
	}

	/* ── CONDITIONING ── */
	if (type === "conditioning") {
		const conditioningExercises = asArray(exercise.conditioning_exercises);
		const emomRounds = getDisplayEmomRounds(exercise);
		const forTimeRoundsDisplay = getForTimeRoundsDisplay(exercise.rounds);
		const schemaFields = asArray(exercise.result_schema);
		const condType = String(exercise.conditioning_type || "")
			.trim()
			.toLowerCase();
		const scoreType = String(exercise.score_type || "")
			.trim()
			.toLowerCase();
		const hiitWorkSeconds = getHiitWorkSecondsPerRound(exercise);
		const hiitWorkShort = hiitWorkSeconds
			? formatHiitIntervalShort(hiitWorkSeconds)
			: "";
		const hiitRestPerRoundSeconds = getHiitRestSecondsPerRound(exercise);
		const hiitRestShort = formatRestDisplay(
			exercise.hiit_rest_interval || hiitRestPerRoundSeconds,
		);
		const hiitOnLabel = hiitWorkShort || "";
		const hiitOffLabel =
			exercise.hiit_rest_mode === "ratio"
				? exercise.hiit_rest_ratio
					? `${normalizeSmartRatioInput(exercise.hiit_rest_ratio)}`
					: ""
				: hiitRestShort
					? `${hiitRestShort}`
					: "";
		const hiitRestChipLabel =
			exercise.hiit_rest_mode === "ratio"
				? t("hiitRatioLabel")
				: t("hiitRecoveryLabel");
		const freeCustomText = String(
			exercise?.conditioning_free_text ||
				exercise?.custom_workout_text ||
				exercise?.conditioning_config?.conditioning_free_text ||
				exercise?.conditioning_config?.custom_workout_text ||
				"",
		).trim();
		const conditioningBadgeLabel =
			condType === "free" || condType === "custom"
				? t("customWodLabel")
				: getLocalizedOptionLabel(
						CONDITIONING_LABELS,
						exercise.conditioning_type,
						language,
						exercise.conditioning_type,
					);
		const scorePreviewFallback = (() => {
			if (schemaFields.length > 0) return "";
			if (condType === "amrap") return t("completedRepsLabel");
			if (condType === "time_or_reps") return "MM:SS / Reps";
			if (condType === "for_time" && scoreType !== "for_time")
				return "MM:SS / Reps";
			if (condType === "for_time") return "MM:SS";
			if (condType === "emom") return t("freeScoreLabel");
			if (condType === "hiit") return t("scoreLabel");
			if (condType === "free" || condType === "custom") return t("scoreLabel");
			return t("scoreLabel");
		})();
		return (
			<div className="space-y-2 mt-3">
				<div className="flex gap-2 flex-wrap">
					{exercise.conditioning_type &&
						condType !== "free" &&
						condType !== "custom" && (
							<Badge
								variant="outline"
								className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs rounded-sm pointer-events-none select-none"
							>
								{conditioningBadgeLabel}
							</Badge>
						)}
					{exercise.time_cap_seconds && (
						<Badge
							variant="outline"
							className="text-xs rounded-sm pointer-events-none select-none"
						>
							<Clock className="w-3 h-3 mr-1" />
							{t("timeCapLabel")} {Math.floor(exercise.time_cap_seconds / 60)}'
							{exercise.time_cap_seconds % 60
								? `:${String(exercise.time_cap_seconds % 60).padStart(2, "0")}`
								: ""}
						</Badge>
					)}
					{condType !== "emom" && forTimeRoundsDisplay && (
						<Badge
							variant="outline"
							className="text-xs rounded-sm pointer-events-none select-none"
						>
							{forTimeRoundsDisplay}
						</Badge>
					)}
					{/* Round e cadenza con le stesse parole del blocco forza. */}
					{condType === "emom" &&
						getEmomStructureParts(exercise, t).map((part) => (
							<Badge
								key={part}
								variant="outline"
								className="text-xs rounded-sm pointer-events-none select-none"
							>
								{part}
							</Badge>
						))}
					{condType === "hiit" && exercise.hiit_cycles && (
						<Badge
							variant="outline"
							className="text-xs rounded-sm pointer-events-none select-none"
						>
							{exercise.hiit_cycles}{" "}
							{Number(exercise.hiit_cycles) === 1
								? t("roundLabel")
								: t("roundsLabel")}
						</Badge>
					)}
					{condType === "hiit" && (hiitWorkShort || hiitOffLabel) && (
						<div className="inline-flex flex-nowrap items-center gap-2 shrink-0">
							{hiitWorkShort && (
								<Badge
									variant="outline"
									className="text-xs rounded-sm pointer-events-none select-none border-volt/40 text-volt"
								>
									<span className="uppercase tracking-wide text-[10px] opacity-70 mr-1">
										{t("hiitWorkLabel")}
									</span>
									{hiitWorkShort}
								</Badge>
							)}
							{hiitOffLabel && (
								<Badge
									variant="outline"
									className="text-xs rounded-sm pointer-events-none select-none"
								>
									<span className="uppercase tracking-wide text-[10px] opacity-70 mr-1">
										{hiitRestChipLabel}
									</span>
									{hiitOffLabel}
								</Badge>
							)}
						</div>
					)}
				</div>
				{condType === "hiit" &&
					exercise.hiit_work_mode === "custom" &&
					exercise.hiit_work_custom_text && (
						<div className="text-xs bg-yellow-500/10 text-yellow-200 border border-yellow-500/20 rounded-sm px-3 py-2 whitespace-pre-wrap">
							{exercise.hiit_work_custom_text}
						</div>
					)}
				{exercise.conditioning_type === "emom" && emomRounds.length > 0 ? (
					<div className="space-y-2">
						{emomRounds.map((round, roundIndex) => (
							<div
								key={round.round_id || roundIndex}
								className="space-y-2 bg-yellow-500/10 border border-yellow-500/20 rounded-sm px-3 py-3"
							>
								<div className="text-[10px] uppercase tracking-wider text-yellow-400 font-medium">
									{getEmomRoundLabel(round, roundIndex, language)}
								</div>
								<div className="space-y-1.5">
									{asArray(round.items).map((item, itemIndex) => {
										const itemName = (item.exercise_name || "").toLowerCase();
										const itemLib = item.is_custom
											? null
											: safeExerciseLibrary[item.exercise_id] ||
												(itemName
													? safeExerciseLibrary[`_name_${itemName}`]
													: null) ||
												null;
										const itemHasMedia =
											hasMediaUrl(itemLib?.video_url) ||
											hasMediaUrl(itemLib?.image_url);
										const itemReferenceId =
											item.load_reference_exercise_id ||
											item.exercise_id ||
											itemLib?.exercise_id ||
											itemLib?._id ||
											null;
										const itemReferenceName =
											item.load_reference_exercise_name ||
											item.exercise_name ||
											null;
										const itemLoadHint = item.load
											? getSuggestedLoadHint(
													safeLoadSuggestions,
													itemReferenceId,
													item.load,
													itemReferenceName,
												)
											: null;
										return (
											<div
												key={item.item_id || itemIndex}
												className="flex items-start gap-2 text-xs bg-background/50 rounded-sm px-3 py-2"
											>
												<div className="flex-1 min-w-0 space-y-1">
													<div className="font-medium">
														{item.is_custom ? (
															item.custom_text
														) : (
															<>
																{item.reps && item.exercise_name
																	? `${item.reps} ${item.exercise_name}`
																	: item.reps
																		? item.reps
																		: item.exercise_name}
																{item.load && (
																	<span className="text-yellow-400 ml-2 inline-flex items-center gap-1 flex-wrap">
																		@
																		{getEmomItemLoadDisplay(
																			item,
																			safeLoadSuggestions,
																			itemReferenceId,
																		)}
																	</span>
																)}
															</>
														)}
													</div>
													{itemLoadHint && itemLoadHint.source_label && (
														<div className="text-[10px] text-muted-foreground/60">
															{itemLoadHint.reference_name
																? `${itemLoadHint.reference_name} · `
																: ""}
															{formatLoadSourceText(itemLoadHint, t)}
														</div>
													)}
													{item.notes && (
														<div className="text-[10px] text-muted-foreground italic">
															{item.notes}
														</div>
													)}
												</div>
												{itemHasMedia && (
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															onOpenMedia?.({
																...item,
																...itemLib,
																video_url: toSafeUrlString(itemLib?.video_url),
																image_url: toSafeUrlString(itemLib?.image_url),
															});
														}}
														className="shrink-0 w-7 h-7 rounded-sm bg-yellow-500/10 flex items-center justify-center active:opacity-70"
													>
														{hasMediaUrl(itemLib?.video_url) ? (
															<Play className="w-3.5 h-3.5 text-yellow-400" />
														) : (
															<Image className="w-3.5 h-3.5 text-yellow-400" />
														)}
													</button>
												)}
											</div>
										);
									})}
								</div>
							</div>
						))}
					</div>
				) : (
					conditioningExercises.length > 0 && (
						<div className="space-y-1">
							{conditioningExercises.map((ce, i) => {
								const ceName = (
									ce.exercise_name ||
									ce.name ||
									""
								).toLowerCase();
								const ceLib =
									safeExerciseLibrary[ce.exercise_id] ||
									(ceName ? safeExerciseLibrary[`_name_${ceName}`] : null) ||
									null;
								const ceHasMedia =
									hasMediaUrl(ceLib?.video_url) ||
									hasMediaUrl(ceLib?.image_url);
								return (
									<div
										key={i}
										className="flex items-center gap-2 text-xs bg-secondary/50 rounded-sm px-3 py-2"
									>
										<div className="flex-1 min-w-0">
											{ce.reps && (
												<span className="font-medium mr-1">{ce.reps}</span>
											)}
											{ce.exercise_name || ce.name}
											{ce.load && (
												<span className="text-muted-foreground ml-2">
													{ce.load}
												</span>
											)}
											{ce.notes && (
												<span className="text-muted-foreground ml-2 text-[10px] italic">
													{ce.notes}
												</span>
											)}
										</div>
										{ceHasMedia && (
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													onOpenMedia?.({
														...ce,
														...ceLib,
														video_url: toSafeUrlString(ceLib?.video_url),
														image_url: toSafeUrlString(ceLib?.image_url),
													});
												}}
												className="shrink-0 w-7 h-7 rounded-sm bg-volt/10 flex items-center justify-center active:opacity-70"
											>
												{hasMediaUrl(ceLib?.video_url) ? (
													<Play className="w-3.5 h-3.5 text-volt" />
												) : (
													<Image className="w-3.5 h-3.5 text-volt" />
												)}
											</button>
										)}
									</div>
								);
							})}
						</div>
					)
				)}
				{freeCustomText && (
					<div className="text-xs bg-secondary/50 rounded-sm px-3 py-2 whitespace-pre-wrap">
						{freeCustomText}
					</div>
				)}
				{!hideScorePreview && (
					<div className="rounded-sm border border-border/60 bg-background/40 px-3 py-2">
						<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
							{t("scoreLabel")}
						</p>
						{schemaFields.length > 0 ? (
							<div className="mt-1 space-y-1">
								{schemaFields.map((field) => (
									<div
										key={field.field_id || field.title}
										className="flex items-center justify-between gap-2 text-[11px]"
									>
										<span className="text-foreground/90 truncate">
											{field.title || t("resultLabel")}
										</span>
										<span className="text-muted-foreground uppercase tracking-wide shrink-0">
											{getScoreUnitPreviewLabel(field.unit, t)}
										</span>
									</div>
								))}
							</div>
						) : (
							<p className="mt-1 text-[11px] text-muted-foreground">
								{scorePreviewFallback}
							</p>
						)}
					</div>
				)}
			</div>
		);
	}

	return null;
}

/* ─── MEDIA PREVIEW (inline clickable) ─── */
function MediaPreview({ lib, exerciseName, onOpen }) {
	const { t } = useLanguage();
	if (!hasMediaUrl(lib?.video_url) && !hasMediaUrl(lib?.image_url)) return null;

	return (
		<button
			onClick={(e) => {
				e.stopPropagation();
				onOpen();
			}}
			className="w-full mt-3 relative rounded-sm overflow-hidden bg-secondary/30 h-32 flex items-center justify-center group active:opacity-80"
		>
			{hasMediaUrl(lib?.image_url) ? (
				<img
					src={getImageUrl(lib.image_url)}
					alt={exerciseName}
					className="w-full h-full object-cover"
				/>
			) : getYouTubeThumb(lib.video_url) ? (
				<img
					src={getYouTubeThumb(lib.video_url)}
					alt={exerciseName}
					className="w-full h-full object-cover"
				/>
			) : (
				<Play className="w-8 h-8 text-muted-foreground" />
			)}
			<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center">
				<span className="text-xs font-medium text-white flex items-center gap-1.5">
					{hasMediaUrl(lib?.video_url) ? (
						<>
							<Play className="w-4 h-4" /> {t("watchVideo")}
						</>
					) : (
						<>
							<Image className="w-4 h-4" /> {t("viewGuide")}
						</>
					)}
				</span>
			</div>
		</button>
	);
}

const RPE_SCALE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function RpeLabel({ className = "" }) {
	const { t } = useLanguage();
	const [open, setOpen] = useState(false);

	return (
		<>
			<span className={cn("inline-flex items-center gap-1", className)}>
				<span>RPE</span>
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="inline-flex h-4 w-4 items-center justify-center text-muted-foreground hover:text-foreground"
					aria-label={t("rpeInfoOpenAria")}
				>
					<CircleHelp className="h-3.5 w-3.5" />
				</button>
			</span>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-w-sm bg-card border-border rounded-sm">
					<DialogHeader>
						<DialogTitle>{t("rpeInfoTitle")}</DialogTitle>
						<DialogDescription className="text-sm text-muted-foreground space-y-1">
							<p>{t("rpeInfoDescription")}</p>
							<p>1 = {t("rpeInfoEasy")}</p>
							<p>7-8 = {t("rpeInfoHard")}</p>
							<p>10 = {t("rpeInfoMax")}</p>
						</DialogDescription>
					</DialogHeader>
				</DialogContent>
			</Dialog>
		</>
	);
}

function RpeInput({ value, onChange, className }) {
	const { t } = useLanguage();
	const [open, setOpen] = useState(false);
	const selectedValue = String(value || "").trim();

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className={cn(
						"w-full rounded-sm border border-transparent bg-secondary px-2.5 text-left text-sm flex items-center justify-between whitespace-nowrap",
						selectedValue ? "text-foreground" : "text-muted-foreground",
						className,
					)}
					aria-label={t("generalRpeLabel")}
				>
					<span className="whitespace-nowrap">
						{selectedValue || t("rpePlaceholder")}
					</span>
					<ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				sideOffset={6}
				className="w-[min(92vw,280px)] rounded-sm border-border bg-card p-2"
			>
				<button
					type="button"
					onClick={() => {
						onChange("");
						setOpen(false);
					}}
					className="mb-2 h-11 w-full rounded-sm border border-border bg-secondary/70 px-3 text-sm text-left"
				>
					{t("remove")} RPE
				</button>
				<div className="grid grid-cols-5 gap-2">
					{RPE_SCALE_OPTIONS.map((option) => {
						const optionValue = String(option);
						const isSelected = selectedValue === optionValue;
						return (
							<button
								key={option}
								type="button"
								onClick={() => {
									onChange(optionValue);
									setOpen(false);
								}}
								className={cn(
									"h-11 rounded-sm border text-base",
									isSelected
										? "border-volt bg-volt/15 text-volt"
										: "border-border bg-secondary/70 text-foreground",
								)}
							>
								{option}
							</button>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
}

/* ─── INLINE RESULT FORM (type-specific) ─── */
function ResultForm({
	exKey,
	exercise,
	resultForms,
	updateResultForm,
	onSubmit,
	onDelete,
	savingKey,
	isEditing,
}) {
	const { t, language } = useLanguage();
	const type = exercise?.exercise_type || "strength";
	const condType = String(
		exercise?.conditioning_type ||
			exercise?.conditioning_config?.conditioning_type ||
			"",
	)
		.trim()
		.toLowerCase();
	const scoreType = String(exercise?.score_type || "")
		.trim()
		.toLowerCase();
	// Backward compat: old exercises stored as conditioning_type="time_or_reps"
	const isTimeOrReps =
		condType === "time_or_reps" ||
		(condType === "for_time" && scoreType !== "for_time") ||
		((condType === "free" || condType === "custom" || !condType) &&
			scoreType === "time_or_reps");
	const form = resultForms[exKey] || {};
	const amrapRoundsPreview =
		type === "conditioning" && condType === "amrap"
			? formatAmrapScoreWithRoundsLabel(
					form.score,
					exercise?.conditioning_exercises ||
						exercise?.conditioning_config?.conditioning_exercises ||
						[],
					language,
				)
			: "";
	const meth = String(exercise?.methodology || "standard")
		.trim()
		.toLowerCase();
	const supersetExercises = asArray(exercise?.superset_exercises);
	const setRows = asArray(exercise?.set_rows);
	const runningIntervalRows = asArray(exercise?.interval_rows);
	const isSuperset = supersetExercises.length > 0;
	const supersetSetCount = isSuperset
		? getSupersetRoundCount(exercise)
		: 0;
	const supersetSetIndices = Array.from(
		{ length: supersetSetCount },
		(_, idx) => idx,
	);
	// Un campo per ogni parziale che la prescrizione fa prevedere: le ripetute
	// di una seduta a `interval_rows`, oppure i tratti di un fartlek o di un
	// progressivo (`fartlek_segments`). Prima i tratti non avevano campi, e
	// un progressivo 25'+25'+20' si registrava solo come totale.
	const runningFartlekSegments = asArray(exercise?.fartlek_segments);
	const runningRepeatSlots =
		type === "running" && runningIntervalRows.length > 0
			? (() => {
					const slots = [];
					let repeatCounter = 1;
					for (const row of runningIntervalRows) {
						const repsValue = Number(row?.repetitions);
						const repetitions =
							Number.isFinite(repsValue) && repsValue > 0 ? repsValue : 1;
						for (let i = 0; i < repetitions; i += 1) {
							slots.push({
								index: repeatCounter - 1,
								kind: "repeat",
								label: `${t("repeatShortLabel")} ${repeatCounter}${row?.distance ? ` · ${row.distance}` : ""}`,
							});
							repeatCounter += 1;
						}
					}
					return slots;
				})()
			: type === "running" && runningFartlekSegments.length > 0
				? (() => {
						const repeatRaw = Number(exercise?.fartlek_repeat);
						const blockRepeat =
							Number.isFinite(repeatRaw) && repeatRaw > 1 ? repeatRaw : 1;
						// In un blocco ripetuto si chiedono i tratti di lavoro: i
						// recuperi di corsa («slow») non si registrano uno per uno,
						// come non si registrano i recuperi delle ripetute.
						const asked = runningFartlekSegments.filter(
							(seg) =>
								blockRepeat === 1 ||
								normalizeFartlekSegmentType(seg?.segment_type || seg?.type) !==
									"slow",
						);
						const slots = [];
						let counter = 1;
						for (let r = 0; r < blockRepeat; r += 1) {
							for (const seg of asked) {
								const pace = String(seg?.pace || seg?.pace_resolved || "").trim();
								slots.push({
									index: counter - 1,
									kind: "segment",
									label: `${counter}. ${String(seg?.duration || "").trim()}`.trim(),
									hint: pace ? `@${pace}` : "",
								});
								counter += 1;
							}
						}
						return slots;
					})()
				: [];

	// ERG / Bike / Swim: expand interval rows into per-repeat result slots
	const bikeErgIntervalRows = asArray(exercise?.bike_intervals);
	const swimIntervalSets = asArray(exercise?.swim_sets);
	const cardioIntervalSlots = (() => {
		// Only for interval/conditioning types on erg/bike/swim
		if (type === "erg" || type === "bike") {
			if (bikeErgIntervalRows.length === 0) return [];
			const slots = [];
			let counter = 1;
			for (const row of bikeErgIntervalRows) {
				const repsValue = Number(row?.repetitions);
				const repetitions =
					Number.isFinite(repsValue) && repsValue > 0 ? repsValue : 1;
				const distLabel = row?.distance
					? ` · ${row.distance}`
					: row?.duration
						? ` · ${row.duration}`
						: "";
				for (let i = 0; i < repetitions; i += 1) {
					slots.push({
						index: counter - 1,
						label: `${t("repeatShortLabel")} ${counter}${distLabel}`,
					});
					counter += 1;
				}
			}
			return slots;
		}
		if (type === "swim") {
			// Only structured (intervals/threshold/sprint/drill) swim types have swim_sets
			if (swimIntervalSets.length === 0) return [];
			const slots = [];
			let counter = 1;
			for (const s of swimIntervalSets) {
				const repsValue = Number(s?.repetitions);
				const repetitions =
					Number.isFinite(repsValue) && repsValue > 0 ? repsValue : 1;
				const distLabel = s?.distance ? ` · ${s.distance}m` : "";
				for (let i = 0; i < repetitions; i += 1) {
					slots.push({
						index: counter - 1,
						label: `${t("repeatShortLabel")} ${counter}${distLabel}`,
					});
					counter += 1;
				}
			}
			return slots;
		}
		return [];
	})();
	const hasCardioIntervalSlots = cardioIntervalSlots.length > 0;

	const isCustomRunning =
		type === "running" &&
		String(exercise?.running_type || "")
			.trim()
			.toLowerCase() === "custom";
	const isConditioningForTime =
		type === "conditioning" && condType === "for_time" && !isTimeOrReps;
	const isConditioningFreeCustom =
		type === "conditioning" &&
		(!condType ||
			condType === "free" ||
			condType === "custom" ||
			condType === "hiit") &&
		!isTimeOrReps;
	const schemaFields = asArray(exercise?.result_schema);
	const hasSchema = type === "conditioning" && schemaFields.length > 0;
	// Custom-score AMRAP: mirror the plain-AMRAP round auto-calc when the logged
	// total reps can be derived from the custom result schema.
	const schemaAmrapRoundsPreview =
		hasSchema && condType === "amrap"
			? formatAmrapScoreWithRoundsLabel(
					extractAmrapTotalRepsFromSchemaValues(
						form.schema_values_raw,
						schemaFields,
					),
					exercise?.conditioning_exercises ||
						exercise?.conditioning_config?.conditioning_exercises ||
						[],
					language,
				)
			: "";
	// Tempo e distanza dell'uscita si chiedono SEMPRE per la corsa: una seduta
	// a ripetute ha i parziali E il totale (36:22 per 5,5 km), una corsa
	// «personalizzata» ha pur sempre dei chilometri. Prima sparivano appena
	// c'era una riga di ripetute o il tipo era custom, e il totale finiva in
	// nota o da nessuna parte.
	const showRunningLegacyMetrics = true;
	// For erg/bike/swim: when interval slots are present, suppress the generic time+distance fields
	const showCardioGenericMetrics = !hasCardioIntervalSlots;

	// Expand set_rows by their individual sets count; fall back to exercise.sets
	const expandedSetPresets = (() => {
		if (setRows.length > 0) {
			const expanded = [];
			setRows.forEach((row) => {
				const setsCount = Number(row?.sets) > 0 ? Number(row.sets) : 1;
				const pyramidReps = splitPyramidReps(row?.reps, setsCount);
				for (let i = 0; i < setsCount; i++) {
					expanded.push({
						reps: pyramidReps ? pyramidReps[i] : row?.reps || "",
						load: row?.load_percentage || row?.load_kg || "",
					});
				}
			});
			return expanded;
		}
		const setsCount = Number(exercise?.sets) > 0 ? Number(exercise.sets) : 0;
		if (setsCount > 0) {
			const pyramidReps = splitPyramidReps(exercise?.reps, setsCount);
			return Array.from({ length: setsCount }, (_, i) => ({
				reps: pyramidReps ? pyramidReps[i] : exercise?.reps || "",
				load: "",
			}));
		}
		return [];
	})();

	const useStrengthMultiRowInput =
		type === "strength" &&
		!isSuperset &&
		meth !== "custom" &&
		((meth === "complex" && expandedSetPresets.length > 0) ||
			(meth !== "complex" && expandedSetPresets.length > 1));
	const strengthRowPresets = useStrengthMultiRowInput
		? expandedSetPresets.map((preset, idx) => ({ index: idx, ...preset }))
		: [];
	const strengthResultRows = useStrengthMultiRowInput
		? strengthRowPresets.map((preset, idx) => ({
				row_index: idx,
				...(asArray(form.result_set_rows)[idx] || {}),
			}))
		: [];
	const prescribedSingleRepsLabel = setRows[0]?.reps || exercise?.reps || "";
	const isTimedStrengthPrescription = isTimedStrengthReps(
		prescribedSingleRepsLabel,
	);

	const updateStrengthResultRow = (rowIndex, field, value) => {
		const rows = [...asArray(form.result_set_rows)];
		rows[rowIndex] = {
			...(rows[rowIndex] || { row_index: rowIndex }),
			[field]: value,
		};
		updateResultForm(exKey, "result_set_rows", rows);
	};

	const getSupersetDetailIndex = (setIndex, exerciseIndex) =>
		setIndex * supersetExercises.length + exerciseIndex;

	// Helper to update superset_details in the form (set x exercise)
	const updateSupersetDetail = (setIndex, exerciseIndex, field, value) => {
		const details = [...(form.superset_details || [])];
		const detailIndex = getSupersetDetailIndex(setIndex, exerciseIndex);
		if (!details[detailIndex]) {
			const se = supersetExercises[exerciseIndex];
			details[detailIndex] = {
				exercise_id: se?.exercise_id || "",
				exercise_name: se?.exercise_name || "",
				row_index: setIndex,
				exercise_slot: exerciseIndex,
			};
		}
		details[detailIndex] = {
			...details[detailIndex],
			exercise_id:
				details[detailIndex]?.exercise_id ||
				supersetExercises[exerciseIndex]?.exercise_id ||
				"",
			exercise_name:
				details[detailIndex]?.exercise_name ||
				supersetExercises[exerciseIndex]?.exercise_name ||
				"",
			row_index: setIndex,
			exercise_slot: exerciseIndex,
			[field]: value,
		};
		updateResultForm(exKey, "superset_details", details);
	};

	// Detect if exercise uses weights (has load in prescription)
	const hasLoadPrescription =
		exercise?.load_percentage ||
		exercise?.load_kg ||
		setRows.some((r) => r?.load_percentage || r?.load_kg) ||
		supersetExercises.some((se) =>
			asArray(se?.set_rows).some((r) => r?.load_percentage || r?.load_kg),
		);
	const missingFields = new Set(asArray(form.__missing_fields));
	const hasMissingRequiredMetric = missingFields.size > 0;
	const sectionClass = (fieldKey, baseClass = "") =>
		cn(
			baseClass,
			missingFields.has(fieldKey)
				? "rounded-sm border border-blaze/60 bg-blaze/5 p-2"
				: "",
		);

	return (
		<div className="mt-4 space-y-3">
			<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
				{isEditing ? t("editResultTitle") : t("logResultTitle")}
			</p>
			{hasMissingRequiredMetric && (
				<p className="text-[11px] text-blaze leading-snug">
					{t("manualResultFillAtLeastOne")}
				</p>
			)}

			{/* SUPERSET: un blocco per esercizio (A, B, C) con la stessa griglia
			    dell'esercizio singolo a piu' serie. Il peso c'e' sempre, vuoto =
			    corpo libero, come nel singolo: prima compariva solo con un carico
			    prescritto e un'atleta non poteva scrivere i chili di affondi e fly.
			    Le reps partono da quelle prescritte finche' l'atleta non tocca il
			    campo (stessa regola delle righe del singolo). */}
			{type === "strength" && isSuperset ? (
				<div className={sectionClass("superset_details", "space-y-4")}>
					{supersetExercises.map((se, exerciseIndex) => {
						const seRow =
							asArray(se?.set_rows).find(
								(row) => row && typeof row === "object",
							) || null;
						const prescribedReps = String(seRow?.reps ?? se?.reps ?? "").trim();
						const timedReps = isTimedStrengthReps(prescribedReps);
						const letter = String.fromCharCode(65 + exerciseIndex);
						const seName = se?.exercise_name || "";
						const prescriptionParts = [
							prescribedReps
								? timedReps
									? prescribedReps
									: `${prescribedReps} ${t("repsShort")}`
								: "",
							seRow?.load_percentage
								? `@${formatLoadDisplay(seRow.load_percentage)}`
								: "",
							seRow?.load_kg ? `${seRow.load_kg}kg` : "",
							seRow?.rpe ? `RPE ${seRow.rpe}` : "",
							seRow?.rir ? `RIR ${seRow.rir}` : "",
						].filter(Boolean);
						return (
							<div key={exerciseIndex} className="space-y-2">
								<p className="text-xs font-medium flex items-baseline gap-1.5 flex-wrap">
									<span className="text-volt text-[10px] uppercase tracking-wider">
										{letter}
									</span>
									<span>{seName}</span>
									{prescriptionParts.length > 0 && (
										<span className="text-muted-foreground font-normal">
											· {prescriptionParts.join(" · ")}
										</span>
									)}
								</p>
								<div className="grid grid-cols-[58px_1fr_1fr_84px] gap-2 items-center text-[10px] text-muted-foreground uppercase tracking-wider px-0.5">
									<span />
									<span>{t("weightKgLabel")}</span>
									<span>{timedReps ? "sec" : t("repsShort")}</span>
									<span>
										<RpeLabel />
									</span>
								</div>
								<div className="space-y-2">
									{supersetSetIndices.map((setIndex) => {
										const detailIndex = getSupersetDetailIndex(
											setIndex,
											exerciseIndex,
										);
										const detail =
											(form.superset_details || [])[detailIndex] || {};
										const repsEdited =
											detail.reps !== undefined && detail.reps !== null;
										const displayReps = repsEdited
											? detail.reps
											: timedReps
												? ""
												: prescribedReps;
										const rowLabel = `${t("setsShort")} ${setIndex + 1}`;
										const fieldLabel = (field) =>
											`${letter} ${seName} · ${rowLabel} · ${field}`;
										return (
											<div
												key={setIndex}
												className="grid grid-cols-[58px_1fr_1fr_84px] gap-2 items-center"
											>
												<span className="text-[11px] text-muted-foreground uppercase tracking-wider">
													{rowLabel}
												</span>
												<Input
													type="text"
													inputMode="decimal"
													pattern="[0-9]*[.,]?[0-9]*"
													placeholder={t("weightKgLabel")}
													aria-label={fieldLabel(t("weightKgLabel"))}
													value={detail.weight_kg || ""}
													onChange={(e) =>
														updateSupersetDetail(
															setIndex,
															exerciseIndex,
															"weight_kg",
															e.target.value,
														)
													}
													onFocus={(e) => e.target.select()}
													className="bg-secondary border-transparent rounded-sm h-10 text-sm"
												/>
												<Input
													type="text"
													inputMode="numeric"
													placeholder={timedReps ? "sec" : t("repsShort")}
													aria-label={fieldLabel(
														timedReps ? "sec" : t("repsShort"),
													)}
													value={displayReps}
													onChange={(e) =>
														updateSupersetDetail(
															setIndex,
															exerciseIndex,
															"reps",
															e.target.value,
														)
													}
													onFocus={(e) => e.target.select()}
													className="bg-secondary border-transparent rounded-sm h-10 text-sm"
												/>
												<RpeInput
													value={detail.rpe || ""}
													onChange={(value) =>
														updateSupersetDetail(
															setIndex,
															exerciseIndex,
															"rpe",
															value,
														)
													}
													className="bg-secondary border-transparent rounded-sm h-10 text-sm"
												/>
											</div>
										);
									})}
								</div>
							</div>
						);
					})}
				</div>
			) : type === "strength" && meth === "custom" ? (
				<div>
					<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("exerciseNoteLabel")}
					</label>
					<Textarea
						placeholder={t("exerciseNotePlaceholder")}
						rows={3}
						value={form.notes || ""}
						onChange={(e) => updateResultForm(exKey, "notes", e.target.value)}
						className="bg-secondary border-transparent rounded-sm text-sm resize-none"
					/>
				</div>
			) : type === "strength" ? (
				<>
					{useStrengthMultiRowInput ? (
						<div className={sectionClass("result_set_rows", "space-y-2")}>
							<div className="grid grid-cols-[58px_1fr_1fr_84px] gap-2 items-center text-[10px] text-muted-foreground uppercase tracking-wider px-0.5">
								<span />
								<span>{t("weightKgLabel")}</span>
								<span>
									{strengthRowPresets.length > 0 &&
									strengthRowPresets.every((preset) =>
										isTimedStrengthReps(preset?.reps),
									)
										? "sec"
										: t("repsShort")}
								</span>
								<span>
									<RpeLabel />
								</span>
							</div>
							<div className="space-y-2">
								{strengthRowPresets.map((preset, idx) => {
									const rowResult = strengthResultRows[idx] || {};
									// Una volta che l'atleta tocca il campo (anche per svuotarlo)
									// vince sempre il suo valore: il prefill con le reps prescritte
									// vale solo finché la riga non è mai stata modificata,
									// altrimenti il campo si ripopola da solo mentre si cancella.
									const rowRepsEdited =
										rowResult.reps !== undefined && rowResult.reps !== null;
									const timedReps = isTimedStrengthReps(preset?.reps);
									const displayRowReps = rowRepsEdited
										? rowResult.reps
										: timedReps
											? ""
											: preset.reps || "";
									return (
										<div
											key={idx}
											className="grid grid-cols-[58px_1fr_1fr_84px] gap-2 items-center"
										>
											<span className="text-[11px] text-muted-foreground uppercase tracking-wider">
												{t("setsShort")} {idx + 1}
											</span>
											<Input
												type="text"
												inputMode="decimal"
												pattern="[0-9]*[.,]?[0-9]*"
												placeholder={t("weightKgLabel")}
												value={rowResult.weight_kg || ""}
												onChange={(e) =>
													updateStrengthResultRow(
														idx,
														"weight_kg",
														e.target.value,
													)
												}
												onFocus={(e) => e.target.select()}
												className="bg-secondary border-transparent rounded-sm h-10 text-sm"
											/>
											<Input
												type="text"
												inputMode="numeric"
												placeholder={timedReps ? "sec" : t("repsShort")}
												value={displayRowReps}
												onChange={(e) =>
													updateStrengthResultRow(idx, "reps", e.target.value)
												}
												onFocus={(e) => e.target.select()}
												className="bg-secondary border-transparent rounded-sm h-10 text-sm"
											/>
											<RpeInput
												value={rowResult.rpe || ""}
												onChange={(value) =>
													updateStrengthResultRow(idx, "rpe", value)
												}
												className="bg-secondary border-transparent rounded-sm h-10 text-sm"
											/>
										</div>
									);
								})}
							</div>
						</div>
					) : hasLoadPrescription ? (
						<div
							className={sectionClass(
								"strength_primary",
								"grid grid-cols-3 gap-2",
							)}
						>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{t("weightKgLabel")}
								</label>
								<Input
									type="text"
									inputMode="decimal"
									pattern="[0-9]*[.,]?[0-9]*"
									placeholder="0"
									value={form.weight_kg || ""}
									onChange={(e) =>
										updateResultForm(exKey, "weight_kg", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{isTimedStrengthPrescription ? "sec" : t("repsShort")}
								</label>
								<Input
									type={isTimedStrengthPrescription ? "text" : "number"}
									inputMode="numeric"
									placeholder={
										isTimedStrengthPrescription ? "sec" : t("repsShort")
									}
									value={
										form.reps !== undefined && form.reps !== null
											? form.reps
											: isTimedStrengthPrescription
												? ""
												: prescribedSingleRepsLabel
									}
									onChange={(e) =>
										updateResultForm(exKey, "reps", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									<RpeLabel />
								</label>
								<RpeInput
									value={form.rpe || ""}
									onChange={(value) => updateResultForm(exKey, "rpe", value)}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
						</div>
					) : meth === "emom" ? (
						<div
							className={sectionClass(
								"strength_primary",
								"grid grid-cols-2 gap-2",
							)}
						>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{t("freeScoreLabel")}
								</label>
								<Input
									type="text"
									placeholder={t("emomScorePlaceholder")}
									value={form.emom_score_text || ""}
									onChange={(e) =>
										updateResultForm(exKey, "emom_score_text", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									<RpeLabel />
								</label>
								<RpeInput
									value={form.rpe || ""}
									onChange={(value) => updateResultForm(exKey, "rpe", value)}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
						</div>
					) : (
						<div
							className={sectionClass(
								"strength_primary",
								"grid grid-cols-3 gap-2",
							)}
						>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{t("weightKgLabel")}
								</label>
								<Input
									type="text"
									inputMode="decimal"
									pattern="[0-9]*[.,]?[0-9]*"
									placeholder="0"
									value={form.weight_kg || ""}
									onChange={(e) =>
										updateResultForm(exKey, "weight_kg", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{isTimedStrengthPrescription ? "sec" : t("repsShort")}
								</label>
								<Input
									type={isTimedStrengthPrescription ? "text" : "number"}
									inputMode="numeric"
									placeholder={
										isTimedStrengthPrescription ? "sec" : t("repsShort")
									}
									value={
										form.reps !== undefined && form.reps !== null
											? form.reps
											: isTimedStrengthPrescription
												? ""
												: prescribedSingleRepsLabel
									}
									onChange={(e) =>
										updateResultForm(exKey, "reps", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									<RpeLabel />
								</label>
								<RpeInput
									value={form.rpe || ""}
									onChange={(value) => updateResultForm(exKey, "rpe", value)}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
						</div>
					)}
				</>
			) : null}

			{/* ── DRY FIRE / LIVE FIRE ──
			    Il form vive in un componente suo: questo file è già a ~8.400
			    righe e l'inserimento per stringa non è una manciata di input. */}
			{(type === "dry_fire" || type === "live_fire") && (
				<ShootingScoreForm
					prescription={{
						...(exercise?.shooting || {}),
						mode: type,
					}}
					value={form.shooting}
					onChange={(next) => updateResultForm(exKey, "shooting", next)}
				/>
			)}

			{/* ── RUNNING / SWIM / BIKE / ERG ── */}
			{(type === "running" ||
				type === "swim" ||
				type === "bike" ||
				type === "erg") && (
				<>
					{type === "running" && runningRepeatSlots.length > 0 && (
						<div
							className={sectionClass("running_interval_scores", "space-y-2")}
						>
							<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{runningRepeatSlots[0]?.kind === "segment"
									? t("segmentScoresLabel")
									: t("repeatScoresLabel")}
							</label>
							<div className="space-y-2">
								{runningRepeatSlots.map((slot) => {
									const currentScores = asArray(form.running_interval_scores);
									return (
										<div
											key={slot.index}
											className="grid grid-cols-[88px_1fr] gap-2 items-center"
										>
											<span className="text-[11px] text-muted-foreground uppercase tracking-wider leading-tight">
												{slot.label}
												{slot.hint && (
													<span className="block normal-case tracking-normal text-[10px] opacity-80">
														{slot.hint}
													</span>
												)}
											</span>
											<Input
												type="text"
												placeholder={
													slot.kind === "segment"
														? t("segmentResultPlaceholder")
														: t("repeatResultPlaceholder")
												}
												value={currentScores[slot.index] || ""}
												onChange={(e) => {
													const updated = [...currentScores];
													updated[slot.index] = e.target.value;
													updateResultForm(
														exKey,
														"running_interval_scores",
														updated,
													);
												}}
												onFocus={(e) => e.target.select()}
												className="bg-secondary border-transparent rounded-sm h-10 text-sm"
											/>
										</div>
									);
								})}
							</div>
						</div>
					)}
					{type === "running" && isCustomRunning && (
						<div>
							<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("customRunningResultLabel")}
							</label>
							<Textarea
								placeholder={t("customRunningResultPlaceholder")}
								rows={3}
								value={form.notes || ""}
								onChange={(e) =>
									updateResultForm(exKey, "notes", e.target.value)
								}
								className="bg-secondary border-transparent rounded-sm text-sm resize-none"
							/>
						</div>
					)}
					{/* ── ERG / BIKE / SWIM: per-repeat slot input ── */}
					{hasCardioIntervalSlots && (
						<div
							className={sectionClass("running_interval_scores", "space-y-2")}
						>
							<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("repeatScoresLabel")}
							</label>
							<p className="text-[11px] text-muted-foreground">
								{t("cardioRepeatResultHint")}
							</p>
							<div className="space-y-2">
								{cardioIntervalSlots.map((slot) => {
									const currentScores = asArray(form.running_interval_scores);
									return (
										<div
											key={slot.index}
											className="grid grid-cols-[88px_1fr] gap-2 items-center"
										>
											<span className="text-[11px] text-muted-foreground uppercase tracking-wider">
												{slot.label}
											</span>
											<Input
												type="text"
												placeholder={t("cardioRepeatResultPlaceholder")}
												value={currentScores[slot.index] || ""}
												onChange={(e) => {
													const updated = [...currentScores];
													updated[slot.index] = e.target.value;
													updateResultForm(
														exKey,
														"running_interval_scores",
														updated,
													);
												}}
												onFocus={(e) => e.target.select()}
												className="bg-secondary border-transparent rounded-sm h-10 text-sm"
											/>
										</div>
									);
								})}
							</div>
						</div>
					)}
					{type === "running" && runningRepeatSlots.length > 0 && (
						<div className="pt-1">
							<div className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("runningTotalsLabel")}
							</div>
							<p className="text-[11px] text-muted-foreground">
								{t("runningTotalsHint")}
							</p>
						</div>
					)}
					{(type !== "running" || showRunningLegacyMetrics) &&
						showCardioGenericMetrics && (
							<div className={sectionClass("cardio_primary")}>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{t("time")}
								</label>
								<div className="grid grid-cols-2 gap-2">
									<Input
										type="number"
										inputMode="numeric"
										placeholder={t("minShort")}
										value={form.time_min || ""}
										onChange={(e) =>
											updateResultForm(exKey, "time_min", e.target.value)
										}
										onFocus={(e) => e.target.select()}
										className="bg-secondary border-transparent rounded-sm h-11 text-base"
									/>
									<Input
										type="number"
										inputMode="numeric"
										placeholder={t("secShort")}
										value={form.time_sec || ""}
										onChange={(e) =>
											updateResultForm(exKey, "time_sec", e.target.value)
										}
										onFocus={(e) => e.target.select()}
										className="bg-secondary border-transparent rounded-sm h-11 text-base"
									/>
								</div>
							</div>
						)}
					{(type !== "running" || showRunningLegacyMetrics) &&
						showCardioGenericMetrics && (
							<div className={sectionClass("cardio_primary")}>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{type === "swim"
										? t("distanceMetersLabel")
										: t("distanceKmLabel")}
								</label>
								<Input
									type="number"
									step={type === "swim" ? "1" : "0.01"}
									inputMode="decimal"
									placeholder="0"
									value={form.score || ""}
									onChange={(e) =>
										updateResultForm(exKey, "score", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
						)}
					{/* FC media/max dell'uscita e RPE: i tre numeri che l'orologio e
					    l'atleta hanno sempre, nessuno dei tre era chiesto. Vuoto = non
					    disponibile, mai zero. */}
					{type === "running" && (
						<div
							className={sectionClass("cardio_primary", "grid grid-cols-3 gap-2")}
						>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{t("avgHeartRateLabel")}
								</label>
								<Input
									type="number"
									inputMode="numeric"
									min={30}
									max={250}
									placeholder="bpm"
									value={form.avg_heart_rate || ""}
									onChange={(e) =>
										updateResultForm(exKey, "avg_heart_rate", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{t("maxHeartRateLabel")}
								</label>
								<Input
									type="number"
									inputMode="numeric"
									min={30}
									max={250}
									placeholder="bpm"
									value={form.max_heart_rate || ""}
									onChange={(e) =>
										updateResultForm(exKey, "max_heart_rate", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
							<div>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									<RpeLabel />
								</label>
								<RpeInput
									value={form.rpe || ""}
									onChange={(value) => updateResultForm(exKey, "rpe", value)}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
						</div>
					)}
				</>
			)}

			{/* ── CONDITIONING: CUSTOM RESULT SCHEMA ── */}
			{hasSchema && (
				<div className={sectionClass("schema_values_raw", "space-y-3")}>
					<div className="text-[10px] text-muted-foreground uppercase tracking-wider">
						Risultati
					</div>
					{schemaFields.map((field) => {
						const fid = field.field_id;
						const unit = String(field.unit || "reps").toLowerCase();
						const rawMap = form.schema_values_raw || {};
						if (unit === "time_or_reps") {
							return (
								<div key={fid}>
									<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
										{field.title || "Risultato"}
									</label>
									<Input
										type="text"
										inputMode="text"
										placeholder="mm:ss oppure reps totali"
										value={rawMap[fid] ?? ""}
										onChange={(e) => {
											const next = { ...(form.schema_values_raw || {}) };
											next[fid] = e.target.value;
											updateResultForm(exKey, "schema_values_raw", next);
										}}
										onBlur={(e) => {
											const next = { ...(form.schema_values_raw || {}) };
											next[fid] = normalizeTimeOrRepsInput(e.target.value);
											updateResultForm(exKey, "schema_values_raw", next);
										}}
										onFocus={(e) => e.target.select()}
										className="bg-secondary border-transparent rounded-sm h-11 text-base"
									/>
									<div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
										<div>{t("timeOrRepsHintTime")}</div>
										<div>· {t("timeOrRepsHintReps")}</div>
									</div>
								</div>
							);
						}
						if (unit === "minutes_seconds") {
							const minVal = rawMap[`${fid}__min`] ?? "";
							const secVal = rawMap[`${fid}__sec`] ?? "";
							return (
								<div key={fid}>
									<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
										{field.title || "Risultato"}
									</label>
									<div className="grid grid-cols-2 gap-2">
										<Input
											type="number"
											inputMode="numeric"
											placeholder="min"
											value={minVal}
											onChange={(e) => {
												const next = {
													...(form.schema_values_raw || {}),
												};
												next[`${fid}__min`] = e.target.value;
												updateResultForm(exKey, "schema_values_raw", next);
											}}
											onFocus={(e) => e.target.select()}
											className="bg-secondary border-transparent rounded-sm h-11 text-base"
										/>
										<Input
											type="number"
											inputMode="numeric"
											placeholder="sec"
											value={secVal}
											onChange={(e) => {
												const next = {
													...(form.schema_values_raw || {}),
												};
												next[`${fid}__sec`] = e.target.value;
												updateResultForm(exKey, "schema_values_raw", next);
											}}
											onFocus={(e) => e.target.select()}
											className="bg-secondary border-transparent rounded-sm h-11 text-base"
										/>
									</div>
								</div>
							);
						}
						const inputProps = getInputPropsForUnit(unit);
						return (
							<div key={fid}>
								<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
									{field.title || "Risultato"}
								</label>
								<Input
									{...inputProps}
									inputMode={
										inputProps.type === "number" ? "decimal" : undefined
									}
									value={rawMap[fid] ?? ""}
									onChange={(e) => {
										const next = {
											...(form.schema_values_raw || {}),
										};
										next[fid] = e.target.value;
										updateResultForm(exKey, "schema_values_raw", next);
									}}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
						);
					})}
					{schemaAmrapRoundsPreview ? (
						<p className="text-[11px] text-muted-foreground">
							{schemaAmrapRoundsPreview}
						</p>
					) : null}
				</div>
			)}

			{/* ── CONDITIONING: AMRAP ── */}
			{type === "conditioning" && condType === "amrap" && !hasSchema && (
				<div className={sectionClass("score")}>
					<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("completedRepsLabel")}
					</label>
					<Input
						type="number"
						inputMode="numeric"
						placeholder={t("amrapTotalRepsPlaceholder")}
						value={form.score || ""}
						onChange={(e) => updateResultForm(exKey, "score", e.target.value)}
						onFocus={(e) => e.target.select()}
						className="bg-secondary border-transparent rounded-sm h-11 text-base"
					/>
					<p className="text-[11px] text-muted-foreground mt-1">
						{t("amrapTotalRepsHint")}
					</p>
					{amrapRoundsPreview ? (
						<p className="text-[11px] text-muted-foreground mt-1">
							{amrapRoundsPreview}
						</p>
					) : null}
				</div>
			)}

			{/* ── CONDITIONING: FOR TIME ── */}
			{type === "conditioning" &&
				condType === "for_time" &&
				!isTimeOrReps &&
				!hasSchema && (
					<div className={sectionClass("conditioning_for_time", "space-y-3")}>
						<div>
							<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("time")}
							</label>
							<div className="grid grid-cols-2 gap-2">
								<Input
									type="number"
									inputMode="numeric"
									placeholder={t("minShort")}
									value={form.time_min || ""}
									onChange={(e) =>
										updateResultForm(exKey, "time_min", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
								<Input
									type="number"
									inputMode="numeric"
									placeholder={t("secShort")}
									value={form.time_sec || ""}
									onChange={(e) =>
										updateResultForm(exKey, "time_sec", e.target.value)
									}
									onFocus={(e) => e.target.select()}
									className="bg-secondary border-transparent rounded-sm h-11 text-base"
								/>
							</div>
						</div>
						<div>
							<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("completedRepsLabel")}
							</label>
							<Input
								type="number"
								inputMode="numeric"
								placeholder="0"
								value={form.reps || ""}
								onChange={(e) =>
									updateResultForm(exKey, "reps", e.target.value)
								}
								onFocus={(e) => e.target.select()}
								className="bg-secondary border-transparent rounded-sm h-11 text-base"
							/>
						</div>
					</div>
				)}

			{/* ── CONDITIONING: TIME OR REPS (CrossFit timecap scoring) ── */}
			{type === "conditioning" && isTimeOrReps && !hasSchema && (
				<div className={sectionClass("time_or_reps_input", "space-y-2")}>
					<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("timeOrRepsLabel")}
					</label>
					<Input
						type="text"
						inputMode="text"
						placeholder={t("timeOrRepsPlaceholder")}
						value={form.time_or_reps_input || ""}
						onChange={(e) =>
							updateResultForm(exKey, "time_or_reps_input", e.target.value)
						}
						onBlur={(e) =>
							updateResultForm(
								exKey,
								"time_or_reps_input",
								normalizeTimeOrRepsInput(e.target.value),
							)
						}
						onFocus={(e) => e.target.select()}
						className="bg-secondary border-transparent rounded-sm h-11 text-base"
					/>
					<div className="rounded-sm bg-secondary/60 px-3 py-2 space-y-1">
						<p className="text-[11px] text-volt/80 leading-snug">
							✓ {t("timeOrRepsHintTime")}
						</p>
						<p className="text-[11px] text-muted-foreground leading-snug">
							· {t("timeOrRepsHintReps")}
						</p>
					</div>
				</div>
			)}

			{/* ── CONDITIONING: EMOM ── */}
			{type === "conditioning" && condType === "emom" && !hasSchema && (
				<div className={sectionClass("emom_score_text")}>
					<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("freeScoreLabel")}
					</label>
					<Input
						type="text"
						placeholder={t("emomRoundsPlaceholder")}
						value={form.emom_score_text || ""}
						onChange={(e) =>
							updateResultForm(exKey, "emom_score_text", e.target.value)
						}
						onFocus={(e) => e.target.select()}
						className="bg-secondary border-transparent rounded-sm h-11 text-base"
					/>
				</div>
			)}

			{/* ── CONDITIONING: FREE / CUSTOM (single free-text result) ── */}
			{isConditioningFreeCustom && !hasSchema && (
				<div className={sectionClass("free_score")}>
					<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("scoreLabel")}
					</label>
					<Textarea
						placeholder={t("customRunningResultPlaceholder")}
						rows={3}
						value={form.free_score || ""}
						onChange={(e) =>
							updateResultForm(exKey, "free_score", e.target.value)
						}
						className="bg-secondary border-transparent rounded-sm text-sm resize-none"
					/>
				</div>
			)}

			{/* ── SCALING (conditioning only) ── */}
			{type === "conditioning" && (
				<div>
					<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("scalingLabel")}
					</label>
					<Input
						type="text"
						placeholder={t("scalingPlaceholder")}
						value={form.scaling || ""}
						onChange={(e) => updateResultForm(exKey, "scaling", e.target.value)}
						className="bg-secondary border-transparent rounded-sm h-9 text-sm"
					/>
				</div>
			)}
			{/* ── NOTE (always except custom methodology / custom running) ── */}
			{meth !== "custom" && !isCustomRunning && (
				<div>
					<label className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("notes")}
					</label>
					<Textarea
						placeholder={t("notesResultPlaceholder")}
						rows={2}
						value={form.notes || ""}
						onChange={(e) => updateResultForm(exKey, "notes", e.target.value)}
						className="bg-secondary border-transparent rounded-sm text-sm resize-none"
					/>
				</div>
			)}
			{isEditing ? (
				<div className="sticky bottom-0 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 -mx-4 px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] border-t border-border/60 grid grid-cols-2 gap-2">
					<Button
						onClick={onDelete}
						disabled={savingKey === exKey}
						variant="outline"
						className="h-12 border-blaze/40 bg-blaze/10 text-blaze hover:bg-blaze/15 rounded-sm font-heading text-base"
					>
						{savingKey === exKey ? (
							<Loader2 className="w-4 h-4 animate-spin mr-2" />
						) : (
							<Trash2 className="w-4 h-4 mr-2" />
						)}
						{t("delete")}
					</Button>
					<Button
						onClick={onSubmit}
						disabled={savingKey === exKey}
						className="h-12 bg-volt hover:bg-volt/90 text-volt-foreground rounded-sm font-heading text-base"
					>
						{savingKey === exKey ? (
							<Loader2 className="w-4 h-4 animate-spin mr-2" />
						) : (
							<Send className="w-4 h-4 mr-2" />
						)}
						{t("updateResultLabel")}
					</Button>
				</div>
			) : (
				<Button
					onClick={onSubmit}
					disabled={savingKey === exKey}
					className="sticky bottom-0 bg-volt hover:bg-volt/90 text-volt-foreground rounded-sm font-heading text-base w-full h-12 border-t border-transparent"
				>
					{savingKey === exKey ? (
						<Loader2 className="w-4 h-4 animate-spin mr-2" />
					) : (
						<Send className="w-4 h-4 mr-2" />
					)}
					{t("saveResultLabel")}
				</Button>
			)}
		</div>
	);
}

/* ─── SAVED RESULT DISPLAY (type-aware) ─── */
function SavedResult({
	result,
	exerciseType,
	conditioningType,
	onEdit,
	exercise,
}) {
	const { t, language } = useLanguage();
	if (!result) return null;
	const type = exerciseType || "strength";
	const isCardio =
		type === "running" || type === "swim" || type === "bike" || type === "erg";
	const toText = (value) => String(value ?? "").trim();
	const hasContent = (value) => toText(value) !== "";
	const isZeroLike = (value) => {
		const text = toText(value);
		return text === "0" || text === "0.0";
	};
	const hasMeaningful = (value) => hasContent(value) && !isZeroLike(value);
	const displayResultReps = hasContent(result?.reps_text)
		? result.reps_text
		: hasMeaningful(result?.reps)
			? result.reps
			: null;
	const runningIntervalScores = Array.isArray(result?.interval_scores)
		? result.interval_scores.filter((value) => hasContent(value))
		: [];
	const displayRunningRepeatScores =
		type === "running" && runningIntervalScores.length > 0
			? runningIntervalScores.join(" · ")
			: "";
	const amrapDisplayLabel =
		type === "conditioning" && conditioningType === "amrap"
			? formatAmrapScoreWithRoundsLabel(
					result?.score,
					exercise?.conditioning_exercises ||
						exercise?.conditioning_config?.conditioning_exercises ||
						result?.conditioning_config?.conditioning_exercises ||
						[],
					language,
				) ||
				(hasContent(result?.reps_text)
					? `${result?.score || ""} reps (${result.reps_text})`
					: "")
			: "";
	const fmtTime = (s) => {
		if (!s) return null;
		return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
	};
	const hasSupersetDetails =
		result.superset_details && result.superset_details.length > 0;
	const groupedSupersetDetails = hasSupersetDetails
		? (() => {
				const rows = new Map();
				asArray(result.superset_details).forEach((detail, idx) => {
					const setIndex =
						typeof detail?.row_index === "number" && detail.row_index >= 0
							? detail.row_index
							: 0;
					const existing = rows.get(setIndex) || [];
					existing.push({
						...detail,
						__fallback_index: idx,
						__slot:
							typeof detail?.exercise_slot === "number"
								? detail.exercise_slot
								: idx,
					});
					rows.set(setIndex, existing);
				});
				return [...rows.entries()]
					.sort((a, b) => a[0] - b[0])
					.map(([setIndex, details]) => ({
						setIndex,
						details: [...details].sort((a, b) => a.__slot - b.__slot),
					}));
			})()
		: [];
	const hasResultSetRows =
		type === "strength" &&
		asArray(result.result_set_rows).some(
			(row) =>
				row?.weight_kg ||
				hasContent(row?.reps_text) ||
				hasMeaningful(row?.reps) ||
				row?.rpe,
		);
	const coachSeen = Boolean(
		result?.coach_feedback_seen || result?.coach_feedback_seen_at,
	);
	// Un risultato digitato dal coach al posto dell'atleta resta dell'atleta, ma
	// non deve passare per autoinserito: ne' per lui, ne' per chi lo rilegge.
	const coachEntryLabel = result?.logged_by_coach_id
		? t("coachSandboxLoggedByCoach")
		: result?.edited_by_coach_id
			? t("coachSandboxEditedByCoach")
			: "";
	const coachEntryName =
		result?.logged_by_coach_name || result?.edited_by_coach_name || "";
	return (
		<div className="mt-3 bg-green-500/10 border border-green-500/20 rounded-sm px-3 py-2">
			<div className="flex items-center justify-between mb-1">
				<div className="flex items-center gap-1.5 min-w-0">
					<p className="text-[10px] uppercase tracking-wider text-green-400">
						{t("resultRecordedLabel")}
					</p>
					{coachEntryLabel && (
						<span
							title={
								coachEntryName
									? `${coachEntryLabel} · ${coachEntryName}`
									: coachEntryLabel
							}
							className="text-[10px] uppercase tracking-wider text-volt border border-volt/30 rounded-sm px-1 shrink-0"
						>
							{coachEntryLabel}
						</span>
					)}
					{coachSeen && (
						<span
							title={t("feedbackSeenByCoach")}
							className="inline-flex items-center justify-center text-volt shrink-0"
						>
							<Check className="w-3 h-3" />
						</span>
					)}
				</div>
				{onEdit && (
					<button
						onClick={onEdit}
						className="flex items-center gap-1 text-[10px] text-volt hover:text-volt/80 active:opacity-70 uppercase tracking-wider font-medium"
					>
						<Pencil className="w-3 h-3" />
						{t("edit")}
					</button>
				)}
			</div>
			{/* Superset details */}
			{hasSupersetDetails ? (
				<div className="space-y-1.5">
					{groupedSupersetDetails.map((setGroup) => (
						<div
							key={`set-${setGroup.setIndex}`}
							className="rounded-sm border border-green-500/20 bg-green-500/5 px-2 py-1.5 space-y-1"
						>
							<p className="text-[10px] uppercase tracking-wider text-green-400 font-medium">
								{t("setsShort")} {setGroup.setIndex + 1}
							</p>
							{setGroup.details.map((d, detailIdx) => (
								<div
									key={`set-${setGroup.setIndex}-${detailIdx}`}
									className="flex items-center gap-2 text-xs border-l-2 border-green-500/30 pl-2"
								>
									<span className="text-green-400 text-[10px] font-medium uppercase">
										{String.fromCharCode(65 + detailIdx)}
									</span>
									<span className="font-medium truncate">
										{d.exercise_name}
									</span>
									<div className="flex gap-2 ml-auto shrink-0">
										{d.weight_kg && (
											<span>
												{t("weightKgLabel")}: {d.weight_kg}kg
											</span>
										)}
										{hasMeaningful(d.reps) && (
											<span>
												{t("repsLabel")}: {d.reps}
											</span>
										)}
										{d.rpe && (
											<span className="text-muted-foreground">
												RPE: {d.rpe}
											</span>
										)}
									</div>
								</div>
							))}
						</div>
					))}
				</div>
			) : hasResultSetRows ? (
				<div className="space-y-1.5">
					{asArray(result.result_set_rows).map((row, idx) => {
						const rowReps = hasContent(row?.reps_text)
							? row.reps_text
							: hasMeaningful(row?.reps)
								? row.reps
								: null;
						const rowIndex = Number.isInteger(Number(row?.row_index))
							? Number(row.row_index)
							: idx;
						const prescribedReps =
							exercise?.set_rows?.[rowIndex]?.reps ?? exercise?.reps;
						const hasRowValue = row?.weight_kg || rowReps || row?.rpe;
						if (!hasRowValue) return null;
						return (
							<div
								key={idx}
								className="flex items-center gap-2 text-xs border-l-2 border-green-500/30 pl-2"
							>
								<span className="text-green-400 text-[10px] font-medium uppercase">
									{t("setsShort")} {idx + 1}
								</span>
								<div className="flex gap-2 ml-auto shrink-0">
									{row.weight_kg && (
										<span>
											{t("weightKgLabel")}: {row.weight_kg}kg
										</span>
									)}
									{rowReps && (
										<span>
											{formatStrengthRepResult(
												rowReps,
												prescribedReps,
												t("repsShort"),
											)}
										</span>
									)}
									{row.rpe && (
										<span className="text-muted-foreground">
											RPE: {row.rpe}
										</span>
									)}
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
					{type === "strength" && (
						<>
							{result.weight_kg && (
								<span>
									<span className="text-muted-foreground">{t("weight")}:</span>{" "}
									{result.weight_kg}kg
								</span>
							)}
							{displayResultReps && (
								<span>
									<span className="text-muted-foreground">
										{isTimedStrengthReps(
											exercise?.set_rows?.[0]?.reps ?? exercise?.reps,
										)
											? "Tempo:"
											: isDistanceStrengthReps(
														exercise?.set_rows?.[0]?.reps ?? exercise?.reps,
												  )
												? `${t("distanceLabel")}:`
												: `${t("repsLabel")}:`}
									</span>{" "}
									{isTimedStrengthReps(
										exercise?.set_rows?.[0]?.reps ?? exercise?.reps,
									) ||
									isDistanceStrengthReps(
										exercise?.set_rows?.[0]?.reps ?? exercise?.reps,
									)
										? formatStrengthRepResult(
												displayResultReps,
												exercise?.set_rows?.[0]?.reps ?? exercise?.reps,
												t("repsShort"),
											)
										: displayResultReps}
								</span>
							)}
							{result.rpe && (
								<span>
									<span className="text-muted-foreground">RPE:</span>{" "}
									{result.rpe}
								</span>
							)}
						</>
					)}
					{isCardio && (
						<>
							{result.time_seconds && (
								<span>
									<span className="text-muted-foreground">{t("time")}:</span>{" "}
									{fmtTime(result.time_seconds)}
								</span>
							)}
							{displayRunningRepeatScores && (
								<span>
									<span className="text-muted-foreground">
										{t("repeatScoresLabel")}:
									</span>{" "}
									{displayRunningRepeatScores}
								</span>
							)}
							{result.score && (
								<span>
									<span className="text-muted-foreground">
										{t("distanceLabel")}:
									</span>{" "}
									{result.score}
									{type === "swim" ? "m" : "km"}
								</span>
							)}
							{(result.avg_heart_rate || result.max_heart_rate) && (
								<span>
									<span className="text-muted-foreground">FC:</span>{" "}
									{result.avg_heart_rate || "—"}
									{result.max_heart_rate ? ` / ${result.max_heart_rate}` : ""}
								</span>
							)}
							{result.rpe && (
								<span>
									<span className="text-muted-foreground">RPE:</span>{" "}
									{result.rpe}
								</span>
							)}
						</>
					)}
					{type === "conditioning" && (
						<>
							{(() => {
								const sch = asArray(exercise?.result_schema);
								const sv = result?.schema_values || {};
								if (sch.length === 0) return null;
								const parts = sch
									.map((field) => {
										const raw = sv[field.field_id];
										if (raw === undefined || raw === null || raw === "")
											return null;
										const unit = String(field.unit || "reps").toLowerCase();
										return (
											<span key={field.field_id}>
												<span className="text-muted-foreground">
													{field.title || t("scoreLabel")}:
												</span>{" "}
												{formatValueByUnit(raw, unit)}
											</span>
										);
									})
									.filter(Boolean);
								return parts.length > 0 ? <>{parts}</> : null;
							})()}
							{!asArray(exercise?.result_schema).length && result.score && (
								<span>
									<span className="text-muted-foreground">
										{conditioningType === "amrap"
											? `${t("repsShort")}:`
											: `${t("scoreLabel")}:`}
									</span>{" "}
									{conditioningType === "amrap"
										? amrapDisplayLabel || result.score
										: result.score}
									{conditioningType === "amrap" &&
									!amrapDisplayLabel &&
									result.reps_text ? (
										<span className="text-muted-foreground">
											{" "}
											· {result.reps_text}
										</span>
									) : null}
								</span>
							)}
							{!asArray(exercise?.result_schema).length &&
								!result.score &&
								amrapDisplayLabel && (
									<span>
										<span className="text-muted-foreground">
											{t("scoreLabel")}:
										</span>{" "}
										{amrapDisplayLabel}
									</span>
								)}
							{!asArray(exercise?.result_schema).length &&
								hasMeaningful(result.reps) && (
									<span>
										<span className="text-muted-foreground">
											{conditioningType === "time_or_reps" &&
											!result.time_seconds
												? `${t("timeOrRepsTimecapBadge")} · ${t("repsLabel")}:`
												: `${t("repsLabel")}:`}
										</span>{" "}
										{result.reps}
									</span>
								)}
							{!asArray(exercise?.result_schema).length &&
								result.time_seconds && (
									<span>
										<span className="text-muted-foreground">{t("time")}:</span>{" "}
										{fmtTime(result.time_seconds)}
									</span>
								)}
						</>
					)}
				</div>
			)}
			{type === "conditioning" && (
				<div className="flex items-center gap-1.5 mt-1.5">
					{hasContent(result?.scaling) ? (
						<span className="inline-flex items-center rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 text-[10px] font-heading uppercase tracking-wider">
							{t("scalingScaledLabel")}
						</span>
					) : (
						<span className="inline-flex items-center rounded-sm bg-green-500/15 text-green-400 border border-green-500/30 px-1.5 py-0.5 text-[10px] font-heading uppercase tracking-wider">
							{t("scalingRxLabel")}
						</span>
					)}
					{hasContent(result?.scaling) && (
						<span className="text-[11px] text-muted-foreground truncate">
							{result.scaling}
						</span>
					)}
				</div>
			)}
			{result.notes && (
				<p className="text-xs text-muted-foreground mt-1">
					{t("notes")}: {result.notes}
				</p>
			)}
		</div>
	);
}

/* ═══════════════════════════════════════════════
   ═══  MAIN DASHBOARD COMPONENT  ═══
   ═══════════════════════════════════════════════ */
// Build a stable key-name for an exercise (used for matching results to program exercises)
function buildExerciseKeyName(exercise) {
	if (
		exercise.exercise_type === "conditioning" &&
		exercise.conditioning_exercises?.length > 0
	) {
		const typeLabel =
			exercise.conditioning_type === "amrap"
				? "AMRAP"
				: exercise.conditioning_type === "for_time"
					? "For Time"
					: exercise.conditioning_type === "time_or_reps"
						? "Time or Reps"
						: exercise.conditioning_type === "emom"
							? "EMOM"
							: exercise.conditioning_type === "hiit"
								? "HIIT"
								: exercise.conditioning_type || "WOD";
		const subNames = exercise.conditioning_exercises
			.map((ce) => ce.exercise_name || ce.name)
			.filter(Boolean);
		return subNames.length > 0
			? `${typeLabel}: ${subNames.join(", ")}`
			: typeLabel;
	}
	return exercise.exercise_name;
}

function getConditioningTypeLabelForDisplay(conditioningType, language = "it") {
	switch (conditioningType) {
		case "amrap":
			return "AMRAP";
		case "for_time":
			return "For Time";
		case "time_or_reps":
			return "Time or Reps";
		case "emom":
			return "EMOM";
		case "hiit":
			return "HIIT";
		case "free":
		case "custom":
			return "Custom";
		default:
			return conditioningType || "Custom";
	}
}

function formatConditioningDisplayNameForAthlete(exercise, language = "it") {
	// Complex (strength): the auto-built "A + B + C" name gets truncated to an
	// unreadable "A + B + …" in the card title. Show a concise label instead —
	// the full movement sequence is always visible in the expanded detail.
	if (String(exercise?.methodology || "").toLowerCase() === "complex") {
		const n = asArray(exercise?.complex_exercises).length;
		if (n > 0) {
			return language === "it"
				? `Complex · ${n} ${n === 1 ? "movimento" : "movimenti"}`
				: `Complex · ${n} ${n === 1 ? "movement" : "movements"}`;
		}
		return "Complex";
	}

	if (exercise?.exercise_type === "running") {
		const runningType = String(exercise?.running_type || "")
			.trim()
			.toLowerCase();
		if (runningType === "custom") return "Running";
		if (runningType && RUNNING_LABELS.en[runningType])
			return `Running - ${getLocalizedOptionLabel(RUNNING_LABELS, runningType, language)}`;
		return exercise?.exercise_name || "";
	}

	if (exercise?.exercise_type === "swim") {
		const swimType = String(exercise?.swim_type || "")
			.trim()
			.toLowerCase();
		if (swimType === "custom") return "Swim";
		if (swimType && SWIM_LABELS.en[swimType])
			return `Swim - ${getLocalizedOptionLabel(SWIM_LABELS, swimType, language)}`;
		return exercise?.exercise_name || "";
	}

	if (exercise?.exercise_type === "bike") {
		const bikeType = String(exercise?.bike_type || "")
			.trim()
			.toLowerCase();
		if (bikeType === "custom") return "Bike";
		if (bikeType && BIKE_LABELS.en[bikeType])
			return `Bike - ${getLocalizedOptionLabel(BIKE_LABELS, bikeType, language)}`;
		return exercise?.exercise_name || "";
	}

	if (exercise?.exercise_type === "erg") {
		const ergType = String(exercise?.bike_type || "")
			.trim()
			.toLowerCase();
		if (ergType === "custom") return "Erg";
		if (ergType && ERG_LABELS.en[ergType])
			return `Erg - ${getLocalizedOptionLabel(ERG_LABELS, ergType, language)}`;
		return exercise?.exercise_name || "";
	}

	if (exercise?.exercise_type !== "conditioning") {
		return exercise?.exercise_name || "";
	}

	const rawName = (exercise?.exercise_name || "").trim();
	const condType = exercise?.conditioning_type;
	if (!condType || condType === "free" || condType === "custom") {
		// Strip any legacy "Custom" pollution from previously-saved names so
		// the athlete never sees the word "Custom".
		const cleaned = rawName
			.replace(/^Conditioning\s*-\s*/i, "")
			.replace(/\s*-\s*Custom\s*$/i, "")
			.replace(/^(Custom|Conditioning)$/i, "")
			.trim();
		return cleaned ? `Conditioning - ${cleaned}` : "Conditioning";
	}

	const typeLabel = getConditioningTypeLabelForDisplay(condType, language);
	const typeLower = typeLabel.toLowerCase();

	if (!rawName) return `Conditioning - ${typeLabel}`;

	const parts = rawName
		.split("-")
		.map((p) => p.trim())
		.filter(Boolean);

	if (rawName.toLowerCase() === typeLower) {
		return `Conditioning - ${typeLabel}`;
	}

	if (
		parts.length === 2 &&
		parts[0].toLowerCase() === typeLower &&
		parts[1].toLowerCase() === typeLower
	) {
		return `Conditioning - ${typeLabel}`;
	}

	if (
		parts.length >= 2 &&
		parts[parts.length - 1].toLowerCase() === typeLower
	) {
		const basePart = parts.slice(0, -1).join(" - ").trim();
		if (!basePart || basePart.toLowerCase() === typeLower) {
			return `Conditioning - ${typeLabel}`;
		}
		return `${basePart} - ${typeLabel}`;
	}

	return `${rawName} - ${typeLabel}`;
}

/**
 * Il flusso di inserimento e correzione di un risultato: stato del modulo,
 * apertura in inserimento o in modifica (`startEdit` riempie il modulo dal
 * risultato salvato), salvataggio ed eliminazione. È **uno solo**: lo usano la
 * Dashboard e il Diario (`pages/workouts/Workouts.js`), che corregge i
 * risultati di programma con lo stesso modulo con cui sono stati inseriti.
 *
 * `todayResults` è la mappa chiave → risultato salvato su cui lavorano
 * modifica ed eliminazione; `currentWeekDays`/`currentDay` danno la data a un
 * risultato nuovo (in modifica vale quella del risultato); `onDeleted(key)`
 * avvisa chi ospita il modulo.
 */
export function useResultEntry({
	todayResults,
	setTodayResults,
	resultsPath,
	sandboxToast,
	currentWeekDays,
	currentDay,
	exerciseLibrary,
	onDeleted,
}) {
	const { t } = useLanguage();
	const [resultForms, setResultForms] = useState({});
	const [savingKey, setSavingKey] = useState(null);
	const [editingKey, setEditingKey] = useState(null);
	const [resultDialogState, setResultDialogState] = useState(null);

	const updateResultForm = useCallback((key, field, value) => {
		const metricFields = new Set([
			"weight_kg",
			"reps",
			"score",
			"time_min",
			"time_sec",
			"time_or_reps_input",
			"emom_score_text",
			"free_score",
			"running_interval_scores",
			"avg_heart_rate",
			"max_heart_rate",
			"result_set_rows",
			"superset_details",
			"schema_values_raw",
		]);
		setResultForms((prev) => {
			const current = prev[key] || {};
			const next = { ...current, [field]: value };
			if (metricFields.has(field) && next.__missing_fields) {
				delete next.__missing_fields;
			}
			return {
				...prev,
				[key]: next,
			};
		});
	}, []);

	const startEdit = useCallback(
		(key, savedResult, exercise, programId, dayId) => {
			setEditingKey(key);
			// Pre-fill form with saved data
			const formData = {};
			const isStrengthEmom =
				exercise?.exercise_type === "strength" &&
				exercise?.methodology === "emom";
			const isConditioningEmom =
				exercise?.exercise_type === "conditioning" &&
				exercise?.conditioning_type === "emom";
			const conditioningType = String(
				exercise?.conditioning_type ||
					exercise?.conditioning_config?.conditioning_type ||
					"",
			)
				.trim()
				.toLowerCase();
			const prefillScoreType = String(exercise?.score_type || "")
				.trim()
				.toLowerCase();
			const isPrefilledTimeOrReps =
				conditioningType === "time_or_reps" ||
				(conditioningType === "for_time" && prefillScoreType !== "for_time") ||
				((conditioningType === "free" ||
					conditioningType === "custom" ||
					!conditioningType) &&
					prefillScoreType === "time_or_reps");
			const isConditioningFreeCustom =
				exercise?.exercise_type === "conditioning" &&
				(!conditioningType ||
					conditioningType === "free" ||
					conditioningType === "custom" ||
					conditioningType === "hiit") &&
				!isPrefilledTimeOrReps;
			const isEmom = isStrengthEmom || isConditioningEmom;
			if (savedResult.weight_kg)
				formData.weight_kg = String(savedResult.weight_kg);
			if (savedResult.reps) formData.reps = String(savedResult.reps);
			if (isEmom && savedResult.reps_text) {
				formData.emom_score_text = String(savedResult.reps_text);
			} else if (savedResult.reps_text)
				formData.reps_text = String(savedResult.reps_text);
			if (savedResult.rpe) formData.rpe = String(savedResult.rpe);
			if (savedResult.score) formData.score = String(savedResult.score);
			if (savedResult.avg_heart_rate)
				formData.avg_heart_rate = String(savedResult.avg_heart_rate);
			if (savedResult.max_heart_rate)
				formData.max_heart_rate = String(savedResult.max_heart_rate);
			if (
				savedResult.interval_scores &&
				savedResult.interval_scores.length > 0
			) {
				formData.running_interval_scores = savedResult.interval_scores.map(
					(x) => String(x || ""),
				);
			}
			// Tiro: il risultato vive in `shooting`, e senza questo ramo la
			// modifica si apriva vuota — sembrava che i dati fossero andati persi.
			// I numeri tornano stringhe perché è ciò che gli input si aspettano.
			if (savedResult.shooting) {
				const saved = savedResult.shooting;
				formData.shooting = {
					...saved,
					// `strings` è il nome storico: i risultati salvati prima della
					// rinomina lo usano ancora. Si ricaricano come `series`,
					// che è il campo su cui il form lavora oggi.
					series: (saved.series || saved.strings || []).map((serie) => ({
						...serie,
						time_seconds:
							serie.time_seconds === null || serie.time_seconds === undefined
								? ""
								: String(serie.time_seconds),
					})),
					strings: undefined,
					times: (saved.times || []).map((time) => String(time)),
				};
			}
			if (savedResult.time_seconds) {
				formData.time_min = String(Math.floor(savedResult.time_seconds / 60));
				formData.time_sec = String(savedResult.time_seconds % 60);
				if (isPrefilledTimeOrReps) {
					const mins = Math.floor(savedResult.time_seconds / 60);
					const secs = savedResult.time_seconds % 60;
					formData.time_or_reps_input = `${mins}:${String(secs).padStart(2, "0")}`;
				}
			}
			if (
				isPrefilledTimeOrReps &&
				savedResult.reps &&
				!formData.time_or_reps_input
			) {
				formData.time_or_reps_input = String(savedResult.reps);
			}
			if (savedResult.notes) {
				if (isEmom) {
					const notesStr = String(savedResult.notes);
					const lines = notesStr.split("\n");
					const scoreLine = lines.find((line) =>
						line.toLowerCase().startsWith("score emom:"),
					);
					if (scoreLine && !formData.emom_score_text) {
						formData.emom_score_text = scoreLine.replace(/^[^:]*:\s*/, "");
					}
					formData.notes = lines
						.filter((line) => !line.toLowerCase().startsWith("score emom:"))
						.join("\n")
						.trim();
				} else {
					formData.notes = savedResult.notes;
				}
			}
			if (isConditioningFreeCustom && !formData.free_score) {
				formData.free_score = String(
					savedResult.reps_text || savedResult.notes || "",
				);
			}
			// Prefill scaling (conditioning-only, but safe for all types)
			if (savedResult.scaling) {
				formData.scaling = String(savedResult.scaling);
			}
			// Pre-fill superset details
			if (
				savedResult.superset_details &&
				savedResult.superset_details.length > 0
			) {
				formData.superset_details = savedResult.superset_details.map((d) => ({
					exercise_id: d.exercise_id || "",
					exercise_name: d.exercise_name || "",
					row_index:
						typeof d.row_index === "number" && d.row_index >= 0
							? d.row_index
							: 0,
					exercise_slot:
						typeof d.exercise_slot === "number" && d.exercise_slot >= 0
							? d.exercise_slot
							: null,
					weight_kg: d.weight_kg ? String(d.weight_kg) : "",
					reps: d.reps ? String(d.reps) : "",
					rpe: d.rpe ? String(d.rpe) : "",
					notes: d.notes || "",
				}));
			}
			if (
				savedResult.result_set_rows &&
				savedResult.result_set_rows.length > 0
			) {
				const normalizedRows = [];
				savedResult.result_set_rows.forEach((row, idx) => {
					const targetIndex =
						typeof row?.row_index === "number" && row.row_index >= 0
							? row.row_index
							: idx;
					normalizedRows[targetIndex] = {
						row_index: targetIndex,
						weight_kg: row?.weight_kg ? String(row.weight_kg) : "",
						reps: row?.reps ? String(row.reps) : "",
						reps_text: row?.reps_text ? String(row.reps_text) : "",
						rpe: row?.rpe ? String(row.rpe) : "",
					};
				});
				formData.result_set_rows = normalizedRows;
			}
			// Prefill schema values
			const schemaFields = asArray(exercise?.result_schema);
			if (schemaFields.length > 0 && savedResult?.schema_values) {
				const raw = {};
				for (const field of schemaFields) {
					const fid = field?.field_id;
					if (!fid) continue;
					const unit = String(field.unit || "reps").toLowerCase();
					const stored = savedResult.schema_values[fid];
					if (stored === undefined || stored === null) continue;
					if (unit === "minutes_seconds") {
						const total = Number(stored);
						if (!Number.isFinite(total) || total < 0) continue;
						raw[`${fid}__min`] = String(Math.floor(total / 60));
						raw[`${fid}__sec`] = String(Math.floor(total % 60));
					} else {
						raw[fid] = formatValueByUnit(stored, unit);
					}
				}
				formData.schema_values_raw = raw;
			}
			setResultForms((prev) => ({ ...prev, [key]: formData }));
			setResultDialogState({
				key,
				exercise,
				programId,
				dayId,
				isEditing: true,
			});
		},
		[],
	);

	const openCreateResultDialog = useCallback(
		(key, exercise, programId, dayId) => {
			setEditingKey(null);
			setResultForms((prev) => {
				const current = prev[key];
				if (!current || !current.__missing_fields) return prev;
				const next = { ...current };
				delete next.__missing_fields;
				return { ...prev, [key]: next };
			});
			setResultDialogState({
				key,
				exercise,
				programId,
				dayId,
				isEditing: false,
			});
		},
		[],
	);

	const cancelEdit = useCallback(() => {
		setEditingKey(null);
		setResultDialogState(null);
	}, []);

	const deleteResult = useCallback(
		async (key) => {
			const existingResult = todayResults[key];
			if (!existingResult?.result_id) return;

			if (!window.confirm("Confermi di voler eliminare questo risultato?")) {
				return;
			}

			setSavingKey(key);
			try {
				await fetchAPI(resultsPath(`/${existingResult.result_id}`), {
					method: "DELETE",
				});
				setTodayResults((prev) => {
					const next = { ...prev };
					delete next[key];
					return next;
				});
				setResultForms((prev) => {
					const next = { ...prev };
					delete next[key];
					return next;
				});
				setEditingKey(null);
				onDeleted?.(key);
				setResultDialogState(null);
				toast.success(
					sandboxToast("coachSandboxResultDeleted", "Risultato eliminato"),
				);
			} catch (error) {
				toast.error("Errore nell'eliminazione");
			} finally {
				setSavingKey(null);
			}
		},
		[todayResults, resultsPath, sandboxToast, onDeleted],
	);

	const submitResult = useCallback(
		async (key, exercise, programId, dayId) => {
			if (exercise?.allow_result_entry === false) {
				toast.error(t("resultEntryBlocked"));
				return;
			}
			const isEdit = editingKey === key;
			const existingResult = todayResults[key];
			const targetProgramId =
				isEdit && existingResult?.program_id
					? existingResult.program_id
					: programId;
			const targetDayId =
				isEdit && existingResult?.day_id ? existingResult.day_id : dayId;
			const form = resultForms[key] || {};
			const isStrengthEmom =
				exercise?.exercise_type === "strength" &&
				exercise?.methodology === "emom";
			const isConditioningEmom =
				exercise?.exercise_type === "conditioning" &&
				exercise?.conditioning_type === "emom";
			const isEmom = isStrengthEmom || isConditioningEmom;
			const isComplex =
				String(exercise?.methodology || "")
					.trim()
					.toLowerCase() === "complex";
			const setRows = asArray(exercise?.set_rows);
			const expandedStrengthPresets = (() => {
				if (setRows.length > 0) {
					const expanded = [];
					setRows.forEach((row) => {
						const setsCount = Number(row?.sets) > 0 ? Number(row.sets) : 1;
						const pyramidReps = splitPyramidReps(row?.reps, setsCount);
						for (let idx = 0; idx < setsCount; idx += 1) {
							expanded.push({
								reps: pyramidReps ? pyramidReps[idx] : row?.reps || "",
							});
						}
					});
					return expanded;
				}
				const setsCount =
					Number(exercise?.sets) > 0 ? Number(exercise.sets) : 0;
				if (setsCount > 0) {
					const pyramidReps = splitPyramidReps(exercise?.reps, setsCount);
					return Array.from({ length: setsCount }, (_, idx) => ({
						reps: pyramidReps ? pyramidReps[idx] : exercise?.reps || "",
					}));
				}
				return [];
			})();
			const supersetExercises = asArray(exercise?.superset_exercises);
			const isSuperset = supersetExercises.length > 0;
			const supersetSetCount = isSuperset
				? getSupersetRoundCount(exercise)
				: 0;
			const useStrengthMultiRowInput =
				exercise?.exercise_type === "strength" &&
				!isSuperset &&
				String(exercise?.methodology || "")
					.trim()
					.toLowerCase() !== "custom" &&
				// Must mirror the render-side condition (based on the EXPANDED
				// presets, not the raw set_rows count) so pyramidal exercises
				// stored as sets=N + reps="4-4-3-3-2-2" (no explicit set_rows
				// array) are saved as multi-row. Otherwise the per-set reps are
				// dropped and top-level reps fall back to parseInt("4-4-...")=4,
				// which is what corrupts the pyramidal leaderboard rep count.
				((isComplex && expandedStrengthPresets.length > 0) ||
					(!isComplex && expandedStrengthPresets.length > 1));
			const timeMin = form.time_min ? parseInt(form.time_min) : 0;
			const timeSec = form.time_sec ? parseInt(form.time_sec) : 0;
			const totalTime = timeMin * 60 + timeSec;
			const scoreVal =
				form.score && !isNaN(parseFloat(form.score))
					? parseFloat(form.score)
					: null;
			const hasRunningRepeatRows =
				exercise?.exercise_type === "running" &&
				asArray(exercise?.interval_rows).length > 0;
			// Cardio interval rows (erg / bike / swim)
			const hasCardioIntervalRows =
				exercise?.exercise_type === "erg" || exercise?.exercise_type === "bike"
					? asArray(exercise?.bike_intervals).length > 0
					: exercise?.exercise_type === "swim"
						? asArray(exercise?.swim_sets).length > 0
						: false;
			const runningIntervalScores =
				exercise?.exercise_type === "running" || hasCardioIntervalRows
					? asArray(form.running_interval_scores).map((value) =>
							String(value || "").trim(),
						)
					: [];
			const hasRunningIntervalScores = runningIntervalScores.some(Boolean);
			const runningIntervalScoresText = hasRunningIntervalScores
				? runningIntervalScores.filter(Boolean).join(" · ")
				: "";
			const emomScoreText = (form.emom_score_text || "").trim();
			const emomNumericPattern = /^-?\d+(?:[.,]\d+)?$/;
			const emomScoreNumeric =
				emomScoreText && emomNumericPattern.test(emomScoreText)
					? Number(emomScoreText.replace(",", "."))
					: null;
			const conditioningType = String(exercise?.conditioning_type || "")
				.trim()
				.toLowerCase();
			const runningType = String(exercise?.running_type || "")
				.trim()
				.toLowerCase();
			const isRunningCustomResult =
				exercise?.exercise_type === "running" && runningType === "custom";
			const isStrengthCustomResult =
				exercise?.exercise_type === "strength" &&
				String(exercise?.methodology || "")
					.trim()
					.toLowerCase() === "custom";
			const isNoteOnlyResultMode =
				isRunningCustomResult || isStrengthCustomResult;
			const submitScoreType = String(exercise?.score_type || "")
				.trim()
				.toLowerCase();
			const isConditioningTimeOrRepsResult =
				exercise?.exercise_type === "conditioning" &&
				(conditioningType === "time_or_reps" ||
					(conditioningType === "for_time" && submitScoreType !== "for_time") ||
					((conditioningType === "free" ||
						conditioningType === "custom" ||
						!conditioningType) &&
						submitScoreType === "time_or_reps"));
			const isConditioningForTimeResult =
				exercise?.exercise_type === "conditioning" &&
				conditioningType === "for_time" &&
				!isConditioningTimeOrRepsResult;
			const isConditioningAmrapResult =
				exercise?.exercise_type === "conditioning" &&
				conditioningType === "amrap";
			const isConditioningFreeCustomResult =
				exercise?.exercise_type === "conditioning" &&
				(!conditioningType ||
					conditioningType === "free" ||
					conditioningType === "custom" ||
					conditioningType === "hiit") &&
				!isConditioningTimeOrRepsResult;
			const baseNotes = (form.notes || "").trim();
			const freeCustomScoreText = isConditioningFreeCustomResult
				? (form.free_score || "").trim()
				: "";
			const freeCustomScoreNumeric =
				freeCustomScoreText && emomNumericPattern.test(freeCustomScoreText)
					? Number(freeCustomScoreText.replace(",", "."))
					: null;
			const mergedNotes = baseNotes;
			const parseOptionalInt = (value) => {
				if (value === null || value === undefined) return null;
				const text = String(value).trim();
				if (!text) return null;
				const parsed = parseInt(text, 10);
				return Number.isFinite(parsed) ? parsed : null;
			};
			const parseOptionalFloat = (value) => {
				if (value === null || value === undefined) return null;
				const text = String(value).trim().replace(/,/g, ".");
				if (!text) return null;
				const parsed = parseFloat(text);
				return Number.isFinite(parsed) ? parsed : null;
			};
			const prescribedSingleReps = (() => {
				if (isTimedStrengthReps(setRows[0]?.reps || exercise?.reps))
					return null;
				const fromSetRow = parseOptionalInt(setRows[0]?.reps);
				if (fromSetRow !== null) return fromSetRow;
				return parseOptionalInt(exercise?.reps);
			})();
			const singleStrengthWeight = parseOptionalFloat(form.weight_kg);
			const singleStrengthReps = parseOptionalInt(form.reps);
			const usesPrescribedSingleReps =
				exercise?.exercise_type === "strength" &&
				!isEmom &&
				!useStrengthMultiRowInput &&
				singleStrengthReps === null &&
				prescribedSingleReps !== null;
			const effectiveSingleStrengthReps =
				exercise?.exercise_type === "strength" &&
				!isEmom &&
				!useStrengthMultiRowInput
					? (singleStrengthReps ?? prescribedSingleReps)
					: singleStrengthReps;
			// Build schema_values from form.schema_values_raw when schema is defined.
			const resultSchemaFields = asArray(exercise?.result_schema);
			const hasResultSchema =
				exercise?.exercise_type === "conditioning" &&
				resultSchemaFields.length > 0;
			const schemaValuesPayload = {};
			if (hasResultSchema) {
				const rawMap = form.schema_values_raw || {};
				for (const field of resultSchemaFields) {
					if (!field || !field.field_id) continue;
					const fid = field.field_id;
					const unit = String(field.unit || "reps").toLowerCase();
					if (unit === "minutes_seconds") {
						const minRaw = rawMap[`${fid}__min`];
						const secRaw = rawMap[`${fid}__sec`];
						const hasAny =
							(minRaw !== undefined && minRaw !== "") ||
							(secRaw !== undefined && secRaw !== "");
						if (!hasAny) continue;
						const mins = Number(minRaw);
						const secs = Number(secRaw);
						const total =
							(Number.isFinite(mins) ? mins : 0) * 60 +
							(Number.isFinite(secs) ? secs : 0);
						schemaValuesPayload[fid] = total;
					} else if (unit === "time_or_reps") {
						const raw = rawMap[fid];
						if (raw === undefined || raw === "" || raw === null) continue;
						schemaValuesPayload[fid] = String(raw).trim();
					} else {
						const raw = rawMap[fid];
						if (raw === undefined || raw === "" || raw === null) continue;
						const parsed = parseValueByUnit(raw, unit);
						if (parsed !== null && parsed !== undefined) {
							schemaValuesPayload[fid] = parsed;
						}
					}
				}
			}
			// Build superset_details from form (set x exercise matrix)
			const rawSupersetDetails = asArray(form.superset_details);
			const supersetDetails = isSuperset
				? Array.from({ length: supersetSetCount }, (_, setIndex) =>
						supersetExercises.map((se, exerciseIndex) => {
							const detailIndex =
								setIndex * supersetExercises.length + exerciseIndex;
							const d = rawSupersetDetails[detailIndex] || {};
							const detailWeight = parseOptionalFloat(d.weight_kg);
							// Come nelle righe del singolo: peso scritto e reps lasciate
							// a quelle prescritte (il modulo le mostra precompilate) =
							// reps prescritte, mai una riga con il peso e senza reps.
							const prescribedDetailReps = asArray(se?.set_rows).find(
								(row) => row && typeof row === "object",
							)?.reps;
							const fallbackDetailReps =
								detailWeight !== null &&
								!isTimedStrengthReps(prescribedDetailReps)
									? parseOptionalInt(prescribedDetailReps)
									: null;
							return {
								exercise_id: d.exercise_id || se?.exercise_id || "",
								exercise_name: d.exercise_name || se?.exercise_name || "",
								exercise_slot: exerciseIndex,
								row_index: setIndex,
								weight_kg: detailWeight,
								reps: parseOptionalInt(d.reps) ?? fallbackDetailReps,
								rpe: d.rpe ? parseInt(d.rpe, 10) : null,
								notes: d.notes || null,
							};
						}),
					).flat()
				: rawSupersetDetails.map((d, idx) => {
						const se = exercise.superset_exercises?.[idx];
						return {
							exercise_id: d.exercise_id || se?.exercise_id || "",
							exercise_name: d.exercise_name || se?.exercise_name || "",
							exercise_slot:
								typeof d.exercise_slot === "number" ? d.exercise_slot : idx,
							row_index: typeof d.row_index === "number" ? d.row_index : 0,
							weight_kg: parseOptionalFloat(d.weight_kg),
							reps: d.reps ? parseInt(d.reps, 10) : null,
							rpe: d.rpe ? parseInt(d.rpe, 10) : null,
							notes: d.notes || null,
						};
					});
			const weightedSupersetDetails = supersetDetails.filter(
				(detail) => detail.weight_kg !== null,
			);
			const bestSupersetDetail =
				weightedSupersetDetails.length > 0
					? [...weightedSupersetDetails].sort(
							(a, b) => (b.weight_kg || 0) - (a.weight_kg || 0),
						)[0]
					: null;
			const rawStrengthRows =
				exercise?.exercise_type === "strength"
					? useStrengthMultiRowInput
						? Array.from(
								{ length: expandedStrengthPresets.length },
								(_, idx) => {
									const row = asArray(form.result_set_rows)[idx] || {};
									const rowWeight = parseOptionalFloat(row?.weight_kg);
									const rowReps = parseOptionalInt(row?.reps);
									const fallbackRowReps =
										rowWeight !== null &&
										!isTimedStrengthReps(expandedStrengthPresets[idx]?.reps)
											? parseOptionalInt(expandedStrengthPresets[idx]?.reps)
											: null;
									return {
										row_index:
											typeof row?.row_index === "number" ? row.row_index : idx,
										weight_kg: rowWeight,
										reps: rowReps ?? fallbackRowReps,
										reps_text: null,
										rpe: parseOptionalInt(row?.rpe),
									};
								},
							)
						: asArray(form.result_set_rows).map((row, idx) => ({
								row_index:
									typeof row?.row_index === "number" ? row.row_index : idx,
								weight_kg: parseOptionalFloat(row?.weight_kg),
								reps: parseOptionalInt(row?.reps),
								reps_text: null,
								rpe: parseOptionalInt(row?.rpe),
							}))
					: [];
			const strengthResultRows = rawStrengthRows.filter(
				(row) => row.weight_kg || row.reps || row.reps_text || row.rpe,
			);
			const weightedStrengthResultRows = strengthResultRows.filter(
				(row) => row.weight_kg !== null,
			);
			const bestStrengthRow =
				weightedStrengthResultRows.length > 0
					? [...weightedStrengthResultRows].sort(
							(a, b) => (b.weight_kg || 0) - (a.weight_kg || 0),
						)[0]
					: null;
			const prescribedDay =
				currentWeekDays.find((day) => day?.day_id === targetDayId) || null;
			// Parse time_or_reps_input: either "mm:ss" (completed) or plain integer reps (timecap)
			let timeOrRepsSeconds = null;
			let timeOrRepsReps = null;
			if (isConditioningTimeOrRepsResult) {
				const rawTOR = String(form.time_or_reps_input || "").trim();
				if (rawTOR.includes(":")) {
					const parts = rawTOR.split(":");
					if (parts.length === 2) {
						const mins = parseInt(parts[0]);
						const secs = parseInt(parts[1]);
						if (
							!isNaN(mins) &&
							!isNaN(secs) &&
							mins >= 0 &&
							secs >= 0 &&
							secs < 60
						) {
							timeOrRepsSeconds = mins * 60 + secs;
						}
					}
				} else if (/^\d+$/.test(rawTOR)) {
					timeOrRepsReps = parseInt(rawTOR);
				}
			}
			const hasSchemaMetricValue =
				hasResultSchema && Object.keys(schemaValuesPayload).length > 0;
			const hasStrengthRowMetricValue = strengthResultRows.some(
				(row) =>
					row.weight_kg !== null ||
					row.reps !== null ||
					String(row.reps_text || "").trim() !== "",
			);
			const hasSupersetMetricValue = supersetDetails.some(
				(detail) => detail.weight_kg !== null || detail.reps !== null,
			);
			const hasPrimaryMetricValue =
				singleStrengthWeight !== null ||
				effectiveSingleStrengthReps !== null ||
				scoreVal !== null ||
				emomScoreText.length > 0 ||
				totalTime > 0 ||
				hasRunningIntervalScores ||
				(isNoteOnlyResultMode && baseNotes.length > 0) ||
				(isConditioningForTimeResult && parseOptionalInt(form.reps) !== null) ||
				(isConditioningTimeOrRepsResult &&
					(timeOrRepsSeconds !== null || timeOrRepsReps !== null)) ||
				(isConditioningFreeCustomResult && freeCustomScoreText.length > 0) ||
				// Il tiro non ha peso, ripetizioni né punteggio digitato: il dato
				// sta dentro `shooting`, e senza questo controllo il salvataggio
				// veniva rifiutato anche con quattro stringhe compilate.
				hasShootingResultContent(form.shooting);
			if (
				!hasPrimaryMetricValue &&
				!hasStrengthRowMetricValue &&
				!hasSupersetMetricValue &&
				!hasSchemaMetricValue
			) {
				const missingFieldKeys = (() => {
					if (hasResultSchema) return ["schema_values_raw"];
					if (exercise?.exercise_type === "strength") {
						if (isSuperset) return ["superset_details"];
						if (useStrengthMultiRowInput) return ["result_set_rows"];
						return ["strength_primary"];
					}
					if (exercise?.exercise_type === "conditioning") {
						if (isConditioningTimeOrRepsResult) return ["time_or_reps_input"];
						if (isConditioningForTimeResult) return ["conditioning_for_time"];
						if (isConditioningEmom) return ["emom_score_text"];
						if (isConditioningFreeCustomResult) return ["free_score"];
						return ["score"];
					}
					if (
						exercise?.exercise_type === "dry_fire" ||
						exercise?.exercise_type === "live_fire"
					) {
						return ["shooting"];
					}
					if (isNoteOnlyResultMode) return ["notes"];
					if (
						hasRunningIntervalScores ||
						hasRunningRepeatRows ||
						hasCardioIntervalRows
					) {
						return ["running_interval_scores"];
					}
					return ["cardio_primary"];
				})();
				setResultForms((prev) => ({
					...prev,
					[key]: {
						...(prev[key] || {}),
						__missing_fields: missingFieldKeys,
					},
				}));
				toast.error(t("manualResultFillAtLeastOne"));
				return;
			}
			setSavingKey(key);
			// `|| ""` e non `|| undefined`: una riga senza esercizio di libreria
			// (conditioning, corsa) non ha un id, e lasciare il campo fuori dal
			// JSON faceva rifiutare il salvataggio dal backend.
			const stableExerciseId =
				resolveStableExerciseId(exercise, exerciseLibrary) ||
				exercise.exercise_id ||
				"";
			// Tiro: si manda il sotto-oggetto grezzo e il backend **ricalcola**.
			// Punti e hit factor che arrivano da qui sono anteprima: quello
			// persistito lo produce il server, così la formula vive in un posto solo.
			const isShootingResult =
				exercise?.exercise_type === "dry_fire" ||
				exercise?.exercise_type === "live_fire";
			const shootingPayload = isShootingResult
				? {
						...(form.shooting || {}),
						mode: exercise.exercise_type,
						// Le schede nascono già pronte dalle serie previste: quelle
						// lasciate vuote non si salvano, o entrerebbero nella
						// media come zeri.
						series: pruneEmptySeries(
							form.shooting?.series || form.shooting?.strings || [],
						),
						strings: undefined,
						// Il sistema di punteggio è quello del drill; se il drill
						// non ce l'ha, vale quello scelto dal tiratore nel form.
						scoring_system_id:
							exercise?.shooting?.scoring_system_id ||
							form.shooting?.scoring_system_id ||
							null,
						par_time_seconds: exercise?.shooting?.par_time_seconds ?? null,
						firearm_type: exercise?.shooting?.firearm_type || null,
						// Come si compongono le serie lo decide la **prescrizione**,
						// e si congela sul risultato: se l'esercizio viene
						// riconfigurato, lo storico resta leggibile com'era.
						series_scoring:
							exercise?.shooting?.series_scoring || "individual",
					}
				: null;

			const payload = {
				program_id: targetProgramId,
				day_id: targetDayId,
				exercise_id: stableExerciseId,
				exercise_name: buildExerciseKeyName(exercise),
				shared_exercise_id: exercise.shared_exercise_id || null,
				shooting: shootingPayload,
				weight_kg:
					bestStrengthRow?.weight_kg ||
					bestSupersetDetail?.weight_kg ||
					singleStrengthWeight,
				reps:
					exercise?.exercise_type === "strength" && !isEmom
						? (bestStrengthRow?.reps ??
							bestSupersetDetail?.reps ??
							effectiveSingleStrengthReps)
						: isConditioningForTimeResult
							? form.reps
								? parseInt(form.reps)
								: null
							: isConditioningTimeOrRepsResult
								? timeOrRepsReps
								: null,
				// Le ripetizioni prescritte completano un risultato, non lo sono.
				//
				// Servono alla stima del massimale: chi scrive «100 kg» su una
				// serie prescritta da 5 ha fatto 100x5, e pretendere che ribatta
				// il 5 sarebbe un attrito inutile. Ma una volta salvate sono
				// indistinguibili da ripetizioni davvero eseguite, e il profilo
				// prestativo le contava come un massimo raggiunto: una serie
				// prescritta da 10 diventava un record da 10, senza che nessuno
				// avesse mai fatto 10.
				//
				// Salvare continua a funzionare — segnare un esercizio come fatto
				// non deve costare un numero inventato — ma il dato si porta
				// dietro da dove viene, e chi misura una prestazione lo scarta.
				reps_inferred: usesPrescribedSingleReps || undefined,
				reps_text: isEmom
					? emomScoreText || null
					: isConditioningAmrapResult
						? null
						: isConditioningFreeCustomResult
							? freeCustomScoreText || null
							: runningIntervalScoresText || null,
				rpe: isSuperset
					? null
					: (bestStrengthRow?.rpe ?? parseOptionalInt(form.rpe)),
				time_seconds: isConditioningTimeOrRepsResult
					? timeOrRepsSeconds
					: // Una corsa a ripetute salva i parziali E il totale: prima
						// `hasRunningRepeatRows` azzerava qui tempo e distanza dell'uscita.
						  isConditioningFreeCustomResult ||
						  !(totalTime > 0)
						? null
						: totalTime,
				score:
					isConditioningForTimeResult ||
					isConditioningTimeOrRepsResult ||
					isConditioningFreeCustomResult
						? isConditioningEmom
							? emomScoreNumeric
							: freeCustomScoreNumeric
						: isEmom
							? null
							: scoreVal,
				interval_scores: hasRunningIntervalScores ? runningIntervalScores : [],
				avg_heart_rate:
					exercise?.exercise_type === "running"
						? parseOptionalInt(form.avg_heart_rate)
						: null,
				max_heart_rate:
					exercise?.exercise_type === "running"
						? parseOptionalInt(form.max_heart_rate)
						: null,
				notes: mergedNotes,
				date:
					(isEdit && existingResult?.date) ||
					prescribedDay?.date ||
					currentDay?.date ||
					getToday(),
				is_public: true,
				exercise_type: exercise.exercise_type || "strength",
				conditioning_type: conditioningType || null,
				score_type: exercise.score_type || null,
				conditioning_config:
					exercise.exercise_type === "conditioning"
						? {
								conditioning_type: conditioningType || null,
								rounds: exercise.rounds || null,
								time_cap_seconds: exercise.time_cap_seconds || null,
								interval_seconds: exercise.interval_seconds || null,
								emom_rounds:
									conditioningType === "emom"
										? getDisplayEmomRounds(exercise)
										: [],
								conditioning_exercises: (
									exercise.conditioning_exercises || []
								).map((ce) => ({
									exercise_name: ce.exercise_name || ce.name || "",
									reps: ce.reps || null,
									notes: ce.notes || null,
								})),
							}
						: null,
				library_conditioning_id: exercise.library_conditioning_id || null,
				superset_details: supersetDetails.length > 0 ? supersetDetails : [],
				result_set_rows:
					strengthResultRows.length > 0 ? strengthResultRows : [],
				schema_values: hasResultSchema ? schemaValuesPayload : {},
				scaling:
					exercise?.exercise_type === "conditioning" &&
					typeof form.scaling === "string" &&
					form.scaling.trim() &&
					form.scaling.trim().toLowerCase() !== "rx"
						? form.scaling.trim()
						: null,
			};
			try {
				if (isEdit && existingResult?.result_id) {
					// PUT to update existing result
					const updated = await fetchAPI(
						resultsPath(`/${existingResult.result_id}`),
						{
							method: "PUT",
							body: JSON.stringify(payload),
						},
					);
					const nextUpdatedResult =
						updated && typeof updated === "object"
							? updated
							: {
									...existingResult,
									...payload,
									result_id: existingResult.result_id,
								};
					toast.success(
						sandboxToast("coachSandboxResultUpdated", "Risultato aggiornato"),
					);
					setTodayResults((prev) => ({
						...prev,
						[key]: { ...nextUpdatedResult },
					}));
					setEditingKey(null);
					setResultDialogState(null);
				} else {
					// POST new result
					const created = await fetchAPI(resultsPath(), {
						method: "POST",
						body: JSON.stringify(payload),
					});
					const createdResultId =
						created && typeof created === "object" && created.result_id
							? created.result_id
							: null;
					toast.success(
						sandboxToast("coachSandboxResultSaved", "Risultato salvato"),
					);
					setTodayResults((prev) => ({
						...prev,
						[key]: {
							...(created && typeof created === "object" ? created : payload),
							result_id: createdResultId,
						},
					}));
					setResultDialogState(null);
				}
			} catch (error) {
				toast.error("Errore nel salvataggio");
			} finally {
				setSavingKey(null);
			}
		},
		[
			resultForms,
			editingKey,
			todayResults,
			t,
			currentWeekDays,
			currentDay,
			exerciseLibrary,
			resultsPath,
			sandboxToast,
		],
	);

	return {
		resultForms,
		savingKey,
		editingKey,
		resultDialogState,
		updateResultForm,
		startEdit,
		openCreateResultDialog,
		cancelEdit,
		deleteResult,
		submitResult,
	};
}

/** Il modale del flusso risultato: `entry` è il valore di `useResultEntry`. */
export function ResultEntryDialog({ entry }) {
	const { t, language } = useLanguage();
	const {
		resultDialogState,
		resultForms,
		updateResultForm,
		submitResult,
		deleteResult,
		savingKey,
		cancelEdit,
	} = entry;
	return (
	<Dialog
		open={!!resultDialogState}
		onOpenChange={(open) => {
			if (!open) cancelEdit();
		}}
	>
		<DialogContent className="w-full max-w-none h-[88dvh] max-h-[88dvh] bg-card border-border rounded-t-sm rounded-b-none p-0 gap-0 overflow-hidden top-auto bottom-0 left-0 right-0 translate-x-0 translate-y-0 flex flex-col sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-sm sm:bottom-auto sm:left-[50%] sm:right-auto sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]">
			<DialogDescription className="hidden" />
			<DialogHeader className="px-4 py-3 border-b border-border sticky top-0 bg-card z-10">
				<DialogTitle className="font-heading text-base truncate">
					{resultDialogState?.exercise
						? formatConditioningDisplayNameForAthlete(
								resultDialogState.exercise,
								language,
							)
						: t("logResultTitle")}
				</DialogTitle>
			</DialogHeader>
			<div className="flex-1 p-4 overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
				{resultDialogState && (
					<>
						<ResultForm
							exKey={resultDialogState.key}
							exercise={resultDialogState.exercise}
							resultForms={resultForms}
							updateResultForm={updateResultForm}
							onSubmit={() =>
								submitResult(
									resultDialogState.key,
									resultDialogState.exercise,
									resultDialogState.programId,
									resultDialogState.dayId,
								)
							}
							onDelete={() => deleteResult(resultDialogState.key)}
							savingKey={savingKey}
							isEditing={resultDialogState.isEditing}
						/>
						<button
							onClick={cancelEdit}
							className="mt-2 w-full text-center text-xs text-muted-foreground hover:text-foreground py-2"
						>
							{t("cancelEditLabel")}
						</button>
					</>
				)}
			</div>
		</DialogContent>
	</Dialog>
	);
}

export default function AthleteDashboard({
	previewState = null,
	containerClassName = "",
}) {
	const { user } = useAuth();
	const { t, language } = useLanguage();
	const location = useLocation();
	const navigate = useNavigate();
	const previewPrograms = useMemo(() => {
		if (
			Array.isArray(previewState?.programs) &&
			previewState.programs.length > 0
		) {
			return previewState.programs
				.filter(Boolean)
				.map((program, index) => {
					const visibleWeeks = Array.isArray(program.weeks)
						? program.weeks.filter(Boolean)
						: [];
					const visibleDays = Array.isArray(program.days)
						? program.days.filter(Boolean)
						: [];
					if (visibleWeeks.length === 0 && visibleDays.length === 0) {
						return null;
					}
					return {
						...program,
						weeks: visibleWeeks,
						program_id: program.program_id || `preview_program_${index}`,
					};
				})
				.filter(Boolean);
		}

		if (!previewState?.program) return [];
		return [
			{
				...previewState.program,
				program_id: previewState.program.program_id || "preview_program_0",
			},
		];
	}, [previewState]);
	const isPreviewMode = previewPrograms.length > 0;
	const previewProgram = useMemo(() => {
		if (!isPreviewMode) return null;
		const selectedProgramId =
			previewState?.selectedProgramId || previewState?.program?.program_id;
		if (selectedProgramId) {
			const selectedProgram = previewPrograms.find(
				(program) => program.program_id === selectedProgramId,
			);
			if (selectedProgram) return selectedProgram;
		}
		return previewPrograms[0] || null;
	}, [isPreviewMode, previewPrograms, previewState]);
	const previewExercises = useMemo(
		() =>
			Array.isArray(previewState?.exercises) ? previewState.exercises : [],
		[previewState],
	);
	const previewWeekIndexRaw = Number(previewState?.weekIndex);
	const previewWeekIndex = Number.isFinite(previewWeekIndexRaw)
		? Math.max(0, Math.floor(previewWeekIndexRaw))
		: 0;
	const previewDayId = previewState?.dayId || null;
	// Sandbox coach: l'anteprima non e' una demo, e' la vista dell'atleta sui
	// SUOI dati. Con un athleteId ogni lettura e scrittura passa dalle rotte
	// /coach/athletes/{id}/... — senza, il coach leggerebbe e scriverebbe sul
	// proprio account (storico vuoto, risultati salvati sulla persona sbagliata).
	const sandboxAthleteId = isPreviewMode
		? String(previewState?.athleteId || "").trim() || null
		: null;
	const sandboxAthleteName = sandboxAthleteId
		? previewState?.athleteName || null
		: null;
	const sandboxBasePath = sandboxAthleteId
		? `/coach/athletes/${encodeURIComponent(sandboxAthleteId)}`
		: null;
	// Vetrina: la vista di un programma, non di una persona (blocco «Oggi» della
	// dashboard coach). Niente da leggere su un atleta, e niente da leggere sul
	// coach: massimali e storico sarebbero i suoi, spacciati per quelli
	// dell'atleta.
	const isShowcasePreview = isPreviewMode && !sandboxAthleteId;

	// Data
	const [programs, setPrograms] = useState([]);
	// `{ count, titles }` of the programs the athlete is assigned to that show
	// no week yet. Fetched only when the active list is empty, so the empty
	// state can say "assigned, not yet published" instead of "nothing assigned".
	const [pendingPrograms, setPendingPrograms] = useState(null);
	const [exerciseLibrary, setExerciseLibrary] = useState({});
	const [todayResults, setTodayResults] = useState({});
	const [loading, setLoading] = useState(true);

	// Navigation
	const [selectedProgramId, setSelectedProgramId] = useState(null);
	const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
	const [selectedDayId, setSelectedDayId] = useState(null);
	const [appliedNavigationTarget, setAppliedNavigationTarget] = useState(false);

	// UI
	const [expandedKey, setExpandedKey] = useState(null);
	const [mediaExercise, setMediaExercise] = useState(null);
	const [loadSuggestions, setLoadSuggestions] = useState({});
	const [programOrderOpen, setProgramOrderOpen] = useState(false);
	const [programDisplayOrder, setProgramDisplayOrder] = useState([]);
	const [openedSupportThreads, setOpenedSupportThreads] = useState({});
	const daySelectorRef = useRef(null);
	// Whether the selected day's title is clipped inside its tab (measured, see
	// below): only then DayView repeats it in full.
	const [dayTitleClipped, setDayTitleClipped] = useState(false);
	const persistedProgramOrderRef = useRef("");
	// Skips redundant /load-suggestions calls when the exercise-id set is unchanged
	// (e.g. the today-first partial→full swap, or days sharing the same exercises).
	const lastLoadSuggestionsSignatureRef = useRef(null);
	const programOrderStorageKey = useMemo(
		() =>
			`athlete_dashboard_program_order_${String(user?.user_id || "anonymous")}`,
		[user?.user_id],
	);

	// Exercise library rarely changes; cache it per user to skip the (M0-heavy)
	// /exercises fetch on every dashboard load. Stale for at most EXERCISE_CACHE_TTL_MS.
	const exerciseCacheKey = useMemo(
		() => `athlete_dashboard_exercises_${String(user?.user_id || "anonymous")}`,
		[user?.user_id],
	);

	const navigationTarget = useMemo(() => {
		if (isPreviewMode) {
			return { programId: null, dayId: null, date: null, weekIndex: null };
		}
		const params = new URLSearchParams(location.search || "");
		const programId = params.get("program_id") || null;
		const dayId = params.get("day_id") || null;
		const date = params.get("date") || null;
		const weekIndexRaw = params.get("week_index");
		const weekNumberRaw = params.get("week_number");
		const parsedWeekIndex = Number(weekIndexRaw);
		const parsedWeekNumber = Number(weekNumberRaw);
		let weekIndex = null;
		if (Number.isFinite(parsedWeekIndex) && parsedWeekIndex >= 0) {
			weekIndex = Math.floor(parsedWeekIndex);
		} else if (Number.isFinite(parsedWeekNumber) && parsedWeekNumber > 0) {
			weekIndex = Math.floor(parsedWeekNumber - 1);
		}
		return { programId, dayId, date, weekIndex };
	}, [isPreviewMode, location.search]);

	const openSupportThread = useCallback(
		(threadId, supportPayload) => {
			if (!threadId || !supportPayload?.coach_id) return;
			const alreadyOpened = Boolean(openedSupportThreads[threadId]);
			if (!alreadyOpened) {
				setOpenedSupportThreads((prev) => ({
					...prev,
					[threadId]: true,
				}));
				navigate("/messages", { state: { support: supportPayload } });
				return;
			}
			navigate("/messages", { state: { openThreadId: threadId } });
		},
		[navigate, openedSupportThreads],
	);

	const buildExerciseLibraryMap = useCallback((exerciseList) => {
		const map = {};
		(exerciseList || []).forEach((exerciseItem) => {
			const libId = exerciseItem.exercise_id || exerciseItem._id;
			if (libId) {
				map[libId] = exerciseItem;
			}
			const sourceId = exerciseItem.source_exercise_id;
			if (sourceId && !map[sourceId]) {
				map[sourceId] = exerciseItem;
			}
			const nameKey = buildNormalizedNameKey(exerciseItem.name);
			if (nameKey) {
				map[nameKey] = exerciseItem;
			}
		});
		return map;
	}, []);

	/* ── Load data ── */
	useEffect(() => {
		if (isPreviewMode) {
			const nextPrograms = previewPrograms;
			setPrograms(nextPrograms);
			setExerciseLibrary(buildExerciseLibraryMap(previewExercises));
			if (!sandboxAthleteId) {
				// In sandbox i risultati veri arrivano da `reloadResults`: azzerarli
				// qui li farebbe sparire a ogni cambio di programma.
				setTodayResults({});
			}
			setAppliedNavigationTarget(true);

			if (previewProgram?.program_id) {
				setSelectedProgramId(previewProgram.program_id);
				const weekCount = Array.isArray(previewProgram.weeks)
					? previewProgram.weeks.length
					: 0;
				const maxWeekIndex = Math.max(weekCount - 1, 0);
				setSelectedWeekIndex(Math.min(previewWeekIndex, maxWeekIndex));
				setSelectedDayId(previewDayId || null);
			} else {
				setSelectedProgramId(null);
				setSelectedWeekIndex(0);
				setSelectedDayId(null);
			}

			setLoading(false);
			return;
		}
		loadDashboard();
	}, [
		isPreviewMode,
		previewProgram,
		previewPrograms,
		previewExercises,
		previewWeekIndex,
		previewDayId,
		sandboxAthleteId,
		buildExerciseLibraryMap,
	]);

	useEffect(() => {
		setAppliedNavigationTarget(false);
	}, [
		navigationTarget.programId,
		navigationTarget.dayId,
		navigationTarget.date,
		navigationTarget.weekIndex,
	]);

	const buildResultsMap = (results) => {
		const completed = {};
		(results || []).forEach((r) => {
			if (r.exercise_id) {
				const key = `${r.program_id}_${r.day_id}_${r.exercise_id}`;
				if (!completed[key]) completed[key] = r;
			}
			// Name-based fallback for matching
			const storedName = String(r.exercise_name || "").trim();
			if (storedName) {
				const nameKey = `${r.program_id}_${r.day_id}_name_${storedName}`;
				if (!completed[nameKey]) completed[nameKey] = r;

				// Backward compatibility: older results may store a different name format
				// than the current buildExerciseKeyName(...) convention.
				const compatibilityName = buildExerciseKeyName({
					exercise_type: r.exercise_type,
					conditioning_type: r.conditioning_type,
					conditioning_exercises:
						r.conditioning_config?.conditioning_exercises || [],
					exercise_name: storedName,
				});
				const compatibilityNameTrimmed = String(compatibilityName || "").trim();
				if (compatibilityNameTrimmed) {
					const compatibilityKey = `${r.program_id}_${r.day_id}_name_${compatibilityNameTrimmed}`;
					if (!completed[compatibilityKey]) completed[compatibilityKey] = r;
				}
			}
		});
		return completed;
	};

	const buildSharedExerciseResultMap = (results) => {
		const latestByKey = {};
		const getComparableTime = (result) => {
			const candidates = [result?.date, result?.created_at, result?.updated_at];
			for (const candidate of candidates) {
				if (!candidate) continue;
				const parsed = Date.parse(candidate);
				if (!Number.isNaN(parsed)) return parsed;
			}
			return 0;
		};
		const setIfNewer = (key, result) => {
			if (!key) return;
			const previous = latestByKey[key];
			if (
				!previous ||
				getComparableTime(result) >= getComparableTime(previous)
			) {
				latestByKey[key] = result;
			}
		};

		(results || []).forEach((r) => {
			const sharedExerciseId = String(r?.shared_exercise_id || "").trim();
			if (!sharedExerciseId) return;
			const programId = String(r?.program_id || "").trim();
			const dayId = String(r?.day_id || "").trim();
			const resultDate = String(r?.date || "").trim();
			if (programId && dayId) {
				setIfNewer(`shared_${programId}_${dayId}_${sharedExerciseId}`, r);
			}
			if (resultDate) {
				setIfNewer(`shared_global_${sharedExerciseId}_${resultDate}`, r);
			}
			// Legacy fallback (older entries missing date) kept to avoid regressions.
			setIfNewer(`shared_global_${sharedExerciseId}`, r);
		});

		return latestByKey;
	};

	// Build a minimal, render-compatible program list from the lightweight
	// /workouts/today payload so we can paint today's workout before the heavier
	// /workouts/active-programs response arrives. The synthesized "today" week
	// reuses the real day objects (same day_id), so the selected day carries over
	// seamlessly once the full data replaces this partial view.
	const buildProgramsFromTodayWorkouts = (todayWorkouts) => {
		const byProgram = new Map();
		(todayWorkouts || []).forEach((entry) => {
			const programId = entry?.program_id;
			const day = entry?.day;
			if (!programId || !day) return;
			if (!byProgram.has(programId)) {
				byProgram.set(programId, {
					program_id: programId,
					title: entry.program_title || "",
					is_dated: true,
					coach_name: entry.coach_name || null,
					type: "individual",
					weeks: [
						{
							week_id: `today_${programId}`,
							week_number: 1,
							label: "",
							days: [],
						},
					],
					days: [],
				});
			}
			byProgram.get(programId).weeks[0].days.push(day);
		});
		return Array.from(byProgram.values());
	};

	// Auto-select startup program: honor explicit URL target, otherwise use saved
	// order. When keepCurrentSelection is set (full data replacing the fast paint)
	// the already-selected program is preserved if it still exists.
	const applyStartupProgramSelection = (
		progList,
		profile,
		keepCurrentSelection,
	) => {
		if (!progList || progList.length === 0) return;
		const availableIds = progList
			.map((program) => program?.program_id)
			.filter(Boolean);
		const availableSet = new Set(availableIds);

		const remoteOrder = Array.isArray(profile?.dashboard_program_order)
			? profile.dashboard_program_order.filter(
					(programId) =>
						typeof programId === "string" && availableSet.has(programId),
				)
			: [];

		let fallbackLocalOrder = [];
		if (typeof window !== "undefined") {
			try {
				const rawValue = window.localStorage.getItem(programOrderStorageKey);
				const parsed = rawValue ? JSON.parse(rawValue) : [];
				if (Array.isArray(parsed)) {
					fallbackLocalOrder = parsed.filter(
						(programId) =>
							typeof programId === "string" && availableSet.has(programId),
					);
				}
			} catch {
				fallbackLocalOrder = [];
			}
		}

		const baseOrder = remoteOrder.length > 0 ? remoteOrder : fallbackLocalOrder;
		const missingIds = availableIds.filter(
			(programId) => !baseOrder.includes(programId),
		);
		const resolvedOrder = [...baseOrder, ...missingIds];
		setProgramDisplayOrder(resolvedOrder);
		persistedProgramOrderRef.current = JSON.stringify(resolvedOrder);

		let startupProgramId = null;
		if (
			navigationTarget.programId &&
			availableSet.has(navigationTarget.programId)
		) {
			startupProgramId = navigationTarget.programId;
		} else if (resolvedOrder.length > 0) {
			startupProgramId = resolvedOrder[0];
		}
		const fallbackSelection = startupProgramId || availableIds[0];

		setSelectedProgramId((prev) => {
			if (keepCurrentSelection && prev && availableSet.has(prev)) return prev;
			return fallbackSelection;
		});
	};

	const loadDashboard = async () => {
		try {
			const cachedExercises = readCachedExercises(exerciseCacheKey);
			// Fire everything in parallel; the lightweight "today" payload lets us
			// paint the current day before the heavier active-programs response lands.
			const fullProgramsPromise = fetchAPI("/workouts/active-programs");
			const todayPromise = fetchAPI("/workouts/today").catch(() => null);
			const exercisesPromise = cachedExercises
				? Promise.resolve(cachedExercises)
				: fetchAPI("/exercises");
			const profilePromise = fetchAPI("/profile").catch(() => null);

			const [exercises, profile] = await Promise.all([
				exercisesPromise,
				profilePromise,
			]);

			if (!cachedExercises && Array.isArray(exercises)) {
				writeCachedExercises(exerciseCacheKey, exercises);
			}
			setExerciseLibrary(buildExerciseLibraryMap(exercises));

			// FAST FIRST PAINT — show today's workout as soon as it is available,
			// ahead of the full visible-week payload. Falls back silently to the
			// full load below when today is empty (e.g. rest day) or fails.
			let fastPaintDone = false;
			try {
				const todayWorkouts = await todayPromise;
				if (Array.isArray(todayWorkouts) && todayWorkouts.length > 0) {
					const partialPrograms = buildProgramsFromTodayWorkouts(todayWorkouts);
					if (partialPrograms.length > 0) {
						setPrograms(partialPrograms);
						applyStartupProgramSelection(partialPrograms, profile, false);
						setLoading(false);
						fastPaintDone = true;
					}
				}
			} catch {
				// Ignore: the full load below is the source of truth.
			}

			// FULL DATA — replace the partial today-only view with the complete
			// visible-week structure (all days, secondary programs, week nav).
			const progs = await fullProgramsPromise;
			const progList = progs || [];
			setPrograms(progList);
			if (progList.length === 0) {
				fetchAPI("/workouts/pending-programs")
					.then((pending) => setPendingPrograms(pending || null))
					.catch(() => setPendingPrograms(null));
			} else {
				setPendingPrograms(null);
			}
			applyStartupProgramSelection(progList, profile, fastPaintDone);
		} catch (error) {
			console.error("Failed to load dashboard:", error);
		} finally {
			setLoading(false);
			// Defer scroll until after React renders the new content
			requestAnimationFrame(() => {
				const main = document.getElementById("main-scroll-area");
				if (main) main.scrollTo(0, 0);
				else window.scrollTo(0, 0);
			});
		}
	};

	const resultsPath = useCallback(
		(suffix = "") =>
			sandboxBasePath
				? `${sandboxBasePath}/results${suffix}`
				: `/results${suffix}`,
		[sandboxBasePath],
	);

	const sandboxToast = useCallback(
		(sandboxKey, fallback) =>
			sandboxAthleteId
				? t(sandboxKey).replace("{name}", sandboxAthleteName || t("athlete"))
				: fallback,
		[sandboxAthleteId, sandboxAthleteName, t],
	);

	// Load results whenever selected program changes
	const reloadResults = useCallback(() => {
		if (isPreviewMode && !sandboxAthleteId) {
			setTodayResults({});
			return Promise.resolve();
		}
		if (!selectedProgramId) return Promise.resolve();
		return Promise.all([
			fetchAPI(resultsPath(`?program_id=${selectedProgramId}`)),
			fetchAPI(resultsPath()),
		])
			.then(([programResults, allResults]) => {
				const scopedResults = buildResultsMap(programResults);
				const sharedResults = buildSharedExerciseResultMap(allResults);
				setTodayResults({
					...sharedResults,
					...scopedResults,
				});
			})
			.catch(() => {
				setTodayResults({});
			});
	}, [isPreviewMode, sandboxAthleteId, selectedProgramId, resultsPath]);

	useEffect(() => {
		reloadResults();
	}, [reloadResults]);

	/* ── Derived data ── */
	const selectedProgram = useMemo(
		() => programs.find((p) => p.program_id === selectedProgramId) || null,
		[programs, selectedProgramId],
	);

	const orderedPrograms = useMemo(() => {
		if (!Array.isArray(programs) || programs.length === 0) return [];
		if (
			!Array.isArray(programDisplayOrder) ||
			programDisplayOrder.length === 0
		) {
			return programs;
		}

		const rankById = {};
		programDisplayOrder.forEach((programId, idx) => {
			rankById[programId] = idx;
		});

		return [...programs]
			.map((program, naturalIndex) => ({
				program,
				naturalIndex,
				rank:
					typeof rankById[program.program_id] === "number"
						? rankById[program.program_id]
						: Number.MAX_SAFE_INTEGER,
			}))
			.sort((a, b) => {
				if (a.rank !== b.rank) return a.rank - b.rank;
				return a.naturalIndex - b.naturalIndex;
			})
			.map((entry) => entry.program);
	}, [programDisplayOrder, programs]);

	const programTitlesById = useMemo(() => {
		const map = {};
		(programs || []).forEach((program) => {
			if (program?.program_id) {
				map[program.program_id] = program.title || "";
			}
		});
		return map;
	}, [programs]);

	useEffect(() => {
		if (isPreviewMode) {
			setProgramDisplayOrder([]);
			return;
		}

		const availableIds = (programs || [])
			.map((program) => program?.program_id)
			.filter(Boolean);
		if (availableIds.length <= 1) {
			setProgramDisplayOrder(availableIds);
			return;
		}

		const availableSet = new Set(availableIds);
		setProgramDisplayOrder((currentOrder) => {
			let baseOrder = Array.isArray(currentOrder)
				? currentOrder.filter(
						(programId) =>
							typeof programId === "string" && availableSet.has(programId),
					)
				: [];

			if (baseOrder.length === 0 && typeof window !== "undefined") {
				try {
					const rawValue = window.localStorage.getItem(programOrderStorageKey);
					const parsed = rawValue ? JSON.parse(rawValue) : [];
					if (Array.isArray(parsed)) {
						baseOrder = parsed.filter(
							(programId) =>
								typeof programId === "string" && availableSet.has(programId),
						);
					}
				} catch {
					baseOrder = [];
				}
			}

			const missingIds = availableIds.filter(
				(programId) => !baseOrder.includes(programId),
			);
			const nextOrder = [...baseOrder, ...missingIds];
			if (JSON.stringify(nextOrder) === JSON.stringify(currentOrder || [])) {
				return currentOrder;
			}
			return nextOrder;
		});
	}, [isPreviewMode, programOrderStorageKey, programs]);

	useEffect(() => {
		if (isPreviewMode) return;
		if (!Array.isArray(programDisplayOrder) || programDisplayOrder.length === 0)
			return;

		const serializedOrder = JSON.stringify(programDisplayOrder);
		if (serializedOrder === persistedProgramOrderRef.current) return;

		persistedProgramOrderRef.current = serializedOrder;
		fetchAPI("/profile", {
			method: "PUT",
			body: JSON.stringify({ dashboard_program_order: programDisplayOrder }),
		}).catch(() => {
			// Keep optimistic UI order even if persistence fails temporarily.
		});

		if (typeof window === "undefined") return;

		try {
			window.localStorage.setItem(
				programOrderStorageKey,
				JSON.stringify(programDisplayOrder),
			);
		} catch {
			// Ignore storage failures in restricted/private environments.
		}
	}, [isPreviewMode, programDisplayOrder, programOrderStorageKey]);

	const moveProgramInOrder = useCallback((programId, direction) => {
		setProgramDisplayOrder((currentOrder) => {
			if (!Array.isArray(currentOrder) || currentOrder.length <= 1) {
				return currentOrder;
			}

			const currentIndex = currentOrder.indexOf(programId);
			if (currentIndex === -1) return currentOrder;

			const targetIndex =
				direction === "up" ? currentIndex - 1 : currentIndex + 1;
			if (targetIndex < 0 || targetIndex >= currentOrder.length) {
				return currentOrder;
			}

			const updated = [...currentOrder];
			const [movedProgram] = updated.splice(currentIndex, 1);
			updated.splice(targetIndex, 0, movedProgram);
			return updated;
		});
	}, []);

	useEffect(() => {
		if (appliedNavigationTarget) return;
		if (!navigationTarget.programId) return;
		if (!programs.length) return;

		const targetExists = programs.some(
			(p) => p.program_id === navigationTarget.programId,
		);
		if (!targetExists) {
			setAppliedNavigationTarget(true);
			return;
		}

		if (selectedProgramId !== navigationTarget.programId) {
			setSelectedProgramId(navigationTarget.programId);
		}
	}, [
		appliedNavigationTarget,
		navigationTarget.programId,
		programs,
		selectedProgramId,
	]);

	// Published weeks from the selected program
	const weeks = useMemo(() => {
		if (!selectedProgram) return [];
		return selectedProgram.weeks || [];
	}, [selectedProgram]);

	const currentWeekRange = useMemo(() => {
		const now = new Date();
		now.setHours(0, 0, 0, 0);
		const monday = new Date(now);
		monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
		const sunday = new Date(monday);
		sunday.setDate(monday.getDate() + 6);

		const toISO = (d) => {
			const yyyy = d.getFullYear();
			const mm = String(d.getMonth() + 1).padStart(2, "0");
			const dd = String(d.getDate()).padStart(2, "0");
			return `${yyyy}-${mm}-${dd}`;
		};

		return {
			start: toISO(monday),
			end: toISO(sunday),
			today: getToday(),
		};
	}, []);

	// Current week
	const currentWeek = useMemo(
		() => weeks[selectedWeekIndex] || null,
		[weeks, selectedWeekIndex],
	);

	useEffect(() => {
		if (appliedNavigationTarget) return;
		if (!weeks.length) return;
		if (
			navigationTarget.programId &&
			navigationTarget.programId !== selectedProgramId
		)
			return;
		if (navigationTarget.weekIndex !== null) {
			const maxWeekIndex = Math.max(weeks.length - 1, 0);
			const targetWeekIndex = Math.min(
				navigationTarget.weekIndex,
				maxWeekIndex,
			);
			if (selectedWeekIndex !== targetWeekIndex) {
				setSelectedWeekIndex(targetWeekIndex);
			}
			setAppliedNavigationTarget(true);
			return;
		}
		if (!navigationTarget.dayId && !navigationTarget.date) {
			setAppliedNavigationTarget(true);
			return;
		}

		const targetWeekIndex = weeks.findIndex((week) =>
			(week?.days || []).some((day) => {
				if (navigationTarget.dayId && day?.day_id === navigationTarget.dayId) {
					return true;
				}
				if (navigationTarget.date && day?.date === navigationTarget.date) {
					return true;
				}
				return false;
			}),
		);

		if (targetWeekIndex < 0) {
			setAppliedNavigationTarget(true);
			return;
		}

		if (selectedWeekIndex !== targetWeekIndex) {
			setSelectedWeekIndex(targetWeekIndex);
		}
	}, [
		appliedNavigationTarget,
		weeks,
		navigationTarget.programId,
		navigationTarget.dayId,
		navigationTarget.date,
		navigationTarget.weekIndex,
		selectedProgramId,
		selectedWeekIndex,
	]);

	useEffect(() => {
		// Don't override week when there is a pending navigation target (e.g. link from diary)
		if (
			!appliedNavigationTarget &&
			(navigationTarget.dayId ||
				navigationTarget.date ||
				navigationTarget.weekIndex !== null)
		)
			return;

		if (!weeks.length) {
			setSelectedWeekIndex(0);
			return;
		}

		if (selectedProgram?.is_dated) {
			const currentWeekIndex = weeks.findIndex((week) =>
				(week.days || []).some(
					(day) =>
						day?.date &&
						day.date >= currentWeekRange.start &&
						day.date <= currentWeekRange.end,
				),
			);
			setSelectedWeekIndex(currentWeekIndex >= 0 ? currentWeekIndex : 0);
		}
	}, [
		selectedProgramId,
		selectedProgram?.is_dated,
		weeks,
		currentWeekRange,
		appliedNavigationTarget,
		navigationTarget.dayId,
		navigationTarget.date,
		navigationTarget.weekIndex,
	]);

	useEffect(() => {
		if (!weeks.length) return;
		if (selectedWeekIndex < weeks.length) return;
		setSelectedWeekIndex(0);
	}, [weeks, selectedWeekIndex]);

	const visibleWeek = useMemo(() => {
		if (!selectedProgram) return null;
		return currentWeek;
	}, [selectedProgram, currentWeek]);

	// Days to show: selected week (dated and undated)
	const currentWeekDays = useMemo(() => {
		if (!selectedProgram) return [];
		return (currentWeek?.days || []).slice().sort((a, b) => {
			if (!a?.date || !b?.date) return 0;
			return String(a.date).localeCompare(String(b.date));
		});
	}, [selectedProgram, currentWeek]);

	// Auto-select day: for dated pick today if present, otherwise first day.
	useEffect(() => {
		const hasPendingNavigationTarget =
			!appliedNavigationTarget &&
			(navigationTarget.dayId || navigationTarget.date) &&
			(!navigationTarget.programId ||
				navigationTarget.programId === selectedProgramId);
		if (hasPendingNavigationTarget) {
			const targetDay = currentWeekDays.find((d) => {
				if (navigationTarget.dayId && d.day_id === navigationTarget.dayId) {
					return true;
				}
				if (navigationTarget.date && d.date === navigationTarget.date) {
					return true;
				}
				return false;
			});

			if (targetDay) {
				if (selectedDayId !== targetDay.day_id) {
					setSelectedDayId(targetDay.day_id);
				}
				setAppliedNavigationTarget(true);
				return;
			}
		}

		if (currentWeekDays.length === 0) {
			setSelectedDayId(null);
			return;
		}

		if (currentWeekDays.find((d) => d.day_id === selectedDayId)) {
			return;
		}

		if (selectedProgram?.is_dated) {
			const todayDay = currentWeekDays.find(
				(d) => d.date === currentWeekRange.today,
			);
			setSelectedDayId((todayDay || currentWeekDays[0]).day_id);
			return;
		}

		setSelectedDayId(currentWeekDays[0].day_id);
	}, [
		currentWeekDays,
		selectedDayId,
		selectedProgram,
		currentWeekRange,
		appliedNavigationTarget,
		navigationTarget.dayId,
		navigationTarget.date,
		navigationTarget.programId,
		selectedProgramId,
	]);

	// Current selected day
	const currentDay = useMemo(() => {
		if (!selectedDayId || !currentWeekDays.length) return null;
		return currentWeekDays.find((d) => d.day_id === selectedDayId) || null;
	}, [selectedDayId, currentWeekDays]);

	useEffect(() => {
		if (!selectedDayId) return;
		const container = daySelectorRef.current;
		if (!container) return;

		const selectedButton = container.querySelector(
			`button[data-day-id="${selectedDayId}"]`,
		);
		if (!selectedButton) return;

		// Si fa scorrere solo la fila dei giorni. `scrollIntoView` fa scorrere
		// anche tutti i contenitori sopra: dentro l'anteprima del coach spostava
		// di lato l'intera finestra quando i titoli delle giornate allungano la
		// fila.
		requestAnimationFrame(() => {
			const rowRect = container.getBoundingClientRect();
			const buttonRect = selectedButton.getBoundingClientRect();
			container.scrollTo({
				left:
					container.scrollLeft +
					(buttonRect.left - rowRect.left) -
					(rowRect.width - buttonRect.width) / 2,
				behavior: "smooth",
			});
		});
	}, [
		selectedDayId,
		selectedWeekIndex,
		selectedProgramId,
		currentWeekDays.length,
	]);

	// The day tab clips long titles (`truncate`). The full title is repeated
	// under the tabs only when the selected one is actually cut: a short title
	// such as "Lunedì" is already readable in the tab and would be a duplicate.
	const currentDayTitle = String(currentDay?.title || "").trim();
	useLayoutEffect(() => {
		const measure = () => {
			const container = daySelectorRef.current;
			const titleSpan = container?.querySelector(
				`button[data-day-id="${selectedDayId}"] [data-day-title]`,
			);
			setDayTitleClipped(
				!!titleSpan && titleSpan.scrollWidth > titleSpan.clientWidth + 1,
			);
		};
		measure();
		window.addEventListener("resize", measure);
		// Web fonts arriving after first paint change the text width.
		if (document.fonts?.ready) {
			document.fonts.ready.then(measure).catch(() => {});
		}
		return () => window.removeEventListener("resize", measure);
	}, [selectedDayId, currentDayTitle, language, currentWeekDays.length]);

	/* ── Fetch load suggestions when day changes ── */
	useEffect(() => {
		if (!currentDay) return;
		if (isShowcasePreview) {
			lastLoadSuggestionsSignatureRef.current = null;
			setLoadSuggestions({});
			return;
		}
		const exercises = asArray(currentDay.exercises);
		const resolveReferenceIdForSuggestion = (exercise, row = null) => {
			const directReferenceId = getLoadReferenceId(exercise, row);
			const referenceName = getLoadReferenceName(exercise, row);
			const referenceNameKey = buildNormalizedNameKey(referenceName);
			const libraryMatch = referenceNameKey
				? exerciseLibrary[referenceNameKey]
				: null;

			if (directReferenceId) {
				const directInLibrary = Boolean(exerciseLibrary[directReferenceId]);
				if (directInLibrary) return directReferenceId;

				// Legacy/program-specific edge case: some programs can carry a local
				// exercise_id that is not a global library id. If name resolves, prefer it.
				if (libraryMatch?.exercise_id || libraryMatch?._id) {
					return libraryMatch.exercise_id || libraryMatch._id;
				}

				return directReferenceId;
			}

			return libraryMatch?.exercise_id || libraryMatch?._id || null;
		};
		// Collect exercise_ids that have a load_percentage prescription
		const idsSet = new Set();
		const exerciseNamesById = {};
		const registerSuggestionExercise = (exerciseId, exerciseName) => {
			if (!exerciseId) return;
			idsSet.add(exerciseId);
			if (
				typeof exerciseName === "string" &&
				exerciseName.trim() &&
				!exerciseNamesById[exerciseId]
			) {
				exerciseNamesById[exerciseId] = exerciseName.trim();
			}
		};
		for (const ex of exercises) {
			const exId = ex.exercise_id;
			const hasPerc =
				ex.load_percentage ||
				asArray(ex.set_rows).some((r) => r?.load_percentage);
			// A complex's own id is never a reference (see getLoadReferenceId).
			if (hasPerc && exId && !isStrengthComplex(ex)) {
				registerSuggestionExercise(exId, ex.exercise_name);
			}
			if (ex?.load_percentage) {
				const referenceId = resolveReferenceIdForSuggestion(ex, null);
				registerSuggestionExercise(
					referenceId,
					getLoadReferenceName(ex, null) || ex.exercise_name,
				);
			}
			for (const row of asArray(ex.set_rows)) {
				if (row.load_percentage) {
					const referenceId = resolveReferenceIdForSuggestion(ex, row);
					registerSuggestionExercise(
						referenceId,
						getLoadReferenceName(ex, row) || ex.exercise_name,
					);
				}
			}
			// Check superset sub-exercises
			const exSuperset = asArray(ex.superset_exercises);
			if (exSuperset.length) {
				for (const se of exSuperset) {
					const hasSupersetPerc = asArray(se.set_rows).some(
						(r) => r?.load_percentage,
					);
					if (hasSupersetPerc) {
						const supersetReferenceId = resolveReferenceIdForSuggestion(
							se,
							null,
						);
						registerSuggestionExercise(
							supersetReferenceId,
							getLoadReferenceName(se, null) || se.exercise_name,
						);
						for (const row of asArray(se.set_rows)) {
							if (row.load_percentage) {
								const referenceId = resolveReferenceIdForSuggestion(se, row);
								registerSuggestionExercise(
									referenceId,
									getLoadReferenceName(se, row) || se.exercise_name,
								);
							}
						}
					}
				}
			}
			// Check EMOM round items
			if (ex.methodology === "emom") {
				for (const round of asArray(ex.emom_rounds)) {
					for (const item of asArray(round?.items)) {
						if (!item?.load) continue;
						const pctValues = extractPercentageNumbers(item.load);
						if (pctValues.length === 0) continue;
						const itemNameKey = buildNormalizedNameKey(item?.exercise_name);
						const itemLibMatch = itemNameKey
							? exerciseLibrary[itemNameKey]
							: null;
						const referenceNameKey = buildNormalizedNameKey(
							item?.load_reference_exercise_name,
						);
						const referenceLibMatch = referenceNameKey
							? exerciseLibrary[referenceNameKey]
							: null;
						const resolvedReferenceId =
							item.load_reference_exercise_id ||
							referenceLibMatch?.exercise_id ||
							referenceLibMatch?._id ||
							item.exercise_id ||
							itemLibMatch?.exercise_id ||
							itemLibMatch?._id ||
							null;
						registerSuggestionExercise(
							resolvedReferenceId,
							item.load_reference_exercise_name || item.exercise_name,
						);
					}
				}
			}
		}
		const ids = [...idsSet].filter(Boolean);
		if (ids.length === 0) {
			lastLoadSuggestionsSignatureRef.current = "";
			setLoadSuggestions({});
			return;
		}
		// Suggestions are keyed by exercise_id (1RM-based), not by day, so an
		// unchanged id-set yields identical results — skip the (M0-heavy) refetch.
		const signature = JSON.stringify([
			sandboxBasePath || "",
			ids
				.slice()
				.sort()
				.map((exerciseId) => [exerciseId, exerciseNamesById[exerciseId] || ""]),
		]);
		if (signature === lastLoadSuggestionsSignatureRef.current) return;
		lastLoadSuggestionsSignatureRef.current = signature;
		fetchAPI(
			sandboxBasePath
				? `${sandboxBasePath}/load-suggestions`
				: "/load-suggestions",
			{
				method: "POST",
				body: JSON.stringify({
					exercise_ids: ids,
					exercise_names: exerciseNamesById,
				}),
			},
		)
			.then((data) => {
				const nextSuggestions =
					data && typeof data === "object" ? { ...data } : {};
				Object.entries(nextSuggestions).forEach(([, suggestion]) => {
					const nameKey = buildNormalizedNameKey(suggestion?.reference_name);
					if (nameKey && !nextSuggestions[nameKey]) {
						nextSuggestions[nameKey] = suggestion;
					}
				});
				setLoadSuggestions(nextSuggestions);
			})
			.catch(() => {
				// Allow a retry on the next day change if this attempt failed.
				lastLoadSuggestionsSignatureRef.current = null;
				setLoadSuggestions({});
			});
	}, [currentDay, exerciseLibrary, sandboxBasePath, isShowcasePreview]);

	/* ── Handlers ── */
	const toggleExpand = useCallback((key) => {
		setExpandedKey((prev) => (prev === key ? null : key));
	}, []);

	const onResultDeleted = useCallback(() => setExpandedKey(null), []);
	const resultEntry = useResultEntry({
		todayResults,
		setTodayResults,
		resultsPath,
		sandboxToast,
		currentWeekDays,
		currentDay,
		exerciseLibrary,
		onDeleted: onResultDeleted,
	});
	const {
		resultForms,
		savingKey,
		editingKey,
		updateResultForm,
		startEdit,
		openCreateResultDialog,
		cancelEdit,
		deleteResult,
		submitResult,
	} = resultEntry;

	/* ── Format today ── */
	const todayFormatted = useMemo(() => {
		const formatted = new Date().toLocaleDateString(getDateLocale(language), {
			weekday: "long",
			day: "numeric",
			month: "long",
		});
		return capitalizeLabel(formatted);
	}, [language]);

	const sandbox = useMemo(
		() =>
			isPreviewMode
				? {
						athleteId: sandboxAthleteId,
						athleteName: sandboxAthleteName,
						basePath: sandboxBasePath,
						// Senza athleteId l'anteprima resta una vetrina: scrivere
						// significherebbe salvare sul diario del coach.
						canWrite: Boolean(sandboxAthleteId),
						onResultsChanged: reloadResults,
					}
				: null,
		[
			isPreviewMode,
			sandboxAthleteId,
			sandboxAthleteName,
			sandboxBasePath,
			reloadResults,
		],
	);

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<Loader2 className="w-6 h-6 animate-spin text-volt" />
			</div>
		);
	}

	/* ═══════════════ RENDER ═══════════════ */
	return (
		<div
			className={cn("max-w-lg mx-auto px-4 pb-28", containerClassName)}
			data-testid="athlete-dashboard"
		>
			{sandboxAthleteId && (
				<div
					className="mt-3 rounded-sm border border-volt/40 bg-volt/10 px-3 py-2"
					data-testid="coach-sandbox-banner"
				>
					<p className="text-[10px] uppercase tracking-wider text-volt font-heading">
						{t("coachSandboxBadge")}
					</p>
					<p className="text-xs text-foreground/80 mt-0.5 leading-snug">
						{t("coachSandboxHint")}
					</p>
				</div>
			)}
			{isShowcasePreview && (
				<div
					className="mt-3 rounded-sm border border-border bg-secondary/40 px-3 py-2"
					data-testid="coach-showcase-banner"
				>
					<p className="text-[10px] uppercase tracking-wider text-muted-foreground font-heading">
						{t("coachShowcaseBadge")}
					</p>
					<p className="text-xs text-foreground/80 mt-0.5 leading-snug">
						{t("coachShowcaseHint")}
					</p>
				</div>
			)}

			{/* Greeting */}
			<div className="pt-5 pb-4">
				<div className="flex items-baseline justify-between">
					<h1 className="font-heading text-2xl">
						{isShowcasePreview ? (
							t("coachShowcaseTitle")
						) : (
							<>
								{t("welcome")},{" "}
								<span className="text-volt">
									{sandboxAthleteId
										? sandboxAthleteName || t("athlete")
										: user?.nickname || user?.name?.split(" ")[0]}
								</span>
							</>
						)}
					</h1>
					<div className="flex items-center gap-1.5">
						<span className="text-xs text-muted-foreground capitalize">
							{todayFormatted}
						</span>
						{orderedPrograms.length > 1 && (
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => setProgramOrderOpen(true)}
								className="h-8 w-8 rounded-sm text-volt hover:bg-volt/10"
								aria-label={t("programOrderLabel")}
							>
								<SlidersHorizontal className="w-3.5 h-3.5" />
							</Button>
						)}
					</div>
				</div>
			</div>

			{/* ── NO PROGRAMS ── */}
			{programs.length === 0 && (
				<div className="flex flex-col items-center justify-center py-16 text-center">
					<div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
						<Calendar className="w-8 h-8 text-muted-foreground" />
					</div>
					{pendingPrograms?.count > 0 ? (
						<>
							<p className="font-heading text-lg mb-1">
								{t("programNotYetPublishedTitle")}
							</p>
							<p className="text-sm text-muted-foreground max-w-sm">
								{pendingPrograms.starts_on
									? `${t("programVisibleFrom")} ${new Date(
											`${pendingPrograms.starts_on}T12:00:00`,
										).toLocaleDateString(language === "en" ? "en-GB" : "it-IT", {
											weekday: "long",
											day: "numeric",
											month: "long",
										})}.`
									: t("programNotYetPublishedBody")}
							</p>
							{(pendingPrograms.titles || []).length > 0 && (
								<p className="text-xs text-muted-foreground mt-2">
									{pendingPrograms.titles.join(" · ")}
								</p>
							)}
						</>
					) : (
						<>
							<p className="font-heading text-lg mb-1">{t("noActiveProgram")}</p>
							<p className="text-sm text-muted-foreground">{t("contactCoach")}</p>
						</>
					)}
				</div>
			)}

			{/* ── PROGRAM HEADER (single program — compact inline) ── */}
			{programs.length === 1 && selectedProgram && (
				<div className="mb-3 flex items-center gap-2">
					<Dumbbell className="w-4 h-4 text-volt flex-shrink-0" />
					<p className="text-xs uppercase tracking-wider text-muted-foreground shrink-0">
						{t("programLabel")}:
					</p>
					<p className="font-heading text-sm truncate text-muted-foreground">
						{selectedProgram.title}
					</p>
				</div>
			)}

			{/* ── PROGRAM SELECTOR (multiple programs — single row pills) ── */}
			{orderedPrograms.length > 1 && (
				<div className="mb-3 flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
					{orderedPrograms.map((prog) => {
						const isActive = selectedProgramId === prog.program_id;
						return (
							<button
								key={prog.program_id}
								onClick={() => {
									setSelectedProgramId(prog.program_id);
									setSelectedWeekIndex(0);
									setSelectedDayId(null);
									setExpandedKey(null);
								}}
								className={cn(
									"shrink-0 px-4 py-2 rounded-sm text-sm font-heading truncate max-w-[200px] min-h-[40px] border",
									isActive
										? "border-volt bg-volt/10 text-volt"
										: "border-border text-muted-foreground hover:text-foreground active:bg-white/5",
								)}
							>
								{prog.title}
							</button>
						);
					})}
				</div>
			)}

			{/* ── SELECTED PROGRAM ── */}
			{selectedProgram && (
				<>
					{/* Week tabs */}
					{weeks.length > 1 && (
						<div className="mb-3 flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
							{weeks.map((week, wi) => (
								<button
									key={week.week_id}
									onClick={() => {
										setSelectedWeekIndex(wi);
										setSelectedDayId(null);
										setExpandedKey(null);
									}}
									className={cn(
										"px-4 py-2 rounded-sm text-xs font-heading uppercase tracking-wider min-h-[44px] border text-center",
										weeks.length <= 5 ? "flex-1" : "shrink-0",
										selectedWeekIndex === wi
											? "border-volt bg-volt/10 text-volt"
											: "border-border text-muted-foreground",
									)}
								>
									{localizeWeekLabel(week.label, t, wi)}
								</button>
							))}
						</div>
					)}

					{/* Day list within week */}
					{visibleWeek?.notes && (
						<div className="mb-4 p-3 rounded-sm bg-secondary/60 border-l-2 border-volt border-y border-r border-border">
							<p className="text-[10px] uppercase tracking-wider text-volt mb-1 font-medium">
								{t("weekNotesLabel")}
							</p>
							<p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
								{visibleWeek.notes}
							</p>
						</div>
					)}

					{currentWeekDays.length > 0 && (
						<div className="mb-4">
							{weeks.length <= 1 && visibleWeek?.label && (
								<p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
									{localizeWeekLabel(visibleWeek.label, t)}
								</p>
							)}
							<div
								ref={daySelectorRef}
								className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide"
							>
								{currentWeekDays.map((day, i) => {
									const isSelected = currentDay?.day_id === day.day_id;
									return (
										<button
											key={day.day_id}
											data-day-id={day.day_id}
											onClick={() => setSelectedDayId(day.day_id)}
											className={cn(
												"px-3 py-2 rounded-sm text-xs font-medium min-h-[44px] min-w-[44px]",
												currentWeekDays.length <= 5 ? "flex-1" : "shrink-0",
												isSelected
													? "bg-volt text-volt-foreground"
													: "bg-secondary text-muted-foreground hover:text-foreground",
											)}
										>
											<span className="flex items-center justify-center gap-1.5 whitespace-nowrap">
												<span className="font-heading text-sm">
													{day.date
														? formatDayLabel(day.date, language)
														: day.day_number || i + 1}
												</span>
												{day.title && (
													<span
														data-day-title
														className="text-[10px] max-w-[140px] truncate"
													>
														{day.title}
													</span>
												)}
											</span>
										</button>
									);
								})}
							</div>
						</div>
					)}

					{/* ═══ SELECTED DAY VIEW ═══ */}
					{currentDay ? (
						<DayView
							day={currentDay}
							showFullTitle={dayTitleClipped}
							programId={selectedProgram.program_id}
							programCoachId={selectedProgram.coach_id || null}
							programCoachName={selectedProgram.coach_name || null}
							programType={selectedProgram.type}
							programTitlesById={programTitlesById}
							exerciseLibrary={exerciseLibrary}
							todayResults={todayResults}
							openedSupportThreads={openedSupportThreads}
							openSupportThread={openSupportThread}
							expandedKey={expandedKey}
							toggleExpand={toggleExpand}
							resultForms={resultForms}
							updateResultForm={updateResultForm}
							submitResult={submitResult}
							deleteResult={deleteResult}
							savingKey={savingKey}
							setMediaExercise={setMediaExercise}
							editingKey={editingKey}
							startEdit={startEdit}
							openCreateResultDialog={openCreateResultDialog}
							cancelEdit={cancelEdit}
							loadSuggestions={loadSuggestions}
							sandbox={sandbox}
						/>
					) : (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
								<Calendar className="w-6 h-6 text-muted-foreground" />
							</div>
							{currentWeekDays.length > 0 ? (
								<>
									<p className="font-heading text-base mb-1">
										{t("selectSession")}
									</p>
									<p className="text-xs text-muted-foreground">
										{t("chooseDay")}
									</p>
								</>
							) : selectedProgram.is_dated ? (
								<>
									<p className="font-heading text-base mb-1">
										{t("noSessionsWeek")}
									</p>
									<p className="text-xs text-muted-foreground">
										{t("noSessionsWeek")}
									</p>
								</>
							) : weeks.length === 0 ? (
								<>
									<p className="font-heading text-base mb-1">
										{t("noWeeksAvailable")}
									</p>
									<p className="text-xs text-muted-foreground">
										{t("weeksNotPublished")}
									</p>
								</>
							) : (
								<>
									<p className="font-heading text-base mb-1">
										{t("emptyWeek")}
									</p>
									<p className="text-xs text-muted-foreground">
										{t("noDaysWeek")}
									</p>
								</>
							)}
						</div>
					)}
				</>
			)}

			{/* ── NO PROGRAM SELECTED (multi-program) — handled by the selector above ── */}

			<Dialog open={programOrderOpen} onOpenChange={setProgramOrderOpen}>
				<DialogContent className="w-full max-w-none max-h-[85dvh] bg-card border-border rounded-t-sm rounded-b-none p-0 gap-0 overflow-hidden top-auto bottom-0 left-0 right-0 translate-x-0 translate-y-0 flex flex-col sm:h-auto sm:max-h-[85vh] sm:max-w-md sm:rounded-sm sm:bottom-auto sm:left-[50%] sm:right-auto sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]">
					<DialogDescription className="hidden" />
					<DialogHeader className="px-4 py-3 border-b border-border shrink-0">
						<DialogTitle className="font-heading flex items-center gap-2">
							<SlidersHorizontal className="w-4 h-4 text-volt" />
							{t("programOrderTitle")}
						</DialogTitle>
						<p className="text-xs text-muted-foreground">
							{t("programOrderDescription")}
						</p>
					</DialogHeader>

					<div className="flex-1 overflow-y-auto overscroll-contain p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] space-y-2">
						{orderedPrograms.map((program, index) => (
							<div
								key={program.program_id}
								className="flex items-center gap-2 p-2.5 rounded-sm border border-border bg-secondary/40"
							>
								<span className="w-6 text-center text-[10px] uppercase tracking-wider text-muted-foreground font-heading">
									{index + 1}
								</span>
								<p className="flex-1 text-sm font-medium truncate">
									{program.title}
								</p>
								<div className="flex items-center gap-1">
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="h-10 w-10 rounded-sm"
										disabled={index === 0}
										onClick={() => moveProgramInOrder(program.program_id, "up")}
										aria-label={t("moveUp")}
									>
										<ArrowUp className="w-4 h-4" />
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="h-10 w-10 rounded-sm"
										disabled={index === orderedPrograms.length - 1}
										onClick={() =>
											moveProgramInOrder(program.program_id, "down")
										}
										aria-label={t("moveDown")}
									>
										<ArrowDown className="w-4 h-4" />
									</Button>
								</div>
							</div>
						))}
					</div>
				</DialogContent>
			</Dialog>

			{/* Result Dialog */}
			<ResultEntryDialog entry={resultEntry} />

			{/* Media Dialog */}
			<Dialog
				open={!!mediaExercise}
				onOpenChange={() => setMediaExercise(null)}
			>
				<DialogContent className="max-w-lg bg-card border-border rounded-sm p-0 gap-0 max-h-[90vh] overflow-y-auto">
					<DialogDescription className="hidden" />
					<DialogHeader className="px-4 py-3 border-b border-border">
						<DialogTitle className="font-heading text-base">
							{mediaExercise?.exercise_name}
						</DialogTitle>
					</DialogHeader>
					<div className="p-4">
						{hasMediaUrl(mediaExercise?.video_url) &&
						getYouTubeEmbedUrl(mediaExercise.video_url) ? (
							<div className="aspect-video rounded-sm overflow-hidden">
								<iframe
									src={getYouTubeEmbedUrl(mediaExercise.video_url)}
									title={mediaExercise.exercise_name}
									className="w-full h-full"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowFullScreen
								/>
							</div>
						) : hasMediaUrl(mediaExercise?.video_url) ? (
							<div className="space-y-2">
								<video
									controls
									playsInline
									className="w-full rounded-sm bg-black max-h-[400px]"
									src={getImageUrl(mediaExercise.video_url)}
								>
									Il tuo browser non supporta il player video.
								</video>
								<a
									href={getImageUrl(mediaExercise.video_url)}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-volt"
								>
									<ExternalLink className="w-3.5 h-3.5" />
									<span>Apri in nuova scheda</span>
								</a>
							</div>
						) : hasMediaUrl(mediaExercise?.image_url) ? (
							<img
								src={getImageUrl(mediaExercise.image_url)}
								alt={mediaExercise.exercise_name}
								className="w-full rounded-sm object-contain max-h-[400px]"
							/>
						) : null}
						{mediaExercise?.description && (
							<p className="text-sm text-muted-foreground mt-4">
								{mediaExercise.description}
							</p>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}

/* ═══════════════════════════════════════════════
   ═══  DAY VIEW COMPONENT  ═══
   ═══════════════════════════════════════════════ */
function DayView({
	day,
	showFullTitle = false,
	programId,
	programCoachId,
	programCoachName,
	programType,
	programTitlesById,
	exerciseLibrary,
	todayResults,
	openedSupportThreads,
	openSupportThread,
	expandedKey,
	toggleExpand,
	resultForms,
	updateResultForm,
	submitResult,
	deleteResult,
	savingKey,
	setMediaExercise,
	editingKey,
	startEdit,
	openCreateResultDialog,
	cancelEdit,
	loadSuggestions,
	sandbox,
}) {
	const { t } = useLanguage();
	const exercises = asArray(day?.exercises);

	// The day tab clips long titles (it is a horizontal scroller), so the
	// parent measures the selected tab and asks for the full title only when
	// it is actually cut. The date is never repeated: the tab already says it.
	const dayTitle = String(day?.title || "").trim();
	const sessions = useMemo(
		() =>
			asArray(day?.sessions)
				.filter((session) => session && session.session_id)
				.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
		[day],
	);
	const hasMultipleSessions = sessions.length > 1;

	const sortExercisesForDisplay = useCallback(
		(items) =>
			[...asArray(items)].sort((a, b) => {
				const aOrder =
					typeof a?.session_order === "number"
						? a.session_order
						: typeof a?.order === "number"
							? a.order
							: 0;
				const bOrder =
					typeof b?.session_order === "number"
						? b.session_order
						: typeof b?.order === "number"
							? b.order
							: 0;
				return aOrder - bOrder;
			}),
		[],
	);

	const sessionSections = useMemo(() => {
		if (!hasMultipleSessions) return [];

		const sessionIds = new Set(sessions.map((session) => session.session_id));

		const mappedSessions = sessions.map((session) => {
			const daySessionExercises = sortExercisesForDisplay(
				exercises.filter(
					(exercise) => exercise.session_id === session.session_id,
				),
			);
			const fallbackSessionExercises =
				daySessionExercises.length === 0
					? sortExercisesForDisplay(session.exercises)
					: [];

			return {
				session,
				exercises:
					daySessionExercises.length > 0
						? daySessionExercises
						: fallbackSessionExercises,
			};
		});

		const unassigned = sortExercisesForDisplay(
			exercises.filter(
				(exercise) =>
					!exercise.session_id || !sessionIds.has(exercise.session_id),
			),
		);

		if (unassigned.length > 0) {
			mappedSessions.push({
				session: {
					session_id: "unassigned",
					label: t("unassignedExercisesLabel"),
					order: sessions.length,
				},
				exercises: unassigned,
			});
		}

		return mappedSessions;
	}, [hasMultipleSessions, sessions, exercises, sortExercisesForDisplay, t]);

	const getExKey = (ex, ei) => {
		if (ex.exercise_id) return `${programId}_${day.day_id}_${ex.exercise_id}`;
		return `${programId}_${day.day_id}_name_${buildExerciseKeyName(ex)}`;
	};

	return (
		<div className="space-y-3">
			{/* Full session title — only when the tab truncates it */}
			{showFullTitle && dayTitle && (
				<p className="pt-0.5 font-heading text-base leading-snug break-words">
					{dayTitle}
				</p>
			)}

			{/* Day notes — shown first so athletes read them before starting */}
			{day.notes && (
				<div className="px-4 py-3 bg-volt/10 border-l-2 border-volt border-y border-r border-volt/20 rounded-sm">
					<p className="text-[10px] uppercase tracking-wider text-volt mb-1 font-medium">
						{t("dayNotesLabel")}
					</p>
					<p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
						{day.notes}
					</p>
				</div>
			)}

			{/* Warmup */}
			{day.warmup && (
				<div className="px-4 py-2 bg-acid/5 border border-acid/20 rounded-sm">
					<p className="text-[10px] uppercase tracking-wider text-acid mb-0.5 font-medium">
						{t("warmup")}
					</p>
					<p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
						{day.warmup}
					</p>
				</div>
			)}

			{/* Exercise list */}
			{hasMultipleSessions ? (
				<div className="space-y-3">
					{sessionSections.map((section, sectionIndex) => (
						<div
							key={section.session.session_id || sectionIndex}
							className="rounded-sm border border-border/60 overflow-hidden"
						>
							<div className="px-4 py-2 bg-secondary/40 border-b border-border/60">
								<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
									{t("sessionLabel")} {sectionIndex + 1}
								</p>
							</div>
							{section.exercises.length > 0 ? (
								<div className="divide-y divide-border/50">
									{section.exercises.map((exercise, ei) => (
										<ExerciseRow
											key={`${section.session.session_id || sectionIndex}_${ei}`}
											exercise={exercise}
											index={`${sectionIndex}_${ei}`}
											programId={programId}
											dayId={day.day_id}
											dayDate={day.date || null}
											programCoachId={programCoachId}
											programCoachName={programCoachName}
											programType={programType}
											programTitlesById={programTitlesById}
											exerciseLibrary={exerciseLibrary}
											todayResults={todayResults}
											openedSupportThreads={openedSupportThreads}
											openSupportThread={openSupportThread}
											expandedKey={expandedKey}
											toggleExpand={toggleExpand}
											resultForms={resultForms}
											updateResultForm={updateResultForm}
											submitResult={submitResult}
											deleteResult={deleteResult}
											savingKey={savingKey}
											setMediaExercise={setMediaExercise}
											editingKey={editingKey}
											startEdit={startEdit}
											openCreateResultDialog={openCreateResultDialog}
											cancelEdit={cancelEdit}
											loadSuggestions={loadSuggestions}
											sandbox={sandbox}
										/>
									))}
								</div>
							) : (
								<p className="px-4 py-3 text-xs text-muted-foreground">
									{t("noExercisesInSession")}
								</p>
							)}
						</div>
					))}
				</div>
			) : (
				<div className="divide-y divide-border/50 border-y border-border/50">
					{exercises.map((exercise, ei) => (
						<ExerciseRow
							key={ei}
							exercise={exercise}
							index={ei}
							programId={programId}
							dayId={day.day_id}
							dayDate={day.date || null}
							programCoachId={programCoachId}
							programCoachName={programCoachName}
							programType={programType}
							programTitlesById={programTitlesById}
							exerciseLibrary={exerciseLibrary}
							todayResults={todayResults}
							openedSupportThreads={openedSupportThreads}
							openSupportThread={openSupportThread}
							expandedKey={expandedKey}
							toggleExpand={toggleExpand}
							resultForms={resultForms}
							updateResultForm={updateResultForm}
							submitResult={submitResult}
							deleteResult={deleteResult}
							savingKey={savingKey}
							setMediaExercise={setMediaExercise}
							editingKey={editingKey}
							startEdit={startEdit}
							openCreateResultDialog={openCreateResultDialog}
							cancelEdit={cancelEdit}
							loadSuggestions={loadSuggestions}
							sandbox={sandbox}
						/>
					))}
				</div>
			)}

			{/* Cooldown */}
			{day.cooldown && (
				<div className="px-4 py-2 bg-blue-500/5 border border-blue-500/20 rounded-sm">
					<p className="text-[10px] uppercase tracking-wider text-blue-400 mb-0.5 font-medium">
						{t("cooldown")}
					</p>
					<p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
						{day.cooldown}
					</p>
				</div>
			)}
		</div>
	);
}

/* ═══════════════════════════════════════════════
   ═══  EXERCISE ROW COMPONENT  ═══
   ═══════════════════════════════════════════════ */
function ExerciseRow({
	exercise,
	index,
	programId,
	dayId,
	dayDate,
	programCoachId,
	programCoachName,
	programType,
	programTitlesById,
	exerciseLibrary,
	todayResults,
	openedSupportThreads,
	openSupportThread,
	expandedKey,
	toggleExpand,
	resultForms,
	updateResultForm,
	submitResult,
	deleteResult,
	savingKey,
	setMediaExercise,
	editingKey,
	startEdit,
	openCreateResultDialog,
	cancelEdit,
	loadSuggestions,
	sandbox,
}) {
	const { user } = useAuth();
	const { t, language } = useLanguage();
	const canAskSupport = user?.role === "athlete";
	const stableExerciseId =
		resolveStableExerciseId(exercise, exerciseLibrary) || exercise.exercise_id;
	const exKey = stableExerciseId
		? `${programId}_${dayId}_${stableExerciseId}`
		: `${programId}_${dayId}_name_${buildExerciseKeyName(exercise)}`;
	const sourceExerciseKey = String(exercise.source_exercise_id || "").trim()
		? `${programId}_${dayId}_${String(exercise.source_exercise_id || "").trim()}`
		: null;
	const legacyRawNameKey = exercise.exercise_id
		? null
		: `${programId}_${dayId}_name_${String(exercise.exercise_name || "").trim()}`;
	const legacyDisplayNameKey = exercise.exercise_id
		? null
		: `${programId}_${dayId}_name_${String(
				formatConditioningDisplayNameForAthlete(exercise, language) || "",
			).trim()}`;
	const sharedExerciseKey = String(exercise.shared_exercise_id || "").trim()
		? `shared_${programId}_${dayId}_${String(exercise.shared_exercise_id || "").trim()}`
		: null;
	const sharedExerciseGlobalDateKey =
		String(exercise.shared_exercise_id || "").trim() &&
		String(dayDate || "").trim()
			? `shared_global_${String(exercise.shared_exercise_id || "").trim()}_${String(dayDate || "").trim()}`
			: null;
	const sharedExerciseGlobalKey = String(
		exercise.shared_exercise_id || "",
	).trim()
		? `shared_global_${String(exercise.shared_exercise_id || "").trim()}`
		: null;
	const resultLookupKeys = [
		exKey,
		sourceExerciseKey,
		legacyRawNameKey,
		legacyDisplayNameKey,
		sharedExerciseKey,
		sharedExerciseGlobalDateKey,
		sharedExerciseGlobalKey,
	].filter((key, idx, arr) => key && arr.indexOf(key) === idx);
	// `sourceExerciseKey` is a clone-era fallback: when the source link is stale
	// two different movements on the same day collapse onto the same key, so the
	// result it finds must still name this movement.
	const isAcceptableResultForKey = (key, result) => {
		if (!result) return false;
		if (key !== sourceExerciseKey) return true;
		const storedName = String(result.exercise_name || "").trim();
		const expectedName = String(buildExerciseKeyName(exercise) || "").trim();
		if (!storedName || !expectedName) return true;
		return storedName.toLowerCase() === expectedName.toLowerCase();
	};
	const matchedResultEntry = resultLookupKeys
		.map((key) => [key, todayResults[key]])
		.find(([key, value]) => isAcceptableResultForKey(key, value));
	const resultStorageKey = matchedResultEntry?.[0] || exKey;
	const savedResult = matchedResultEntry?.[1] || null;
	// Expand key includes index to guarantee uniqueness for toggle
	const expandKey = `${exKey}_${index}`;
	const isExpanded = expandedKey === expandKey;
	const isCompleted = !!savedResult;
	const canLogResult =
		exercise?.allow_result_entry !== false &&
		(sandbox ? sandbox.canWrite : true);
	const leaderboardEnabled = exercise?.leaderboard_enabled !== false;
	const isSharedReadonly =
		exercise?.shared_readonly === true && exercise?.shared_is_origin !== true;
	const sharedOriginProgramLabel =
		exercise?.shared_origin_program_name ||
		programTitlesById?.[exercise?.shared_origin_program_id] ||
		(language === "it" ? "programma sorgente" : "source program");
	const lib = resolveExerciseLibraryEntry(exerciseLibrary, exercise);
	const hasMedia = hasMediaUrl(lib?.video_url) || hasMediaUrl(lib?.image_url);
	const meth = exercise.methodology || "standard";
	const supersetExercises = asArray(exercise.superset_exercises);
	const emomRounds = getDisplayEmomRounds(exercise);
	const conditioningExercises = asArray(exercise.conditioning_exercises);
	const isSuperset = supersetExercises.length > 0;
	const isEmom = meth === "emom" && emomRounds.length > 0;
	const isComplex = meth === "complex";
	const isConditioning = exercise.exercise_type === "conditioning";
	const conditioningType = String(exercise.conditioning_type || "")
		.trim()
		.toLowerCase();
	const isCustomConditioning =
		conditioningType === "free" || conditioningType === "custom";
	const sharedLocalNoteText = String(exercise?.shared_local_note || "").trim();
	const customConditioningPreview = String(
		exercise?.conditioning_free_text ||
			exercise?.custom_workout_text ||
			exercise?.conditioning_config?.conditioning_free_text ||
			exercise?.conditioning_config?.custom_workout_text ||
			"",
	).trim();
	const collapsedCustomConditioningPreview =
		customConditioningPreview.length > 160
			? `${customConditioningPreview.slice(0, 160)}...`
			: customConditioningPreview;
	const hiitWorkSeconds = getHiitWorkSecondsPerRound(exercise);
	const hiitWorkShort = hiitWorkSeconds
		? formatHiitIntervalShort(hiitWorkSeconds)
		: "";
	const hiitRestPerRoundSeconds = getHiitRestSecondsPerRound(exercise);
	const hiitRestShort = formatRestDisplay(
		exercise.hiit_rest_interval || hiitRestPerRoundSeconds,
	);
	const hiitOnLabel = hiitWorkShort || "";
	const hiitOffLabel =
		exercise.hiit_rest_mode === "ratio"
			? exercise.hiit_rest_ratio
				? `${normalizeSmartRatioInput(exercise.hiit_rest_ratio)}`
				: ""
			: hiitRestShort
				? `${hiitRestShort}`
				: "";
	const [leaderboardOpen, setLeaderboardOpen] = useState(false);
	const [leaderboardData, setLeaderboardData] = useState(null);
	const [leaderboardLoading, setLeaderboardLoading] = useState(false);
	const [leaderboardError, setLeaderboardError] = useState("");
	const [leaderboardGenderFilter, setLeaderboardGenderFilter] = useState("all");
	const [leaderboardSchemaFieldId, setLeaderboardSchemaFieldId] =
		useState(null);

	const [historyOpen, setHistoryOpen] = useState(false);
	const [historyExercise, setHistoryExercise] = useState(null);
	const [historyResults, setHistoryResults] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyError, setHistoryError] = useState("");

	const expandedContentRef = useRef(null);
	const headerRef = useRef(null);
	// Viewport top of the header captured at tap time, consumed by the layout
	// effect below to keep the tapped row under the finger.
	const tapAnchorTopRef = useRef(null);
	const expandedContentId = useId();
	const canViewLeaderboard =
		programType === "group" &&
		leaderboardEnabled &&
		user?.results_public !== false;
	const supportExerciseRef = String(
		stableExerciseId || buildExerciseKeyName(exercise),
	)
		.trim()
		.replace(/\s+/g, "_");
	const supportThreadId = `sup:${programId || ""}:${dayId || ""}:${supportExerciseRef || ""}`;
	const supportThreadOpened = Boolean(openedSupportThreads?.[supportThreadId]);
	const supportActionLabel = supportThreadOpened
		? t("msgGoToThread")
		: t("msgAskSupport");
	const showInsertResultAction = canLogResult && !isCompleted;
	const showSupportAction = canAskSupport;
	const showLeaderboardAction = canViewLeaderboard;
	// Per ogni tipo di esercizio, tranne nella vetrina di un programma: lo
	// storico sarebbe quello del coach.
	const showHistoryAction = !(sandbox && !sandbox.athleteId);
	const compactSupportLabel = supportThreadOpened
		? t("actionThreadShort")
		: t("actionSupportShort");
	const actionsCount =
		(showInsertResultAction ? 1 : 0) +
		(showSupportAction ? 1 : 0) +
		(showLeaderboardAction ? 1 : 0) +
		(showHistoryAction ? 1 : 0);
	const actionGridClass =
		actionsCount >= 4
			? "grid-cols-4"
			: actionsCount >= 3
				? "grid-cols-3"
				: actionsCount === 2
					? "grid-cols-2"
					: "grid-cols-1";
	const handleSupportAction = (event) => {
		event.preventDefault();
		event.stopPropagation();
		if (!programCoachId) {
			toast.error(t("msgSendError"));
			return;
		}
		openSupportThread?.(supportThreadId, {
			coach_id: programCoachId,
			coach_name: programCoachName || null,
			program_id: programId,
			day_id: dayId,
			exercise_id: stableExerciseId || null,
			exercise_name: formatConditioningDisplayNameForAthlete(
				exercise,
				language,
			),
			date: dayDate || null,
		});
	};

	const handleOpenHistory = useCallback(async () => {
		if (!exercise) return;

		setHistoryExercise(exercise);
		setHistoryResults([]);
		setHistoryLoading(true);
		setHistoryError("");
		setHistoryOpen(true);

		try {
			// A duplicated row carries a runtime `exercise_id`, so the id candidates
			// alone often miss the tracked lift. Resolving the library entry (by id,
			// or by name when the ids are runtime/stale) recovers the real library id
			// and with it the full profile history instead of the bare result list.
			const libraryExerciseId = String(
				resolveExerciseLibraryEntry(exerciseLibrary, exercise)?.exercise_id ||
					"",
			)
				.trim()
				.toLowerCase();
			const lookupCandidates = Array.from(
				new Set(
					[
						...getExerciseHistoryLookupCandidates(exercise),
						libraryExerciseId,
					].filter(Boolean),
				),
			);
			const loadFullResultsForCandidates = async (candidateIds) => {
				const resultLists = await Promise.all(
					(candidateIds || []).map(async (candidateId) => {
						try {
							const payload = await fetchAPI(
								`${sandbox?.basePath || ""}/results?exercise_id=${encodeURIComponent(
									candidateId,
								)}`,
							);
							return Array.isArray(payload) ? payload : [];
						} catch {
							return [];
						}
					}),
				);
				return resultLists.flat().filter(Boolean);
			};

			let loadedFromProfileHistory = false;

			for (const candidateId of lookupCandidates) {
				try {
					const historyPayload = await fetchAPI(
						sandbox?.basePath
							? `${sandbox.basePath}/performance-profile/history/${encodeURIComponent(
									candidateId,
								)}`
							: `/profile/performance-profile/history/${encodeURIComponent(candidateId)}`,
					);
					// The endpoint resolves by id alone: a stale `source_exercise_id`
					// (a row cloned from another lift and then edited) resolves to a
					// real but WRONG tracked exercise. Reject the payload unless its
					// configured name is this movement, and keep trying.
					if (
						!doesProfileHistoryConfigMatchExercise(historyPayload, exercise)
					) {
						continue;
					}
					const historyItems = extractExerciseHistoryItems(historyPayload);
					const fullWorkoutResults = await loadFullResultsForCandidates([
						candidateId,
						...lookupCandidates,
					]);
					const fullResultById = new Map(
						fullWorkoutResults
							.filter((result) => result?.result_id)
							.map((result) => [result.result_id, result]),
					);
					setHistoryResults(
						historyItems.map((item) => ({
							...item,
							...(item?.result_id && fullResultById.get(item.result_id)
								? fullResultById.get(item.result_id)
								: {}),
						})),
					);
					loadedFromProfileHistory = true;
					break;
				} catch (candidateError) {
					const status =
						candidateError?.status || candidateError?.response?.status;
					if (status && status !== 404) {
						throw candidateError;
					}
				}
			}

			if (!loadedFromProfileHistory) {
				const allResults = await fetchAPI(`${sandbox?.basePath || ""}/results`);
				if (!Array.isArray(allResults)) {
					setHistoryError(
						t("errorLoadingResults") || "Errore nel caricamento dei risultati",
					);
					return;
				}

				const matchingResults = allResults.filter((result) =>
					doesHistoryResultMatchExercise(result, exercise),
				);
				const fullResultById = new Map(
					matchingResults
						.filter((result) => result?.result_id)
						.map((result) => [result.result_id, result]),
				);
				setHistoryResults(
					matchingResults.map((item) => ({
						...item,
						...(item?.result_id && fullResultById.get(item.result_id)
							? fullResultById.get(item.result_id)
							: {}),
					})),
				);
			}
		} catch (error) {
			console.error("Error loading exercise history:", error);
			setHistoryError(
				t("errorLoadingResults") || "Errore nel caricamento dei risultati",
			);
		} finally {
			setHistoryLoading(false);
		}
	}, [exercise, exerciseLibrary, sandbox?.basePath, t]);

	const handleToggle = () => {
		tapAnchorTopRef.current =
			headerRef.current?.getBoundingClientRect().top ?? null;
		toggleExpand(expandKey);
	};

	// Single-open accordion: tapping a row collapses the one open above it (or
	// the row itself), so the whole list would shift under the finger and the
	// next tap would land on a neighbour. iOS Safari has no scroll anchoring,
	// so the tapped header is pinned to its pre-tap position before paint.
	useLayoutEffect(() => {
		const before = tapAnchorTopRef.current;
		if (before == null) return;
		tapAnchorTopRef.current = null;
		const header = headerRef.current;
		if (!header) return;
		const delta = header.getBoundingClientRect().top - before;
		if (Math.abs(delta) < 1) return;
		const main = document.getElementById("main-scroll-area");
		if (main) {
			main.scrollTop += delta;
		} else {
			window.scrollBy(0, delta);
		}
	}, [isExpanded]);

	useEffect(() => {
		if (!isExpanded) return;
		if (typeof window === "undefined") return;
		if (!window.matchMedia("(max-width: 767px)").matches) return;

		const prefersReducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;

		// Scroll only when the opened detail does not fit below the header:
		// a row already high on the screen opens in place, without motion.
		const scrollExpandedIntoView = () => {
			const header = headerRef.current;
			const target = expandedContentRef.current;
			if (!header || !target) return;

			const main = document.getElementById("main-scroll-area");
			const headerRect = header.getBoundingClientRect();
			const targetRect = target.getBoundingClientRect();
			const viewportBottom = main
				? main.getBoundingClientRect().bottom
				: window.innerHeight;
			const viewportTop = main ? main.getBoundingClientRect().top : 0;
			const headerVisible =
				headerRect.top >= viewportTop && headerRect.bottom <= viewportBottom;
			if (headerVisible && targetRect.bottom <= viewportBottom) return;

			const topGap = 20;
			if (main) {
				const mainRect = main.getBoundingClientRect();
				const targetTop =
					main.scrollTop + (headerRect.top - mainRect.top) - topGap;
				main.scrollTo({
					top: Math.max(0, targetTop),
					behavior: prefersReducedMotion ? "auto" : "smooth",
				});
				return;
			}

			header.scrollIntoView({
				behavior: prefersReducedMotion ? "auto" : "smooth",
				block: "start",
			});
		};

		requestAnimationFrame(() => {
			requestAnimationFrame(scrollExpandedIntoView);
		});
	}, [isExpanded]);

	useEffect(() => {
		if (!leaderboardOpen || !canViewLeaderboard) return undefined;

		let isActive = true;

		const loadLeaderboard = async () => {
			try {
				setLeaderboardLoading(true);
				setLeaderboardError("");
				const params = new URLSearchParams();
				if (exercise.exercise_id) {
					params.set("exercise_id", exercise.exercise_id);
				}
				if (exercise.exercise_name) {
					params.set("exercise_name", exercise.exercise_name);
				}
				if (leaderboardGenderFilter !== "all") {
					params.set(
						"gender",
						leaderboardGenderFilter === "female" ? "F" : "M",
					);
				}
				if (leaderboardSchemaFieldId) {
					params.set("schema_field_id", leaderboardSchemaFieldId);
				}
				const data = await fetchAPI(
					`/leaderboards/${programId}/${dayId}/exercise?${params.toString()}`,
				);
				if (isActive) {
					setLeaderboardData(data);
					if (
						data?.result_schema &&
						data.result_schema.length > 0 &&
						!leaderboardSchemaFieldId
					) {
						setLeaderboardSchemaFieldId(
							data.active_schema_field_id || data.result_schema[0].field_id,
						);
					}
				}
			} catch (error) {
				if (isActive) {
					setLeaderboardError(error?.message || t("leaderboardLoadError"));
				}
			} finally {
				if (isActive) {
					setLeaderboardLoading(false);
				}
			}
		};

		loadLeaderboard();

		return () => {
			isActive = false;
		};
	}, [
		leaderboardOpen,
		canViewLeaderboard,
		exercise.exercise_id,
		exercise.exercise_name,
		programId,
		dayId,
		leaderboardSchemaFieldId,
		leaderboardGenderFilter,
		t,
	]);

	return (
		<div className="relative">
			{/* Open-row rail. An absolutely positioned element, not a left
			    border: the list's `divide-y divide-border/50` sets `border-color`
			    on every child but the first and would override it. */}
			{isExpanded && (
				<span
					aria-hidden="true"
					className="pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-volt"
				/>
			)}
			{/* Tap to expand — the whole header is the control; the open row is
			    marked by a volt rail so it stands out from its neighbours. */}
			<button
				ref={headerRef}
				type="button"
				aria-expanded={isExpanded}
				aria-controls={expandedContentId}
				onClick={handleToggle}
				className={cn(
					"w-full text-left px-4 py-3 flex items-center gap-3 min-h-[52px] active:bg-white/5 transition-colors",
					isExpanded && "bg-volt/5",
				)}
			>
				{/* Status */}
				<div className="shrink-0">
					<div className="w-7 h-7 rounded-full border border-border flex items-center justify-center">
						{isSuperset ? (
							<Repeat className="w-3.5 h-3.5 text-volt" />
						) : isEmom ? (
							<Timer className="w-3.5 h-3.5 text-volt" />
						) : (
							getExerciseIcon(exercise.exercise_type)
						)}
					</div>
				</div>

				{/* Exercise info */}
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2 min-w-0">
						{/* Dry o a fuoco si deve capire SENZA aprire: sono due
						    preparazioni diverse, e una richiede di andare al poligono. */}
						{(exercise.exercise_type === "dry_fire" ||
							exercise.exercise_type === "live_fire") && (
							<span
								className={`shrink-0 rounded-sm px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold ${
									exercise.exercise_type === "dry_fire"
										? "bg-sky-400/15 text-sky-400"
										: "bg-red-400/15 text-red-400"
								}`}
							>
								{exercise.exercise_type === "dry_fire" ? "Dry" : "Fuoco"}
							</span>
						)}
						<p className="text-sm font-medium truncate">
							{formatConditioningDisplayNameForAthlete(exercise, language)}
						</p>
						{isCompleted && (
							<span
								title={t("resultAlreadyLogged")}
								className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-500/15"
							>
								<Check className="w-2.5 h-2.5 text-green-400" />
							</span>
						)}
					</div>
					{isSharedReadonly && (
						<div className="mt-1 flex flex-wrap gap-1.5">
							{isSharedReadonly && (
								<span className="inline-flex max-w-[190px] items-center gap-1 rounded-sm border border-amber-500/25 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-300/90 align-top">
									<Share2 className="h-2.5 w-2.5 flex-shrink-0" />
									<span className="truncate">{sharedOriginProgramLabel}</span>
								</span>
							)}
							{sharedLocalNoteText && (
								<span
									title={
										language === "it"
											? "Nota presente nel dettaglio esercizio"
											: "Note available in exercise details"
									}
									className="inline-flex h-4 w-4 items-center justify-center rounded-sm border border-amber-500/25 bg-amber-500/10 text-amber-300/90"
								>
									<FileText className="h-2.5 w-2.5" />
								</span>
							)}
						</div>
					)}
					{/* Show superset partners inline */}
					{isSuperset && !isExpanded && (
						<div className="space-y-0.5">
							{supersetExercises.map((se, i) => (
								<span
									key={i}
									className="block text-[10px] text-muted-foreground"
								>
									{se.exercise_name}
								</span>
							))}
						</div>
					)}
					{isConditioning && !isExpanded && (
						<div className="space-y-0.5">
							{/* Conditioning header: "3 Rounds For Time · TC 20'" or "15' AMRAP" */}
							<span className="text-[10px] text-muted-foreground">
								{exercise.conditioning_type === "for_time" &&
									exercise.score_type === "for_time" && (
										<>
											{getForTimeRoundsDisplay(exercise.rounds) && (
												<span className="font-medium">
													{getForTimeRoundsDisplay(exercise.rounds)}{" "}
												</span>
											)}
											For Time
											{exercise.time_cap_seconds && (
												<span>
													{" "}
													· {t("timeCapLabel")}{" "}
													{Math.floor(exercise.time_cap_seconds / 60)}'
												</span>
											)}
										</>
									)}
								{(exercise.conditioning_type === "time_or_reps" ||
									(exercise.conditioning_type === "for_time" &&
										exercise.score_type !== "for_time")) && (
									<>
										{getForTimeRoundsDisplay(exercise.rounds) && (
											<span className="font-medium">
												{getForTimeRoundsDisplay(exercise.rounds)}{" "}
											</span>
										)}
										Time or Reps
										{exercise.time_cap_seconds && (
											<span>
												{" "}
												· {t("timeCapLabel")}{" "}
												{Math.floor(exercise.time_cap_seconds / 60)}'
											</span>
										)}
									</>
								)}
								{exercise.conditioning_type === "amrap" && (
									<>
										{exercise.time_cap_seconds && (
											<span className="font-medium">
												{Math.floor(exercise.time_cap_seconds / 60)}'{" "}
											</span>
										)}
										AMRAP
									</>
								)}
								{exercise.conditioning_type === "emom" &&
									formatEmomHeadline(exercise, t)}
								{exercise.conditioning_type === "hiit" && (
									<>
										HIIT
										{exercise.hiit_cycles && (
											<span>
												{" "}
												· {exercise.hiit_cycles}{" "}
												{Number(exercise.hiit_cycles) === 1
													? t("roundLabel")
													: t("roundsLabel")}
											</span>
										)}
										{hiitOnLabel && (
											<span>
												{" "}
												· {t("hiitWorkLabel")} {hiitOnLabel}
											</span>
										)}
										{hiitOffLabel && (
											<span>
												{" "}
												·{" "}
												{exercise.hiit_rest_mode === "ratio"
													? t("hiitRatioLabel")
													: t("hiitRecoveryLabel")}{" "}
												{hiitOffLabel}
											</span>
										)}
									</>
								)}
								{(exercise.conditioning_type === "free" ||
									exercise.conditioning_type === "custom") &&
									"WOD"}
							</span>
							{/* Sub-exercises list */}
							{exercise.conditioning_type === "emom" &&
							emomRounds.length > 0 ? (
								<div className="space-y-0.5">
									{emomRounds.map((round, i) => (
										<span
											key={round.round_id || i}
											className="block text-[10px] text-muted-foreground"
										>
											<span className="font-medium text-yellow-400">
												{getEmomRoundLabel(round, i, language)}:
											</span>{" "}
											{asArray(round.items)
												.map((item) => formatEmomItemSummary(item, language))
												.join(" + ")}
										</span>
									))}
								</div>
							) : (
								conditioningExercises.length > 0 && (
									<div
										className={
											exercise.conditioning_type === "amrap" ||
											exercise.conditioning_type === "for_time" ||
											exercise.conditioning_type === "time_or_reps" ||
											exercise.conditioning_type === "hiit"
												? "space-y-0.5"
												: "flex flex-wrap gap-x-1 items-center"
										}
									>
										{conditioningExercises.map((ce, i) => (
											<span
												key={i}
												className={`text-[10px] text-muted-foreground ${
													exercise.conditioning_type === "amrap" ||
													exercise.conditioning_type === "for_time" ||
													exercise.conditioning_type === "time_or_reps" ||
													exercise.conditioning_type === "hiit"
														? "block"
														: ""
												}`}
											>
												{exercise.conditioning_type !== "amrap" &&
													exercise.conditioning_type !== "for_time" &&
													exercise.conditioning_type !== "time_or_reps" &&
													exercise.conditioning_type !== "hiit" &&
													i > 0 &&
													" · "}
												{ce.reps && (
													<span className="font-medium">{ce.reps} </span>
												)}
												{ce.exercise_name || ce.name}
											</span>
										))}
									</div>
								)
							)}
							{isCustomConditioning && collapsedCustomConditioningPreview && (
								<div className="text-[10px] text-muted-foreground whitespace-pre-wrap break-words">
									{collapsedCustomConditioningPreview}
								</div>
							)}
						</div>
					)}
					{!isConditioning && !(isComplex && isExpanded) && (
						<ExerciseSummary
							exercise={exercise}
							loadSuggestions={loadSuggestions}
						/>
					)}
				</div>

				{/* Right side */}
				<div className="flex items-center gap-1.5 shrink-0">
					{/* Affordance: contenitore circolare bordato (speculare all'icona
					    di stato a sinistra) così il chevron si legge come pulsante.
					    Il solo chevron grigio su nero passava inosservato. Il chevron
					    ruota invece di cambiare icona: il movimento lega il tocco
					    all'apertura (motion di disclosure standard). */}
					<div
						className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${
							isExpanded
								? "border-volt/40 bg-volt/10 text-volt"
								: "border-border bg-white/[0.03] text-foreground/70"
						}`}
					>
						<ChevronDown
							className={cn(
								"w-4 h-4 transition-transform duration-200 motion-reduce:transition-none",
								isExpanded && "rotate-180",
							)}
						/>
					</div>
				</div>
			</button>

			{/* Expanded section */}
			{isExpanded && (
				<div
					id={expandedContentId}
					ref={expandedContentRef}
					className="px-4 pb-4 border-t border-border/30"
				>
					{sharedLocalNoteText && (
						<div className="mt-3 rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-2">
							<div className="inline-flex max-w-full items-start gap-1.5 text-[10px] text-amber-200/95">
								<FileText className="mt-0.5 h-3 w-3 flex-shrink-0" />
								<span className="uppercase tracking-wider">
									{language === "it" ? "Nota programma" : "Program note"}
								</span>
							</div>
							<p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap break-words">
								<LinkifiedText text={sharedLocalNoteText} />
							</p>
						</div>
					)}

					{/* Library description */}
					{lib?.description && (
						<p className="text-xs text-muted-foreground mt-2 italic">
							{lib.description}
						</p>
					)}

					{/* Full prescription detail */}
					<ExerciseDetail
						exercise={exercise}
						exerciseLibrary={exerciseLibrary}
						onOpenMedia={setMediaExercise}
						loadSuggestions={loadSuggestions}
					/>

					{/* Coach notes */}
					{exercise.notes && (
						<div className="mt-3 bg-volt/10 border-l-2 border-volt border-y border-r border-volt/20 rounded-sm px-3 py-2">
							<p className="text-[10px] uppercase tracking-wider text-volt mb-0.5 font-medium">
								{t("coachNotes")}
							</p>
							<p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
								<LinkifiedText text={exercise.notes} />
							</p>
						</div>
					)}

					{/* Saved result with edit button */}
					{isCompleted && editingKey !== resultStorageKey && (
						<SavedResult
							result={savedResult}
							exercise={exercise}
							exerciseType={exercise.exercise_type}
							conditioningType={
								String(
									exercise.conditioning_type ||
										exercise.conditioning_config?.conditioning_type ||
										"",
								)
									.trim()
									.toLowerCase() === "for_time" &&
								exercise.score_type !== "for_time"
									? "time_or_reps"
									: String(
											exercise.conditioning_type ||
												exercise.conditioning_config?.conditioning_type ||
												"",
										)
											.trim()
											.toLowerCase()
							}
							onEdit={
								canLogResult
									? () =>
											startEdit(
												resultStorageKey,
												savedResult,
												exercise,
												programId,
												dayId,
											)
									: undefined
							}
						/>
					)}

					{/* Compact action bar (mobile-first): result / support / leaderboard */}
					{actionsCount > 0 && (
						<div className={`mt-3 grid ${actionGridClass} gap-2`}>
							{showInsertResultAction && (
								<button
									onClick={() =>
										openCreateResultDialog(exKey, exercise, programId, dayId)
									}
									className="h-11 rounded-sm border border-volt/30 bg-volt/10 text-volt inline-flex flex-col items-center justify-center gap-0.5 px-1.5 text-[11px] font-heading hover:bg-volt/15 active:bg-volt/20 transition-colors"
									title={t("insertResultLabel")}
									aria-label={t("insertResultLabel")}
								>
									<Send className="w-3.5 h-3.5" />
									<span className="text-[10px] leading-none whitespace-nowrap">
										{t("actionResultShort")}
									</span>
								</button>
							)}

							{showSupportAction && (
								<button
									onClick={handleSupportAction}
									className={`h-11 rounded-sm border inline-flex flex-col items-center justify-center gap-0.5 px-1.5 text-[11px] font-heading transition-colors ${
										supportThreadOpened
											? "border-volt/40 text-volt hover:bg-volt/5"
											: "border-border text-muted-foreground hover:text-foreground"
									}`}
									title={supportActionLabel}
									aria-label={supportActionLabel}
								>
									<Mail className="w-3.5 h-3.5" />
									<span className="text-[10px] leading-none whitespace-nowrap">
										{compactSupportLabel}
									</span>
								</button>
							)}

							{showLeaderboardAction && (
								<button
									onClick={() => {
										setLeaderboardGenderFilter("all");
										setLeaderboardSchemaFieldId(null);
										setLeaderboardOpen(true);
									}}
									className="h-11 rounded-sm border border-dashed border-volt/40 inline-flex flex-col items-center justify-center gap-0.5 px-1.5 text-[11px] font-heading text-volt hover:bg-volt/5 active:bg-volt/10 transition-colors"
									title={t("viewLeaderboardLabel")}
									aria-label={t("viewLeaderboardLabel")}
								>
									<Trophy className="w-3.5 h-3.5" />
									<span className="text-[10px] leading-none whitespace-nowrap">
										{t("actionLeaderboardShort")}
									</span>
								</button>
							)}

							{showHistoryAction && (
								<button
									onClick={handleOpenHistory}
									className="h-11 rounded-sm border border-dashed border-volt/40 inline-flex flex-col items-center justify-center gap-0.5 px-1.5 text-[11px] font-heading text-volt hover:bg-volt/5 active:bg-volt/10 transition-colors"
									title={t("exerciseHistoryLabel") || "Storico Esercizio"}
									aria-label={t("exerciseHistoryLabel") || "Storico Esercizio"}
								>
									<History className="w-3.5 h-3.5" />
									<span className="text-[10px] leading-none whitespace-nowrap">
										{t("actionHistoryShort") || "Storico"}
									</span>
								</button>
							)}
						</div>
					)}

					{canViewLeaderboard && (
						<>
							<LeaderboardDialog
								open={leaderboardOpen}
								onOpenChange={(open) => {
									setLeaderboardOpen(open);
									if (!open) {
										setLeaderboardData(null);
										setLeaderboardError("");
										setLeaderboardGenderFilter("all");
										setLeaderboardSchemaFieldId(null);
									}
								}}
								title={t("leaderboardTitle")}
								description={formatConditioningDisplayNameForAthlete(
									exercise,
									language,
								)}
								loading={leaderboardLoading}
								error={leaderboardError}
								data={leaderboardData}
								showScalingBadges={isConditioning}
								genderFilter={leaderboardGenderFilter}
								onGenderFilterChange={setLeaderboardGenderFilter}
								schemaFields={leaderboardData?.result_schema || []}
								activeSchemaFieldId={
									leaderboardData?.active_schema_field_id ||
									leaderboardSchemaFieldId
								}
								onActiveSchemaFieldChange={setLeaderboardSchemaFieldId}
								formatMetric={(entry) =>
									formatLeaderboardMetric(
										entry,
										t,
										leaderboardData,
										language,
										exercise?.conditioning_exercises ||
											exercise?.conditioning_config?.conditioning_exercises ||
											[],
										String(
											exercise?.conditioning_type ||
												exercise?.conditioning_config?.conditioning_type ||
												"",
										)
											.trim()
											.toLowerCase(),
									)
								}
								getEntryMetaLabel={() => ""}
								t={t}
								emptyDescription={t("leaderboardEmptyLabel")}
							/>
						</>
					)}

					{/* History Dialog */}
					<ExerciseHistoryDialog
						open={historyOpen}
						onOpenChange={(open) => {
							setHistoryOpen(open);
							if (!open) {
								setHistoryExercise(null);
								setHistoryResults([]);
								setHistoryError("");
							}
						}}
						exercise={historyExercise}
						loading={historyLoading}
						error={historyError}
						results={historyResults}
						athleteId={sandbox?.athleteId || null}
						onHistoryChanged={async () => {
							await handleOpenHistory();
							// Una correzione nello storico tocca la stessa riga mostrata nel
							// giorno: senza questo il coach vede ancora il vecchio dato.
							await sandbox?.onResultsChanged?.();
						}}
						t={t}
					/>
				</div>
			)}
		</div>
	);
}

// Riusato dal Diario atleta per coerenza di formattazione esercizi tra
// Dashboard e Diario. Vedi frontend/src/pages/workouts/components/ExerciseDetailAccordion.js
export { ExerciseDetail };
